import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../lib/AuthContext';
import './Dashboard.css';
import PurchaseGraph from '../components/PurchaseGraph';
import AdminAnalytics from '../components/AdminAnalytics';
import orangeLogo from '../assets/orangelog.png';

const fmt = (n) => `$${Number(n).toFixed(2)}`;
const round2 = (n) => Math.round(n * 100) / 100;

function StatCard({ label, value, sub }) {
  return (
    <div style={{
      backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px',
      padding: '1.25rem 1.5rem', flex: '1 1 180px',
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    }}>
      <div style={{ fontSize: '0.8rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.4rem' }}>{label}</div>
      <div style={{ fontSize: '1.75rem', fontWeight: '800', color: '#1a1a1a', lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: '0.78rem', color: '#aaa', marginTop: '0.25rem' }}>{sub}</div>}
    </div>
  );
}

// ── Promo Codes Section ───────────────────────────────────────────────────────
function PromoCodesSection({ token, promoStats }) {
  const [promoTab, setPromoTab]       = React.useState('list');
  const [codes, setCodes]             = React.useState([]);
  const [loadingCodes, setLoadingCodes] = React.useState(false);
  const [codesError, setCodesError]   = React.useState('');

  // Generate individual batch
  const [genCount, setGenCount]       = React.useState(50);
  const [genTokens, setGenTokens]     = React.useState(25);
  const [genExpiry, setGenExpiry]     = React.useState('');
  const [genLabel, setGenLabel]       = React.useState('');
  const [genPassword, setGenPassword] = React.useState('');
  const [genLoading, setGenLoading]   = React.useState(false);
  const [genResult, setGenResult]     = React.useState(null);
  const [genError, setGenError]       = React.useState('');

  // Create bulk code
  const [bulkCode, setBulkCode]       = React.useState('');
  const [bulkTokens, setBulkTokens]   = React.useState(50);
  const [bulkMaxUses, setBulkMaxUses] = React.useState(500);
  const [bulkUnlimited, setBulkUnlimited] = React.useState(false);
  const [bulkExpiry, setBulkExpiry]   = React.useState('');
  const [bulkDesc, setBulkDesc]       = React.useState('');
  const [bulkPassword, setBulkPassword] = React.useState('');
  const [bulkLoading, setBulkLoading] = React.useState(false);
  const [bulkResult, setBulkResult]   = React.useState(null);
  const [bulkError, setBulkError]     = React.useState('');

  const authHeader = { Authorization: `Bearer ${token}` };
  const sectionStyle = { maxWidth: 1100, margin: '0 auto', padding: '0 2.5rem 2rem' };
  const h5Style = { color: '#555', marginBottom: '0.75rem', textTransform: 'uppercase', fontSize: '0.78rem', letterSpacing: '0.07em', scrollMarginTop: '160px' };
  const inputStyle = { padding: '0.5rem 0.75rem', border: '1px solid #ccc', borderRadius: '6px', fontSize: '0.9rem', width: '100%' };
  const btnPrimary = { padding: '0.55rem 1.25rem', background: '#0d3165', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: '700', fontSize: '0.88rem', cursor: 'pointer' };
  const btnSm = { padding: '0.25rem 0.6rem', fontSize: '0.78rem', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600' };
  const td = { padding: '0.6rem 0.75rem', borderBottom: '1px solid #f0f0f0', fontSize: '0.85rem' };

  const fetchCodes = React.useCallback(async () => {
    setLoadingCodes(true); setCodesError('');
    try {
      const res = await axios.get('/api/admin/promo-codes', { headers: authHeader });
      setCodes(res.data);
    } catch (err) {
      setCodesError(err.response?.data?.error || 'Failed to load promo codes.');
    } finally { setLoadingCodes(false); }
  }, [token]);

  React.useEffect(() => { if (promoTab === 'list') fetchCodes(); }, [promoTab, fetchCodes]);

  const handleToggle = async (id, current) => {
    try {
      const res = await axios.patch(`/api/admin/promo-codes/${id}/toggle`, {}, { headers: authHeader });
      setCodes(prev => prev.map(c => c._id === id ? { ...c, isActive: res.data.isActive } : c));
    } catch (err) { alert(err.response?.data?.error || 'Toggle failed.'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this unused promo code?')) return;
    try {
      await axios.delete(`/api/admin/promo-codes/${id}`, { headers: authHeader });
      setCodes(prev => prev.filter(c => c._id !== id));
    } catch (err) { alert(err.response?.data?.error || 'Delete failed.'); }
  };

  const handleGenerate = async (e) => {
    e.preventDefault(); setGenError(''); setGenResult(null); setGenLoading(true);
    try {
      const res = await axios.post('/api/admin/promo-codes/generate', {
        codeType: 'individual', count: genCount, tokensAwarded: genTokens,
        expiresAt: genExpiry || null, batchLabel: genLabel, password: genPassword,
      }, { headers: authHeader });
      setGenResult(res.data);
      setGenPassword(''); setGenLabel(''); setGenExpiry('');
      if (promoTab === 'list') fetchCodes();
    } catch (err) { setGenError(err.response?.data?.error || 'Generation failed.'); }
    finally { setGenLoading(false); }
  };

  const handleBulk = async (e) => {
    e.preventDefault(); setBulkError(''); setBulkResult(null); setBulkLoading(true);
    try {
      const res = await axios.post('/api/admin/promo-codes/generate', {
        codeType: 'bulk', code: bulkCode, tokensAwarded: bulkTokens,
        maxUses: bulkUnlimited ? null : bulkMaxUses,
        expiresAt: bulkExpiry || null, description: bulkDesc, password: bulkPassword,
      }, { headers: authHeader });
      setBulkResult(res.data);
      setBulkCode(''); setBulkDesc(''); setBulkExpiry(''); setBulkPassword('');
      if (promoTab === 'list') fetchCodes();
    } catch (err) { setBulkError(err.response?.data?.error || 'Creation failed.'); }
    finally { setBulkLoading(false); }
  };

  const downloadCSV = (codes, tokens, expiry, label) => {
    const rows = ['Code,Tokens,Expiry'].concat(
      codes.map(c => `${c},${tokens},${expiry || 'No expiry'}`)
    ).join('\n');
    const url = URL.createObjectURL(new Blob([rows], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = url;
    a.download = `promo-${(label || 'batch').replace(/\s+/g, '-').toLowerCase()}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  // Group individual codes by batchId for display
  const bulkCodes = codes.filter(c => c.codeType === 'bulk');
  const batches = {};
  codes.filter(c => c.codeType === 'individual').forEach(c => {
    const key = c.batchId || 'ungrouped';
    if (!batches[key]) batches[key] = { label: c.batchLabel || key, tokens: c.tokensAwarded, expiry: c.expiresAt, items: [] };
    batches[key].items.push(c);
  });

  const tabBtn = (id, label) => (
    <button key={id} onClick={() => setPromoTab(id)} style={{
      padding: '0.4rem 1rem', fontWeight: '700', fontSize: '0.82rem', cursor: 'pointer',
      background: promoTab === id ? '#0d3165' : '#f0f0f0',
      color: promoTab === id ? '#fff' : '#555',
      border: 'none', borderRadius: '5px',
    }}>{label}</button>
  );

  return (
    <div id="section-promo" style={{ ...sectionStyle, scrollMarginTop: '160px' }}>

      {/* ── Promotional Tokens header ── */}
      <hr style={{ border: 'none', borderTop: '2px solid #e0d0ff', marginBottom: '1.25rem' }} />
      <h5 style={{ ...h5Style, marginBottom: '1rem' }}>🪙 Promotional Tokens</h5>
      <div style={{
        backgroundColor: '#f5f0ff', border: '2px solid #c4a0f8', borderRadius: '10px',
        padding: '1.25rem 1.75rem', marginBottom: '2rem',
      }}>
        <div style={{ display: 'flex', gap: '2.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '0.2rem' }}>Total Promotional Credits in circulation</div>
            <div style={{ fontSize: '2.25rem', fontWeight: '900', color: '#6a00cc', lineHeight: 1 }}>
              {(promoStats?.totalPromoTokens ?? 0).toLocaleString()}
            </div>
          </div>
          <div style={{ borderLeft: '1px solid #d8c0f8', paddingLeft: '2.5rem' }}>
            <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '0.2rem' }}>Avg per user with promo balance</div>
            <div style={{ fontSize: '2.25rem', fontWeight: '900', color: '#8e44ad', lineHeight: 1 }}>
              {(promoStats?.avgPromoTokens ?? 0).toLocaleString()}
              <span style={{ fontSize: '1rem', fontWeight: 600, color: '#aaa', marginLeft: '0.4rem' }}>
                avg ({(promoStats?.promoUserCount ?? 0).toLocaleString()})
              </span>
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <h5 style={{ ...h5Style, margin: 0 }}>🎟️ Promo Codes</h5>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {tabBtn('list', 'All Codes')}
          {tabBtn('generate', 'Generate Individual')}
          {tabBtn('bulk', 'Create Bulk')}
        </div>
      </div>

      {/* ── All Codes tab ── */}
      {promoTab === 'list' && (
        <>
          {loadingCodes && <p style={{ color: '#888', fontSize: '0.88rem' }}>Loading...</p>}
          {codesError  && <p style={{ color: '#dc3545', fontSize: '0.88rem' }}>{codesError}</p>}

          {/* Bulk codes */}
          {bulkCodes.length > 0 && (
            <>
              <p style={{ fontWeight: '700', fontSize: '0.8rem', color: '#555', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.4rem' }}>Bulk / Vanity Codes</p>
              <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden', marginBottom: '1.5rem' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead><tr style={{ background: '#f8f8f8' }}>
                    {['Code', 'Tokens', 'Used / Max', 'Expires', 'Status', 'Actions'].map(h => (
                      <th key={h} style={{ ...td, fontWeight: '700', color: '#444', textAlign: 'left' }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {bulkCodes.map(c => (
                      <tr key={c._id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={td}><strong style={{ letterSpacing: '0.05em' }}>{c.code}</strong>{c.description && <span style={{ color: '#888', fontSize: '0.78rem', marginLeft: '0.5rem' }}>— {c.description}</span>}</td>
                        <td style={td}>{c.tokensAwarded}</td>
                        <td style={td}>{c.usedCount} / {c.maxUses ?? '∞'}</td>
                        <td style={td}>{c.expiresAt ? new Date(c.expiresAt).toLocaleDateString() : '—'}</td>
                        <td style={td}><span style={{ fontWeight: '700', color: c.isActive && (!c.maxUses || c.usedCount < c.maxUses) ? '#28a745' : '#dc3545' }}>{c.isActive ? (c.maxUses && c.usedCount >= c.maxUses ? 'Exhausted' : 'Active') : 'Inactive'}</span></td>
                        <td style={{ ...td, display: 'flex', gap: '0.4rem' }}>
                          <button onClick={() => handleToggle(c._id, c.isActive)} style={{ ...btnSm, background: c.isActive ? '#ffc107' : '#28a745', color: '#fff' }}>{c.isActive ? 'Deactivate' : 'Activate'}</button>
                          {c.usedCount === 0 && <button onClick={() => handleDelete(c._id)} style={{ ...btnSm, background: '#dc3545', color: '#fff' }}>Delete</button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Individual batches */}
          {Object.entries(batches).map(([batchId, batch]) => {
            const used = batch.items.filter(c => c.usedCount > 0).length;
            const remaining = batch.items.length - used;
            return (
              <div key={batchId} style={{ marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                  <p style={{ fontWeight: '700', fontSize: '0.8rem', color: '#555', textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0 }}>
                    {batch.label} — {batch.tokens} credits · {used} used / {remaining} remaining{batch.expiry ? ` · expires ${new Date(batch.expiry).toLocaleDateString()}` : ''}
                  </p>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button onClick={() => {
                      const unused = batch.items.filter(c => c.usedCount === 0).map(c => c.code);
                      downloadCSV(unused, batch.tokens, batch.expiry ? new Date(batch.expiry).toLocaleDateString() : '', batch.label);
                    }} style={{ ...btnSm, background: '#17a2b8', color: '#fff' }}>⬇ Download Unused CSV</button>
                    <button onClick={async () => {
                      for (const c of batch.items.filter(i => i.isActive)) {
                        try { await axios.patch(`/api/admin/promo-codes/${c._id}/toggle`, {}, { headers: authHeader }); } catch {}
                      }
                      fetchCodes();
                    }} style={{ ...btnSm, background: '#ffc107', color: '#333' }}>Deactivate Batch</button>
                  </div>
                </div>
                <div style={{ background: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: '6px', padding: '0.5rem 0.75rem', fontSize: '0.8rem', color: '#555', display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                  {batch.items.slice(0, 20).map(c => (
                    <span key={c._id} style={{ background: c.usedCount > 0 ? '#f5c6cb' : c.isActive ? '#d4edda' : '#e2e3e5', borderRadius: '3px', padding: '0.15rem 0.4rem', fontFamily: 'monospace', fontSize: '0.78rem' }}>{c.code}</span>
                  ))}
                  {batch.items.length > 20 && <span style={{ color: '#888', fontStyle: 'italic' }}>+{batch.items.length - 20} more</span>}
                </div>
              </div>
            );
          })}

          {!loadingCodes && codes.length === 0 && (
            <p style={{ color: '#aaa', fontStyle: 'italic', fontSize: '0.88rem' }}>No promo codes yet. Use the tabs above to create some.</p>
          )}
        </>
      )}

      {/* ── Generate Individual tab ── */}
      {promoTab === 'generate' && (
        <div style={{ maxWidth: 520 }}>
          <p style={{ fontSize: '0.88rem', color: '#555', marginBottom: '1.25rem' }}>
            Generates a batch of unique one-time-use codes (format: <code>XXXX-XXXX</code>). Each code can only be redeemed once by any single user.
          </p>
          {genResult ? (
            <div style={{ background: '#d4edda', border: '1px solid #a5d6a7', borderRadius: '8px', padding: '1.25rem', marginBottom: '1rem' }}>
              <p style={{ fontWeight: '700', color: '#1b5e20', marginBottom: '0.5rem' }}>✓ {genResult.count} codes generated — "{genResult.batchLabel || 'Batch'}" · {genResult.tokensAwarded} credits each</p>
              <button onClick={() => downloadCSV(genResult.codes, genResult.tokensAwarded, '', genResult.batchLabel)} style={{ ...btnPrimary, background: '#17a2b8' }}>⬇ Download CSV</button>
              <button onClick={() => { setGenResult(null); fetchCodes(); setPromoTab('list'); }} style={{ ...btnPrimary, background: '#6c757d', marginLeft: '0.5rem' }}>View All Codes</button>
            </div>
          ) : (
            <form onSubmit={handleGenerate} style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
              <div>
                <label style={{ fontSize: '0.82rem', fontWeight: '700', color: '#444', display: 'block', marginBottom: '0.3rem' }}>How many codes</label>
                <input type="number" min="1" max="1000" value={genCount} onChange={e => setGenCount(e.target.value)} style={inputStyle} required />
              </div>
              <div>
                <label style={{ fontSize: '0.82rem', fontWeight: '700', color: '#444', display: 'block', marginBottom: '0.3rem' }}>Tokens per code</label>
                <input type="number" min="1" value={genTokens} onChange={e => setGenTokens(e.target.value)} style={inputStyle} required />
              </div>
              <div>
                <label style={{ fontSize: '0.82rem', fontWeight: '700', color: '#444', display: 'block', marginBottom: '0.3rem' }}>Batch label (your notes)</label>
                <input type="text" placeholder="e.g. Winter 2026 Promo" value={genLabel} onChange={e => setGenLabel(e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: '0.82rem', fontWeight: '700', color: '#444', display: 'block', marginBottom: '0.3rem' }}>Expiration date (optional)</label>
                <input type="date" value={genExpiry} onChange={e => setGenExpiry(e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: '0.82rem', fontWeight: '700', color: '#444', display: 'block', marginBottom: '0.3rem' }}>Admin password</label>
                <input type="password" value={genPassword} onChange={e => setGenPassword(e.target.value)} style={inputStyle} required />
              </div>
              {genError && <p style={{ color: '#dc3545', fontSize: '0.85rem', margin: 0 }}>{genError}</p>}
              <button type="submit" disabled={genLoading} style={{ ...btnPrimary, opacity: genLoading ? 0.6 : 1 }}>
                {genLoading ? 'Generating…' : `Generate ${genCount} Codes`}
              </button>
            </form>
          )}
        </div>
      )}

      {/* ── Create Bulk tab ── */}
      {promoTab === 'bulk' && (
        <div style={{ maxWidth: 520 }}>
          <p style={{ fontSize: '0.88rem', color: '#555', marginBottom: '1.25rem' }}>
            A single shared code (e.g. <code>LAUNCH50</code>) with a set usage limit. Each user can still only redeem it once.
          </p>
          {bulkResult ? (
            <div style={{ background: '#d4edda', border: '1px solid #a5d6a7', borderRadius: '8px', padding: '1.25rem', marginBottom: '1rem' }}>
              <p style={{ fontWeight: '700', color: '#1b5e20', marginBottom: '0.5rem' }}>✓ Bulk code <strong>{bulkResult.code}</strong> created — {bulkResult.tokensAwarded} credits · {bulkResult.maxUses ?? 'unlimited'} max uses</p>
              <button onClick={() => { setBulkResult(null); fetchCodes(); setPromoTab('list'); }} style={{ ...btnPrimary, background: '#6c757d' }}>View All Codes</button>
            </div>
          ) : (
            <form onSubmit={handleBulk} style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
              <div>
                <label style={{ fontSize: '0.82rem', fontWeight: '700', color: '#444', display: 'block', marginBottom: '0.3rem' }}>Code (alphanumeric, 3–20 chars)</label>
                <input type="text" placeholder="e.g. LAUNCH50" value={bulkCode} onChange={e => setBulkCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))} style={{ ...inputStyle, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '700' }} required />
              </div>
              <div>
                <label style={{ fontSize: '0.82rem', fontWeight: '700', color: '#444', display: 'block', marginBottom: '0.3rem' }}>Tokens awarded</label>
                <input type="number" min="1" value={bulkTokens} onChange={e => setBulkTokens(e.target.value)} style={inputStyle} required />
              </div>
              <div>
                <label style={{ fontSize: '0.82rem', fontWeight: '700', color: '#444', display: 'block', marginBottom: '0.3rem' }}>Max uses</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <input type="number" min="1" value={bulkMaxUses} onChange={e => setBulkMaxUses(e.target.value)} disabled={bulkUnlimited} style={{ ...inputStyle, flex: 1, opacity: bulkUnlimited ? 0.4 : 1 }} />
                  <label style={{ fontSize: '0.82rem', color: '#555', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    <input type="checkbox" checked={bulkUnlimited} onChange={e => setBulkUnlimited(e.target.checked)} /> Unlimited
                  </label>
                </div>
              </div>
              <div>
                <label style={{ fontSize: '0.82rem', fontWeight: '700', color: '#444', display: 'block', marginBottom: '0.3rem' }}>Description (optional)</label>
                <input type="text" placeholder="e.g. Launch promo for newsletter" value={bulkDesc} onChange={e => setBulkDesc(e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: '0.82rem', fontWeight: '700', color: '#444', display: 'block', marginBottom: '0.3rem' }}>Expiration date (optional)</label>
                <input type="date" value={bulkExpiry} onChange={e => setBulkExpiry(e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: '0.82rem', fontWeight: '700', color: '#444', display: 'block', marginBottom: '0.3rem' }}>Admin password</label>
                <input type="password" value={bulkPassword} onChange={e => setBulkPassword(e.target.value)} style={inputStyle} required />
              </div>
              {bulkError && <p style={{ color: '#dc3545', fontSize: '0.85rem', margin: 0 }}>{bulkError}</p>}
              <button type="submit" disabled={bulkLoading} style={{ ...btnPrimary, opacity: bulkLoading ? 0.6 : 1 }}>
                {bulkLoading ? 'Creating…' : 'Create Bulk Code'}
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminPage() {
  const navigate = useNavigate();
  const { accessToken: token, clearSession } = useAuth();
  const isAdmin = localStorage.getItem('isAdmin') === 'true';

  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [expandedCompany, setExpandedCompany] = useState(null);
  const [billingEmailEdits, setBillingEmailEdits] = useState({}); // { [companyId]: { val, msg } }
  const setBEField = (id, field, value) => setBillingEmailEdits(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [liveStats, setLiveStats] = useState(null); // { totalVisitors, totalReaders, byCompany, byArticle }
  const [graphArticle, setGraphArticle] = useState(null); // { _id, title }
  const [locEditing, setLocEditing] = useState({}); // { [companyId]: { zipcode, city, state, isLocalOnly, serviceRadius } }
  const [locMsg, setLocMsg] = useState({}); // { [companyId]: message }
  const [mediaEditing, setMediaEditing] = useState({}); // { [companyId]: 'news'|'magazine'|'blog'|'podcast' }
  const [mediaMsg, setMediaMsg] = useState({}); // { [companyId]: message }
  const [brandingColor, setBrandingColor] = useState({}); // { [companyId]: '#rrggbb' }
  const [brandingMsg, setBrandingMsg] = useState({}); // { [companyId]: message }
  const [brandingLoading, setBrandingLoading] = useState({}); // { [companyId]: bool }
  const [feeRateEditing, setFeeRateEditing] = useState({}); // { [companyId]: { baseTier, threshold, elevatedTier } }
  const [feeRatePassword, setFeeRatePassword] = useState({}); // { [companyId]: string }
  const [feeRateMsg, setFeeRateMsg] = useState({}); // { [companyId]: { text, type } }
  const liveIntervalRef = useRef(null);

  // Invoice modal state
  const [invoiceTarget, setInvoiceTarget] = useState(null);
  const [invoiceMonth, setInvoiceMonth] = useState('');
  const [invoiceData, setInvoiceData] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceError, setInvoiceError] = useState('');

  // Payout ledger state
  const [payoutRecords, setPayoutRecords]     = useState({});   // keyed by `${companyId}-${month}`
  const [payoutForm, setPayoutForm]           = useState({ method: 'ach', notes: '' });
  const [markingPaid, setMarkingPaid]         = useState(false);
  const [invoiceSending, setInvoiceSending]   = useState(false);
  const [invoiceSentMsg, setInvoiceSentMsg]   = useState('');
  const [masterPayoutMap, setMasterPayoutMap] = useState({});   // keyed by companyId, for master invoice

  // Master invoice state
  const [masterInvOpen, setMasterInvOpen]       = useState(false);
  const [masterInvMonth, setMasterInvMonth]     = useState('');
  const [masterInvData, setMasterInvData]       = useState(null);
  const [masterInvLoading, setMasterInvLoading] = useState(false);
  const [masterInvError, setMasterInvError]     = useState('');

  // Purchase audit log modal state (per-article)
  const [purchaseLog, setPurchaseLog] = useState(null); // { articleId, title, purchases: [], aggregate: {} }
  const [purchaseLogLoading, setPurchaseLogLoading] = useState(false);
  const [purchaseLogError, setPurchaseLogError] = useState('');
  const [purchaseLogSort, setPurchaseLogSort] = useState({ col: 'purchasedAt', dir: 'desc' });

  // Token package purchase log modal state (site-wide)
  const [tokenPkgLog, setTokenPkgLog] = useState(null); // null | array of rows
  const [tokenPkgLoading, setTokenPkgLoading] = useState(false);
  const [tokenPkgError, setTokenPkgError] = useState('');
  const [tokenPkgSort, setTokenPkgSort] = useState({ col: 'purchasedAt', dir: 'desc' });

  // User history lookup state
  const [userSearch, setUserSearch]           = useState('');
  const [userHistory, setUserHistory]         = useState(null);
  const [userHistoryLoading, setUserHistoryLoading] = useState(false);
  const [userHistoryError, setUserHistoryError]     = useState('');
  const [receiptEmailModal, setReceiptEmailModal]   = useState(null);
  const [receiptEmailLoading, setReceiptEmailLoading] = useState(false);

  // All-users table state
  const [allUsers, setAllUsers]           = useState([]);
  const [allUsersLoading, setAllUsersLoading] = useState(false);
  const [allUsersLoaded, setAllUsersLoaded]   = useState(false);
  const [userSort, setUserSort]           = useState('date_created');
  const [userSortDir, setUserSortDir]     = useState('desc');
  const [userFilter, setUserFilter]       = useState('');
  const [userStatusFilter, setUserStatusFilter] = useState('all'); // 'all' | 'active' | 'suspended'
  const [chargebackModal, setChargebackModal] = useState(null); // user obj to suspend
  const [chargebackForm, setChargebackForm]   = useState({ amount: '', reason: '', stripeChargeId: '' });
  const [unsuspendConfirm, setUnsuspendConfirm] = useState(null); // user obj to unsuspend

  // Bulk email state
  const [selectedUserIds, setSelectedUserIds]     = useState(new Set());
  const [broadcastModal, setBroadcastModal]       = useState(false);
  const [broadcastForm, setBroadcastForm]         = useState({ subject: '', html: '' });
  const [broadcastPassword, setBroadcastPassword] = useState('');
  const [broadcastResult, setBroadcastResult]     = useState(null);
  const [broadcastLoading, setBroadcastLoading]   = useState(false);

  // Company disable/enable/message modal
  const [disableTarget, setDisableTarget] = useState(null); // company object
  const [disableMode, setDisableMode] = useState('disable'); // 'disable' | 'enable' | 'message'
  const [disableReason, setDisableReason] = useState('');
  const [disableMessage, setDisableMessage] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [disableLoading, setDisableLoading] = useState(false);
  const [disableError, setDisableError] = useState('');

  // Bulk-sunset all articles modal
  const [sunsetAllTarget, setSunsetAllTarget] = useState(null);
  const [sunsetAllPassword, setSunsetAllPassword] = useState('');
  const [sunsetAllLoading, setSunsetAllLoading] = useState(false);
  const [sunsetAllMsg, setSunsetAllMsg] = useState('');

  // Clear deletion debt modal
  const [clearDebtTarget, setClearDebtTarget] = useState(null); // company object
  const [clearDebtPassword, setClearDebtPassword] = useState('');
  const [clearDebtLoading, setClearDebtLoading] = useState(false);
  const [clearDebtError, setClearDebtError] = useState('');

  // Force-delete article modal
  const [forceDeleteTarget, setForceDeleteTarget] = useState(null); // { _id, title }
  const [forceDeletePassword, setForceDeletePassword] = useState('');
  const [forceDeleteLoading, setForceDeleteLoading] = useState(false);
  const [forceDeleteError, setForceDeleteError] = useState('');

  // Reset embed keys modal
  const [resetEmbedTarget, setResetEmbedTarget] = useState(null); // company object
  const [resetEmbedPassword, setResetEmbedPassword] = useState('');
  const [resetEmbedLoading, setResetEmbedLoading] = useState(false);
  const [resetEmbedError, setResetEmbedError] = useState('');
  const [resetEmbedResult, setResetEmbedResult] = useState(null); // { embedKey, embedSecret }

  // Plugin license key state
  const [licenseState, setLicenseState] = useState({}); // { [companyId]: { loading, msg } }

  // Archive company modal
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [archivePassword, setArchivePassword] = useState('');
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [archiveError, setArchiveError] = useState('');
  const [archivedCompanies, setArchivedCompanies] = useState([]);
  const [archivedOpen, setArchivedOpen] = useState(false);
  const [unarchiveTarget, setUnarchiveTarget] = useState(null);
  const [unarchivePassword, setUnarchivePassword] = useState('');
  const [unarchiveLoading, setUnarchiveLoading] = useState(false);
  const [unarchiveError, setUnarchiveError] = useState('');

  // Broken-link flagged articles
  const [needsReview, setNeedsReview] = useState([]);

  // Returns stats + kill-switch
  const [returnsStats, setReturnsStats]         = useState(null);
  const [returnsTogglePw, setReturnsTogglePw]   = useState('');
  const [returnsToggleMsg, setReturnsToggleMsg] = useState('');
  const [returnsToggling, setReturnsToggling]   = useState(false);

  // Ad management
  const [adsOpen,        setAdsOpen]        = useState(false);
  const [adsList,        setAdsList]        = useState([]);
  const [adsLoading,     setAdsLoading]     = useState(false);
  const [adForm,         setAdForm]         = useState(null);
  const [adSaving,       setAdSaving]       = useState(false);
  const [adError,        setAdError]        = useState('');
  const [adImgUploading, setAdImgUploading] = useState(false);

  useEffect(() => {
    if (!token || !isAdmin) { navigate('/login'); return; }
    fetchStats();
    fetchNeedsReview();

    const headers = { Authorization: `Bearer ${token}` };
    const pollLive = () => {
      axios.get('/api/analytics/live/admin', { headers })
        .then(res => setLiveStats(res.data))
        .catch(() => {});
    };
    pollLive();
    liveIntervalRef.current = setInterval(pollLive, 30000);
    return () => clearInterval(liveIntervalRef.current);
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, companiesRes, returnsRes, archivedRes] = await Promise.all([
        axios.get('/api/admin/stats',              { headers }),
        axios.get('/api/admin/companies',          { headers }),
        axios.get('/api/admin/returns-stats',      { headers }),
        axios.get('/api/admin/companies/archived', { headers }),
      ]);
      setStats(statsRes.data);
      setCompanies(companiesRes.data);
      setBillingEmailEdits(Object.fromEntries(companiesRes.data.map(c => [c._id, { val: c.billingEmail || '', msg: '' }])));
      setReturnsStats(returnsRes.data);
      setArchivedCompanies(archivedRes.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load admin stats.');
    } finally {
      setLoading(false);
    }
  };

  const fetchNeedsReview = async () => {
    try {
      const res = await axios.get('/api/admin/articles/needs-review', { headers: { Authorization: `Bearer ${token}` } });
      setNeedsReview(res.data.articles || []);
    } catch (_) {}
  };

  const loadAds = async () => {
    setAdsLoading(true);
    try {
      const r = await axios.get('/api/ads', { headers: { Authorization: `Bearer ${token}` } });
      setAdsList(r.data);
    } catch {}
    finally { setAdsLoading(false); }
  };

  const saveAd = async () => {
    if (!adForm?.advertiserName?.trim() || !adForm?.imageUrl?.trim() || !adForm?.clickUrl?.trim()) {
      setAdError('Advertiser name, image URL, and click URL are required.');
      return;
    }
    setAdSaving(true); setAdError('');
    try {
      const h = { headers: { Authorization: `Bearer ${token}` } };
      if (adForm._id) {
        await axios.patch(`/api/ads/${adForm._id}`, adForm, h);
      } else {
        await axios.post('/api/ads', adForm, h);
      }
      setAdForm(null);
      loadAds();
    } catch (e) {
      setAdError(e.response?.data?.error || 'Save failed.');
    } finally { setAdSaving(false); }
  };

  const toggleAdActive = async (ad) => {
    try {
      await axios.patch(`/api/ads/${ad._id}`, { active: !ad.active }, { headers: { Authorization: `Bearer ${token}` } });
      setAdsList(prev => prev.map(a => a._id === ad._id ? { ...a, active: !a.active } : a));
    } catch {}
  };

  const deleteAd = async (id) => {
    if (!window.confirm('Delete this ad permanently?')) return;
    try {
      await axios.delete(`/api/ads/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setAdsList(prev => prev.filter(a => a._id !== id));
    } catch {}
  };

  const uploadAdImage = async (file) => {
    if (!file) return;
    setAdImgUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const r = await fetch('/api/upload/image', { method: 'POST', body: fd });
      const data = await r.json();
      if (data.url) setAdForm(f => ({ ...f, imageUrl: data.url }));
    } catch {}
    finally { setAdImgUploading(false); }
  };

  const AD_BLANK = { advertiserName: '', advertiserEmail: '', imageUrl: '', clickUrl: '', altText: '', genres: [], audienceLevels: [], targetState: '', targetCity: '', targetZip: '', radiusMiles: 0, tier: 'standard', startDate: '', endDate: '', active: false };

  const handleSunsetAll = async () => {
    if (!sunsetAllPassword) return;
    setSunsetAllLoading(true);
    setSunsetAllMsg('');
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(`/api/admin/companies/${sunsetAllTarget._id}/sunset-all-articles`, { password: sunsetAllPassword }, { headers });
      setSunsetAllMsg(`✓ ${res.data.articlesUpdated} article${res.data.articlesUpdated !== 1 ? 's' : ''} sunset immediately.`);
      setSunsetAllPassword('');
    } catch (err) {
      setSunsetAllMsg(err.response?.data?.error || 'Error — check password and try again.');
    } finally {
      setSunsetAllLoading(false);
    }
  };

  const handleDisableSubmit = async () => {
    setDisableLoading(true);
    setDisableError('');
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (disableMode === 'disable') {
        await axios.post(`/api/admin/companies/${disableTarget._id}/disable`, { reason: disableReason, message: disableMessage, password: disablePassword }, { headers });
        setCompanies(prev => prev.map(c => c._id === disableTarget._id ? { ...c, disabled: true, disabledReason: disableReason, adminMessage: disableMessage } : c));
      } else if (disableMode === 'enable') {
        await axios.post(`/api/admin/companies/${disableTarget._id}/enable`, { password: disablePassword }, { headers });
        setCompanies(prev => prev.map(c => c._id === disableTarget._id ? { ...c, disabled: false, disabledReason: '', disabledBy: '' } : c));
      } else if (disableMode === 'message') {
        await axios.post(`/api/admin/companies/${disableTarget._id}/message`, { message: disableMessage }, { headers });
        setCompanies(prev => prev.map(c => c._id === disableTarget._id ? { ...c, adminMessage: disableMessage } : c));
      }
      setDisableTarget(null);
      setDisableReason('');
      setDisableMessage('');
      setDisablePassword('');
    } catch (err) {
      setDisableError(err.response?.data?.error || 'Action failed.');
    } finally {
      setDisableLoading(false);
    }
  };

  const handleReturnsToggle = async () => {
    if (!returnsTogglePw) return;
    setReturnsToggling(true);
    setReturnsToggleMsg('');
    try {
      const res = await axios.post('/api/admin/returns/toggle', { password: returnsTogglePw }, { headers: { Authorization: `Bearer ${token}` } });
      setReturnsStats(prev => ({ ...prev, returnsEnabled: res.data.returnsEnabled }));
      setReturnsToggleMsg(res.data.returnsEnabled ? 'Returns are now ENABLED.' : 'Returns are now DISABLED.');
      setReturnsTogglePw('');
    } catch (err) {
      setReturnsToggleMsg(err.response?.data?.error || 'Toggle failed.');
    } finally {
      setReturnsToggling(false);
    }
  };

  const handleClearDebt = async () => {
    setClearDebtLoading(true);
    setClearDebtError('');
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.patch(`/api/admin/companies/${clearDebtTarget._id}/clear-deletion-debt`, { password: clearDebtPassword }, { headers });
      setCompanies(prev => prev.map(c => c._id === clearDebtTarget._id ? { ...c, deletionFlagged: false, deletionDebt: 0 } : c));
      setClearDebtTarget(null);
      setClearDebtPassword('');
    } catch (err) {
      setClearDebtError(err.response?.data?.error || 'Failed to clear debt.');
    } finally {
      setClearDebtLoading(false);
    }
  };

  const handleForceDelete = async () => {
    setForceDeleteLoading(true);
    setForceDeleteError('');
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`/api/admin/articles/${forceDeleteTarget._id}/force-delete`, { password: forceDeletePassword }, { headers });
      setForceDeleteTarget(null);
      setForceDeletePassword('');
      fetchStats();
      fetchNeedsReview();
    } catch (err) {
      setForceDeleteError(err.response?.data?.error || 'Failed to force delete.');
    } finally {
      setForceDeleteLoading(false);
    }
  };

  const handleResetEmbedKeys = async () => {
    setResetEmbedLoading(true);
    setResetEmbedError('');
    try {
      const res = await axios.post(
        `/api/admin/companies/${resetEmbedTarget._id}/reset-embed-keys`,
        { password: resetEmbedPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResetEmbedResult(res.data);
      setResetEmbedPassword('');
    } catch (err) {
      setResetEmbedError(err.response?.data?.error || 'Failed to reset keys.');
    } finally {
      setResetEmbedLoading(false);
    }
  };

  const handleGenerateLicense = async (companyId) => {
    setLicenseState(prev => ({ ...prev, [companyId]: { loading: true, msg: '' } }));
    try {
      const res = await axios.post(
        `/api/admin/companies/${companyId}/plugin-license`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCompanies(prev => prev.map(c => c._id === companyId ? { ...c, pluginLicenseKey: res.data.licenseKey } : c));
      setLicenseState(prev => ({ ...prev, [companyId]: { loading: false, msg: 'Generated' } }));
      setTimeout(() => setLicenseState(prev => { const n = { ...prev }; delete n[companyId]; return n; }), 4000);
    } catch (err) {
      setLicenseState(prev => ({ ...prev, [companyId]: { loading: false, msg: err.response?.data?.error || 'Error' } }));
    }
  };

  const handleRevokeLicense = async (companyId) => {
    if (!window.confirm("Revoke this publisher's plugin license key? Their plugin will stop working immediately.")) return;
    setLicenseState(prev => ({ ...prev, [companyId]: { loading: true, msg: '' } }));
    try {
      await axios.delete(
        `/api/admin/companies/${companyId}/plugin-license`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCompanies(prev => prev.map(c => c._id === companyId ? { ...c, pluginLicenseKey: null } : c));
      setLicenseState(prev => ({ ...prev, [companyId]: { loading: false, msg: 'Revoked' } }));
      setTimeout(() => setLicenseState(prev => { const n = { ...prev }; delete n[companyId]; return n; }), 2500);
    } catch (err) {
      setLicenseState(prev => ({ ...prev, [companyId]: { loading: false, msg: err.response?.data?.error || 'Error' } }));
    }
  };

  const handleArchiveSubmit = async () => {
    setArchiveLoading(true);
    setArchiveError('');
    try {
      await axios.post(`/api/admin/companies/${archiveTarget._id}/archive`, { password: archivePassword }, { headers: { Authorization: `Bearer ${token}` } });
      setArchivedCompanies(prev => [...prev, { ...archiveTarget, archived: true, archivedAt: new Date().toISOString() }]);
      setCompanies(prev => prev.filter(c => c._id !== archiveTarget._id));
      setArchiveTarget(null);
      setArchivePassword('');
    } catch (err) {
      setArchiveError(err.response?.data?.error || 'Archive failed.');
    } finally {
      setArchiveLoading(false);
    }
  };

  const handleUnarchiveSubmit = async () => {
    setUnarchiveLoading(true);
    setUnarchiveError('');
    try {
      await axios.post(`/api/admin/companies/${unarchiveTarget._id}/unarchive`, { password: unarchivePassword }, { headers: { Authorization: `Bearer ${token}` } });
      setCompanies(prev => [...prev, { ...unarchiveTarget, archived: false, archivedAt: null }]);
      setArchivedCompanies(prev => prev.filter(c => c._id !== unarchiveTarget._id));
      setUnarchiveTarget(null);
      setUnarchivePassword('');
    } catch (err) {
      setUnarchiveError(err.response?.data?.error || 'Restore failed.');
    } finally {
      setUnarchiveLoading(false);
    }
  };

  const fetchUserHistory = async (e) => {
    e.preventDefault();
    if (!userSearch.trim()) return;
    setUserHistoryLoading(true);
    setUserHistoryError('');
    setUserHistory(null);
    try {
      const res = await axios.get(`/api/admin/users/history?q=${encodeURIComponent(userSearch.trim())}`, { headers: { Authorization: `Bearer ${token}` } });
      setUserHistory(res.data);
    } catch (err) {
      setUserHistoryError(err.response?.data?.error || 'User not found.');
    } finally {
      setUserHistoryLoading(false);
    }
  };

  const fetchReceiptEmail = async (receiptId, receiptNumber) => {
    setReceiptEmailLoading(true);
    try {
      const res = await axios.get(`/api/admin/receipts/${receiptId}/email`, { headers: { Authorization: `Bearer ${token}` } });
      setReceiptEmailModal({ receiptNumber: res.data.receiptNumber, emailHtml: res.data.emailHtml });
    } catch {
      alert('Could not load email for this receipt.');
    } finally {
      setReceiptEmailLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    setAllUsersLoading(true);
    try {
      const res = await axios.get('/api/admin/users', { headers: { Authorization: `Bearer ${token}` } });
      setAllUsers(res.data.users || []);
      setAllUsersLoaded(true);
    } catch {
      alert('Could not load users.');
    } finally {
      setAllUsersLoading(false);
    }
  };

  const handleChargebackSuspend = async () => {
    if (!chargebackModal) return;
    const { amount, reason, stripeChargeId } = chargebackForm;
    if (!amount || isNaN(Number(amount))) { alert('Enter a valid charge amount.'); return; }
    try {
      const res = await axios.patch(
        `/api/admin/users/${chargebackModal._id}/chargeback-suspend`,
        { chargebackAmount: Number(amount), chargebackReason: reason, chargebackStripeId: stripeChargeId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAllUsers(prev => prev.map(u => u._id === chargebackModal._id
        ? { ...u, chargebackSuspended: true, chargebackSuspendedAt: new Date().toISOString(), chargebackAmount: Number(amount), chargebackReason: reason }
        : u
      ));
      alert(`Suspended. ${res.data.creditsRemoved} credits removed. New balance: ${res.data.newBalance}.`);
      setChargebackModal(null);
      setChargebackForm({ amount: '', reason: '', stripeChargeId: '' });
    } catch (err) {
      alert(err.response?.data?.error || 'Suspension failed.');
    }
  };

  const handleChargebackUnsuspend = async () => {
    if (!unsuspendConfirm) return;
    try {
      await axios.patch(`/api/admin/users/${unsuspendConfirm._id}/chargeback-unsuspend`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setAllUsers(prev => prev.map(u => u._id === unsuspendConfirm._id ? { ...u, chargebackSuspended: false } : u));
      setUnsuspendConfirm(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Unsuspend failed.');
    }
  };

  const lookupUserFromTable = async (username) => {
    setUserSearch(username);
    setUserHistoryLoading(true);
    setUserHistoryError('');
    setUserHistory(null);
    try {
      const res = await axios.get(`/api/admin/users/history?q=${encodeURIComponent(username)}`, { headers: { Authorization: `Bearer ${token}` } });
      setUserHistory(res.data);
    } catch (err) {
      setUserHistoryError(err.response?.data?.error || 'User not found.');
    } finally {
      setUserHistoryLoading(false);
    }
  };

  const handleBroadcastSend = async () => {
    if (!broadcastForm.subject.trim() || !broadcastForm.html.trim() || !broadcastPassword) return;
    setBroadcastLoading(true);
    setBroadcastResult(null);
    try {
      const res = await axios.post('/api/admin/broadcast-email', {
        userIds:  Array.from(selectedUserIds),
        subject:  broadcastForm.subject.trim(),
        html:     broadcastForm.html.trim(),
        password: broadcastPassword,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setBroadcastResult(res.data);
      setBroadcastPassword('');
      if (res.data.failed === 0) {
        setSelectedUserIds(new Set());
      }
    } catch (err) {
      setBroadcastResult({ error: err.response?.data?.error || 'Send failed.' });
    } finally {
      setBroadcastLoading(false);
    }
  };

  const handleLogout = () => {
    if (token) axios.post('/api/auth/logout', {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    clearSession();
    navigate('/');
  };

  const handleMediaSave = async (companyId) => {
    const mediaType = mediaEditing[companyId];
    if (!mediaType) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.patch(`/api/admin/companies/${companyId}/media-type`, { mediaType }, { headers });
      setCompanies(prev => prev.map(c => c._id === companyId ? { ...c, mediaType } : c));
      setMediaMsg(prev => ({ ...prev, [companyId]: 'Saved' }));
      setTimeout(() => setMediaMsg(prev => { const n = { ...prev }; delete n[companyId]; return n; }), 2500);
    } catch (err) {
      setMediaMsg(prev => ({ ...prev, [companyId]: err.response?.data?.error || 'Error' }));
    }
  };

  const handleLocSave = async (companyId) => {
    const loc = locEditing[companyId];
    if (!loc) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.patch(`/api/admin/companies/${companyId}/location`, loc, { headers });
      setCompanies(prev => prev.map(c => c._id === companyId ? { ...c, location: res.data.location } : c));
      setLocMsg(prev => ({ ...prev, [companyId]: 'Saved' }));
      setTimeout(() => setLocMsg(prev => { const n = { ...prev }; delete n[companyId]; return n; }), 2500);
    } catch (err) {
      setLocMsg(prev => ({ ...prev, [companyId]: err.response?.data?.error || 'Error' }));
    }
  };

  const handleLogoUpload = (companyId, file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      setBrandingMsg(prev => ({ ...prev, [companyId]: 'Image must be under 3 MB.' }));
      return;
    }
    setBrandingLoading(prev => ({ ...prev, [companyId]: true }));
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.patch(`/api/admin/companies/${companyId}/branding`, { hero_image: reader.result }, { headers });
        setCompanies(prev => prev.map(c => c._id === companyId ? { ...c, hero_image: res.data.hero_image } : c));
        setBrandingMsg(prev => ({ ...prev, [companyId]: 'Logo saved.' }));
        setTimeout(() => setBrandingMsg(prev => { const n = { ...prev }; delete n[companyId]; return n; }), 3000);
      } catch (err) {
        setBrandingMsg(prev => ({ ...prev, [companyId]: err.response?.data?.error || 'Upload failed.' }));
      } finally {
        setBrandingLoading(prev => ({ ...prev, [companyId]: false }));
      }
    };
    reader.onerror = () => {
      setBrandingMsg(prev => ({ ...prev, [companyId]: 'Failed to read file.' }));
      setBrandingLoading(prev => ({ ...prev, [companyId]: false }));
    };
    reader.readAsDataURL(file);
  };

  const handleColorSave = async (companyId) => {
    const color = brandingColor[companyId] ?? '';
    if (color && !/^#[0-9A-Fa-f]{6}$/.test(color)) {
      setBrandingMsg(prev => ({ ...prev, [companyId]: 'Enter a valid 6-digit hex (e.g. #1A2B3C).' }));
      return;
    }
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.patch(`/api/admin/companies/${companyId}/branding`, { navbar_color: color || null }, { headers });
      setCompanies(prev => prev.map(c => c._id === companyId ? { ...c, navbar_color: res.data.navbar_color } : c));
      setBrandingMsg(prev => ({ ...prev, [companyId]: 'Color saved.' }));
      setTimeout(() => setBrandingMsg(prev => { const n = { ...prev }; delete n[companyId]; return n; }), 2500);
    } catch (err) {
      setBrandingMsg(prev => ({ ...prev, [companyId]: err.response?.data?.error || 'Error' }));
    }
  };

  const handleFeeRateSave = async (companyId, co) => {
    const editing = feeRateEditing[companyId] || {};
    const password = feeRatePassword[companyId] || '';
    if (!password) {
      setFeeRateMsg(prev => ({ ...prev, [companyId]: { text: 'Admin password required', type: 'error' } }));
      return;
    }
    // Parse values — empty string means "don't change this field"
    const baseTierRaw     = editing.baseTier     !== undefined && editing.baseTier     !== '' ? parseFloat(editing.baseTier)     : null;
    const thresholdRaw    = editing.threshold    !== undefined && editing.threshold    !== '' ? parseFloat(editing.threshold)    : null;
    const elevatedTierRaw = editing.elevatedTier !== undefined && editing.elevatedTier !== '' ? parseFloat(editing.elevatedTier) : null;
    const threshold2Raw   = editing.threshold2   !== undefined && editing.threshold2   !== '' ? parseFloat(editing.threshold2)   : null;
    const tier3RateRaw    = editing.tier3Rate    !== undefined && editing.tier3Rate    !== '' ? parseFloat(editing.tier3Rate)    : null;

    // Convert percent inputs to decimals for storage
    const baseTier     = baseTierRaw     != null ? baseTierRaw     / 100 : null;
    const elevatedTier = elevatedTierRaw != null ? elevatedTierRaw / 100 : null;
    const tier3Rate    = tier3RateRaw    != null ? tier3RateRaw    / 100 : null;
    const threshold    = thresholdRaw;
    const threshold2   = threshold2Raw;

    if (baseTier == null && threshold == null && elevatedTier == null && threshold2 == null && tier3Rate == null) {
      setFeeRateMsg(prev => ({ ...prev, [companyId]: { text: 'Enter at least one rate to update', type: 'error' } }));
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.patch(`/api/admin/companies/${companyId}/fee-rates`, { baseTier, threshold, elevatedTier, threshold2, tier3Rate, password }, { headers });
      setCompanies(prev => prev.map(c => c._id === companyId ? { ...c, feeRates: res.data.feeRates } : c));
      setFeeRateEditing(prev => ({ ...prev, [companyId]: {} }));
      setFeeRatePassword(prev => ({ ...prev, [companyId]: '' }));
      const msg = res.data.scheduled
        ? 'Increase scheduled — effective in 60 days. Notify the company.'
        : 'Rates updated immediately.';
      setFeeRateMsg(prev => ({ ...prev, [companyId]: { text: msg, type: res.data.scheduled ? 'warn' : 'ok' } }));
      setTimeout(() => setFeeRateMsg(prev => { const n = { ...prev }; delete n[companyId]; return n; }), 5000);
    } catch (err) {
      setFeeRateMsg(prev => ({ ...prev, [companyId]: { text: err.response?.data?.error || 'Error', type: 'error' } }));
    }
  };

  const handleFeeRateNotify = async (companyId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.patch(`/api/admin/companies/${companyId}/fee-rates/notify`, {}, { headers });
      setCompanies(prev => prev.map(c => c._id === companyId ? { ...c, feeRates: { ...c.feeRates, pendingNotifiedAt: res.data.pendingNotifiedAt } } : c));
      setFeeRateMsg(prev => ({ ...prev, [companyId]: { text: 'Marked as notified', type: 'ok' } }));
      setTimeout(() => setFeeRateMsg(prev => { const n = { ...prev }; delete n[companyId]; return n; }), 3000);
    } catch (err) {
      setFeeRateMsg(prev => ({ ...prev, [companyId]: { text: err.response?.data?.error || 'Error', type: 'error' } }));
    }
  };

  const handleFeeRateCancelPending = async (companyId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.patch(`/api/admin/companies/${companyId}/fee-rates/cancel-pending`, {}, { headers });
      setCompanies(prev => prev.map(c => {
        if (c._id !== companyId) return c;
        return { ...c, feeRates: { ...c.feeRates, pendingBaseTier: null, pendingThreshold: null, pendingElevatedTier: null, pendingThreshold2: null, pendingTier3Rate: null, pendingEffectiveAt: null, pendingSetAt: null, pendingNotifiedAt: null } };
      }));
      setFeeRateMsg(prev => ({ ...prev, [companyId]: { text: 'Pending increase cancelled', type: 'ok' } }));
      setTimeout(() => setFeeRateMsg(prev => { const n = { ...prev }; delete n[companyId]; return n; }), 3000);
    } catch (err) {
      setFeeRateMsg(prev => ({ ...prev, [companyId]: { text: err.response?.data?.error || 'Error', type: 'error' } }));
    }
  };

  // Build list of YYYY-MM strings from company start through current month (inclusive)
  function getInvoiceMonths(company) {
    const raw = company.create_date;
    const start = raw ? new Date(raw) : (() => { const d = new Date(); d.setMonth(d.getMonth() - 24); return d; })();
    if (isNaN(start.getTime())) return [];
    const months = [];
    const now    = new Date();
    const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
    // Include current month (cursor <= first of current month)
    const limit  = new Date(now.getFullYear(), now.getMonth(), 1);
    while (cursor <= limit) {
      const y = cursor.getFullYear();
      const m = String(cursor.getMonth() + 1).padStart(2, '0');
      months.unshift(`${y}-${m}`); // newest first
      cursor.setMonth(cursor.getMonth() + 1);
    }
    return months;
  }

  function formatMonthLabel(ym) {
    const [y, m] = ym.split('-');
    const label  = new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    const now    = new Date();
    const isCurrent = Number(y) === now.getFullYear() && Number(m) === now.getMonth() + 1;
    return isCurrent ? `${label} (Current)` : label;
  }

  const openInvoice = (co) => {
    const months = getInvoiceMonths(co);
    console.log('[Invoice] available months:', months);
    const defaultMonth = months[0] || '';
    setInvoiceTarget(co);
    setInvoiceMonth(defaultMonth);
    setInvoiceData(null);
    setInvoiceError('');
    if (defaultMonth) fetchInvoice(co._id, defaultMonth);
  };

  const fetchInvoice = async (companyId, month) => {
    setInvoiceLoading(true);
    setInvoiceError('');
    setInvoiceData(null);
    setInvoiceSentMsg('');
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`/api/admin/invoices/${companyId}?month=${month}`, { headers });
      setInvoiceData(res.data);
    } catch (err) {
      setInvoiceError(err.response?.data?.error || 'Failed to load invoice.');
    } finally {
      setInvoiceLoading(false);
    }
    fetchPayoutRecord(companyId, month);
  };

  const handleInvoiceMonthChange = (month) => {
    setInvoiceMonth(month);
    setInvoiceSentMsg('');
    if (invoiceTarget) {
      fetchInvoice(invoiceTarget._id, month);
    }
  };

  // Build month options for master invoice (24 months back)
  const masterInvMonths = (() => {
    const opts = [];
    const now = new Date();
    for (let i = 0; i < 24; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      opts.push({ val, label });
    }
    return opts;
  })();

  const openMasterInvoice = () => {
    const defaultMonth = masterInvMonths[1]?.val || masterInvMonths[0]?.val;
    setMasterInvData(null);
    setMasterInvError('');
    setMasterInvMonth(defaultMonth);
    setMasterInvOpen(true);
    if (defaultMonth) fetchMasterInvoice(defaultMonth);
  };

  const fetchMasterInvoice = async (month) => {
    setMasterInvLoading(true);
    setMasterInvError('');
    setMasterInvData(null);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [invRes, payRes] = await Promise.all([
        axios.get(`/api/admin/master-invoice?month=${month}`, { headers }),
        axios.get(`/api/admin/payout-records?month=${month}`, { headers }),
      ]);
      setMasterInvData(invRes.data);
      const map = {};
      for (const r of (payRes.data.records || [])) {
        map[r.companyId.toString()] = r;
      }
      setMasterPayoutMap(map);
    } catch (err) {
      setMasterInvError(err.response?.data?.error || 'Failed to load master invoice.');
    } finally {
      setMasterInvLoading(false);
    }
  };

  const fetchPayoutRecord = async (companyId, month) => {
    try {
      const res = await axios.get(`/api/admin/payout-records?companyId=${companyId}&month=${month}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const rec = res.data.records?.[0] || null;
      const key = `${companyId}-${month}`;
      setPayoutRecords(prev => ({ ...prev, [key]: rec }));
    } catch {}
  };

  const handleMarkPaid = async (companyId, month, gross, fee, net) => {
    setMarkingPaid(true);
    try {
      const res = await axios.post('/api/admin/payout-records', {
        companyId, month, gross, fee, net,
        method: payoutForm.method,
        notes:  payoutForm.notes,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setPayoutRecords(prev => ({ ...prev, [`${companyId}-${month}`]: res.data }));
      setMasterPayoutMap(prev => ({ ...prev, [companyId]: res.data }));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to record payout.');
    } finally {
      setMarkingPaid(false);
    }
  };

  const handleUndoPayout = async (recordId, companyId, month) => {
    try {
      await axios.delete(`/api/admin/payout-records/${recordId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const key = `${companyId}-${month}`;
      setPayoutRecords(prev => ({ ...prev, [key]: null }));
      setMasterPayoutMap(prev => { const n = { ...prev }; delete n[companyId]; return n; });
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to undo payout.');
    }
  };

  const handleEmailInvoice = async (companyId, month) => {
    setInvoiceSending(true);
    setInvoiceSentMsg('');
    try {
      const res = await axios.post(`/api/admin/invoices/${companyId}/send-email?month=${month}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInvoiceSentMsg(`Sent to ${res.data.to}`);
    } catch (err) {
      setInvoiceSentMsg(`Error: ${err.response?.data?.error || 'Send failed.'}`);
    } finally {
      setInvoiceSending(false);
    }
  };

  const openPurchaseLog = async (art) => {
    setPurchaseLog({ articleId: art._id, title: art.title, purchases: [], aggregate: null });
    setPurchaseLogLoading(true);
    setPurchaseLogError('');
    setPurchaseLogSort({ col: 'purchasedAt', dir: 'desc' });
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`/api/admin/articles/${art._id}/purchases`, { headers });
      setPurchaseLog({ articleId: art._id, title: art.title, purchases: res.data.purchases, aggregate: res.data.aggregate });
    } catch (err) {
      setPurchaseLogError(err.response?.data?.error || 'Failed to load purchase log.');
    } finally {
      setPurchaseLogLoading(false);
    }
  };

  const sortedPurchases = () => {
    if (!purchaseLog) return [];
    const { col, dir } = purchaseLogSort;
    return [...purchaseLog.purchases].sort((a, b) => {
      let av = a[col], bv = b[col];
      if (col === 'purchasedAt' || col === 'returnedAt') { av = av ? new Date(av) : 0; bv = bv ? new Date(bv) : 0; }
      if (av < bv) return dir === 'asc' ? -1 : 1;
      if (av > bv) return dir === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const toggleSort = (col) => {
    setPurchaseLogSort(prev => ({ col, dir: prev.col === col && prev.dir === 'asc' ? 'desc' : 'asc' }));
  };

  const sortIcon = (col) => purchaseLogSort.col === col ? (purchaseLogSort.dir === 'asc' ? ' ▲' : ' ▼') : ' ⬦';

  const openTokenPkgLog = async () => {
    setTokenPkgLog([]);
    setTokenPkgLoading(true);
    setTokenPkgError('');
    setTokenPkgSort({ col: 'purchasedAt', dir: 'desc' });
    try {
      const res = await axios.get('/api/admin/token-purchases', { headers: { Authorization: `Bearer ${token}` } });
      setTokenPkgLog(res.data);
    } catch (err) {
      setTokenPkgError(err.response?.data?.error || 'Failed to load token purchases.');
    } finally {
      setTokenPkgLoading(false);
    }
  };

  const sortedTokenPkgRows = () => {
    if (!tokenPkgLog) return [];
    const { col, dir } = tokenPkgSort;
    return [...tokenPkgLog].sort((a, b) => {
      let av = a[col], bv = b[col];
      if (col === 'purchasedAt') { av = av ? new Date(av) : 0; bv = bv ? new Date(bv) : 0; }
      if (av < bv) return dir === 'asc' ? -1 : 1;
      if (av > bv) return dir === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const toggleTokenPkgSort = (col) => {
    setTokenPkgSort(prev => ({ col, dir: prev.col === col && prev.dir === 'asc' ? 'desc' : 'asc' }));
  };

  const tokenPkgSortIcon = (col) => tokenPkgSort.col === col ? (tokenPkgSort.dir === 'asc' ? ' ▲' : ' ▼') : ' ⬦';

  return (
    <div className="dashboard-container">
      {/* ── LP5 Navbar ── */}
      <nav style={{ display: 'grid', gridTemplateColumns: '220px 1fr 300px', gridTemplateRows: '110px 36px', position: 'sticky', top: 0, zIndex: 1000, boxShadow: '0 2px 12px rgba(0,0,0,0.6)' }}>
        {/* Logo — spans both rows */}
        <div style={{ gridColumn: 1, gridRow: '1 / 3', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F41238', cursor: 'pointer' }} onClick={() => navigate('/')}>
          <img src={orangeLogo} alt="PubSilo" style={{ height: '90%', maxWidth: '210px', objectFit: 'contain' }} />
        </div>
        {/* Center row 1 — title */}
        <div style={{ gridColumn: 2, gridRow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#13273F' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#aaa', fontSize: '13px', fontFamily: 'Arial, sans-serif', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '6px' }}>PubSilo Admin</div>
            <div style={{ color: '#fff', fontSize: '28px', fontWeight: '300', fontFamily: 'Arial, sans-serif', letterSpacing: '0.04em', lineHeight: 1 }}>Site Administration</div>
          </div>
        </div>
        {/* Right — spans both rows */}
        <div style={{ gridColumn: 3, gridRow: '1 / 3', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', background: '#13273F' }}>
          <div style={{ color: '#fff', fontSize: '15px', fontFamily: 'Arial, sans-serif', fontWeight: '700' }}>sctadmin73</div>
          <div style={{ color: '#aaa', fontSize: '11px', fontFamily: 'Arial, sans-serif', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Admin</div>
          <button onClick={handleLogout} style={{ marginTop: '4px', padding: '6px 16px', background: 'transparent', border: '1px solid #3a3a3a', borderRadius: '5px', color: '#ccc', fontSize: '12px', fontFamily: 'Arial, sans-serif', cursor: 'pointer' }}
            onMouseEnter={e => { e.currentTarget.style.background = '#222'; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#ccc'; }}>
            Log Out
          </button>
        </div>
        {/* Row 2 — section jump strip */}
        <div style={{ gridColumn: 2, gridRow: 2, display: 'flex', alignItems: 'center', background: '#13273F', borderBottom: '2px solid #000' }}>
          {[
            { label: 'Companies',   anchor: 'section-companies' },
            { label: 'Revenue',     anchor: 'section-revenue' },
            { label: 'Users',       anchor: 'section-users' },
            { label: 'Promo Codes', anchor: 'section-promo' },
            { label: 'Returns',     anchor: 'section-returns' },
            { label: 'Broken Links',anchor: 'section-broken' },
            { label: 'Analytics',   anchor: 'section-analytics' },
          ].map(({ label, anchor }) => (
            <button key={anchor} onClick={() => document.getElementById(anchor)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              style={{ padding: '0 18px', height: '100%', background: 'transparent', border: 'none', borderBottom: '2px solid transparent', color: '#aaa', fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: 'Arial, sans-serif', cursor: 'pointer', marginBottom: '-2px' }}
              onMouseEnter={e => { e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderBottomColor = '#F41238'; }}
              onMouseLeave={e => { e.currentTarget.style.color = '#aaa'; e.currentTarget.style.borderBottomColor = 'transparent'; }}>
              {label}
            </button>
          ))}
          <div style={{ marginLeft: 'auto', paddingRight: '1rem' }}>
            <button onClick={() => navigate('/admin/add-company')}
              style={{ padding: '0 16px', height: '26px', background: '#16a34a', border: 'none', borderRadius: '5px', color: '#fff', fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: 'Arial, sans-serif', cursor: 'pointer' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#15803d'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#16a34a'; }}>
              + Add Publisher
            </button>
          </div>
        </div>
      </nav>

      <div style={{ padding: '2rem 2.5rem', maxWidth: '1100px', margin: '0 auto' }}>
        {loading && <p style={{ color: '#888' }}>Loading stats...</p>}
        {error   && <p style={{ color: 'red'  }}>{error}</p>}

        {stats && (
          <>
            {/* ── Today ── */}
            <h5 style={{ color: '#555', marginBottom: '0.75rem', textTransform: 'uppercase', fontSize: '0.78rem', letterSpacing: '0.07em' }}>
              Today — {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </h5>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginBottom: '2rem' }}>
              <StatCard label="Unique Visitors"     value={(stats.today?.uniqueVisitors    ?? 0).toLocaleString()} sub="distinct sessions today" />
              <StatCard label="New Users"          value={(stats.today?.newUsers          ?? 0).toLocaleString()} sub="registered today" />
              <StatCard label="Articles Published" value={(stats.today?.articlesPublished  ?? 0).toLocaleString()} sub="created today" />
              <StatCard label="Articles Purchased" value={(stats.today?.articlesPurchased  ?? 0).toLocaleString()} sub="purchases today" />
              <StatCard label="Article Credit Packages Sold" value={(stats.today?.tokenPackages     ?? 0).toLocaleString()} sub="packages bought today" />
            </div>

            {/* ── Platform Counts ── */}
            <h5 style={{ color: '#555', marginBottom: '0.75rem', textTransform: 'uppercase', fontSize: '0.78rem', letterSpacing: '0.07em' }}>Platform</h5>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginBottom: '2rem' }}>
              <StatCard label="Total Articles"   value={stats.totalArticles.toLocaleString()} />
              <StatCard label="Avg Credit Price"  value={`${round2(stats.avgTokenPrice)} credits`} sub="across all articles" />
              <StatCard label="Total Users"      value={stats.totalUsers.toLocaleString()} sub="reader accounts" />
              <StatCard label="Total Companies"  value={stats.totalCompanies.toLocaleString()} sub="publisher accounts" />
            </div>

            {/* ── Revenue ── */}
            <h5 id="section-revenue" style={{ color: '#555', marginBottom: '0.75rem', textTransform: 'uppercase', fontSize: '0.78rem', letterSpacing: '0.07em', scrollMarginTop: '160px' }}>Revenue</h5>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginBottom: '2rem' }}>
              <StatCard
                label="Gross Credit Revenue"
                value={fmt(stats.totalGrossRevenue)}
                sub={`${stats.tokenPurchaseCount} package purchase${stats.tokenPurchaseCount !== 1 ? 's' : ''}`}
              />
              <StatCard
                label="Stripe Fees"
                value={`−${fmt(stats.totalStripeFees)}`}
                sub="processing costs"
              />
              <StatCard
                label="Company Bank"
                value={`−${fmt(stats.totalCompanyBank)}`}
                sub="credits × $0.01"
              />
              <StatCard
                label="Net Platform Revenue"
                value={fmt(stats.totalPlatformNet)}
                sub="after Stripe and company payouts"
              />
              <StatCard
                label="Article Gross Revenue"
                value={fmt(stats.totalArticleGross ?? 0)}
                sub="credits × $0.01, all time"
              />
              <StatCard
                label="Article Purchases"
                value={stats.totalPurchaseCount.toLocaleString()}
                sub="all time"
              />
            </div>

            {/* ── Advertisements ── */}
            <h5 style={{ color: '#555', marginBottom: '0.75rem', textTransform: 'uppercase', fontSize: '0.78rem', letterSpacing: '0.07em' }}>Advertisements</h5>
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
              <div
                onClick={() => { setAdsOpen(true); loadAds(); }}
                style={{ backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px', padding: '1.25rem 1.5rem', flex: '1 1 200px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 3px 12px rgba(0,0,0,0.14)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.06)'}
              >
                <div style={{ fontSize: '0.8rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.4rem' }}>Active Campaigns</div>
                <div style={{ fontSize: '1.75rem', fontWeight: '800', color: '#1a1a1a', lineHeight: 1.1 }}>
                  {adsList.length ? adsList.filter(a => a.active).length : '—'}
                </div>
                <div style={{ fontSize: '0.78rem', color: '#aaa', marginTop: '0.25rem' }}>Click to manage all ads</div>
              </div>
              <div
                onClick={() => { setAdsOpen(true); loadAds(); setAdError(''); setAdForm({ ...AD_BLANK }); }}
                style={{ backgroundColor: '#13273F', border: '1px solid #0d1f33', borderRadius: '10px', padding: '1.25rem 1.5rem', flex: '1 1 200px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 3px 12px rgba(0,0,0,0.25)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.06)'}
              >
                <div style={{ fontSize: '0.8rem', color: '#7a9cbf', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.4rem' }}>Quick Action</div>
                <div style={{ fontSize: '1.25rem', fontWeight: '800', color: '#fff', lineHeight: 1.1 }}>+ New Ad</div>
                <div style={{ fontSize: '0.78rem', color: '#7a9cbf', marginTop: '0.25rem' }}>Create a new campaign</div>
              </div>
            </div>

            {/* ── Token Package Purchase Counts ── */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <h5 style={{ color: '#555', margin: 0, textTransform: 'uppercase', fontSize: '0.78rem', letterSpacing: '0.07em' }}>Article Credit Package Sales</h5>
              <button
                onClick={openTokenPkgLog}
                style={{ fontSize: '0.78rem', padding: '0.3rem 0.8rem', backgroundColor: '#0d3165', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
              >
                🧾 View All Purchases
              </button>
            </div>
            <div style={{ backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px', overflow: 'hidden', marginBottom: '1rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #e0e0e0' }}>
                    <th style={th}>Package</th>
                    <th style={th}>Price</th>
                    <th style={th}>Purchases</th>
                    <th style={th}>Gross Collected</th>
                    <th style={th}>Stripe Fees</th>
                    <th style={th}>To Companies</th>
                    <th style={th}>Platform Net</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { id: 'starter',  label: 'Starter',  price: 6.00,  tokens: 500 },
                    { id: 'standard', label: 'Standard', price: 10.00, tokens: 900 },
                  ].map((row, i) => {
                    const r2 = v => Math.round(v * 100) / 100;
                    const ceilS = v => Math.ceil(v * 100) / 100;
                    const count       = stats.packageCounts?.[row.id] ?? 0;
                    const gross       = count * row.price;
                    const stripePer   = ceilS(row.price * 0.029 + 0.30);
                    const toCoPer     = r2(row.tokens * 0.01);
                    const sitePer     = r2(row.price - stripePer - toCoPer);
                    const stripe      = r2(count * stripePer);
                    const toCo        = r2(count * toCoPer);
                    const site        = r2(count * sitePer);
                    return (
                      <tr key={i} style={{ borderBottom: '1px solid #f0f0f0', backgroundColor: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                        <td style={{ ...td, fontWeight: '600' }}>{row.label}</td>
                        <td style={td}>{fmt(row.price)}</td>
                        <td style={{ ...td, fontWeight: '600' }}>{count.toLocaleString()}</td>
                        <td style={td}>{fmt(gross)}</td>
                        <td style={{ ...td, color: '#dc3545' }}>{fmt(stripe)}</td>
                        <td style={{ ...td, color: '#28a745', fontWeight: '600' }}>{fmt(toCo)}</td>
                        <td style={{ ...td, color: '#555' }}>{fmt(site)}</td>
                      </tr>
                    );
                  })}
                  {/* Totals row */}
                  <tr style={{ borderTop: '2px solid #e0e0e0', backgroundColor: '#f8f9fa', fontWeight: '700' }}>
                    <td style={td}>Total</td>
                    <td style={td}>—</td>
                    <td style={td}>{stats.tokenPurchaseCount.toLocaleString()}</td>
                    <td style={td}>{fmt(stats.totalGrossRevenue)}</td>
                    <td style={{ ...td, color: '#dc3545' }}>{fmt(stats.totalStripeFees)}</td>
                    <td style={{ ...td, color: '#28a745' }}>{fmt(stats.totalCompanyBank)}</td>
                    <td style={{ ...td, color: '#555' }}>{fmt(stats.totalPlatformNet)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* ── Company Payment Bank ── */}
            <div style={{
              backgroundColor: '#f0faf4', border: '2px solid #28a745', borderRadius: '10px',
              padding: '1.25rem 1.75rem', marginBottom: '0.75rem',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem',
            }}>
              <div>
                <div style={{ fontSize: '0.8rem', color: '#555', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.25rem' }}>Company Payment Bank</div>
                <div style={{ fontSize: '0.85rem', color: '#777' }}>
                  {fmt(stats.totalGrossRevenue)} gross &minus; {fmt(stats.totalStripeFees)} Stripe &minus; {fmt(stats.totalPlatformNet)} pkg margin
                </div>
              </div>
              <div style={{ fontSize: '2.25rem', fontWeight: '900', color: '#1a7a3a', lineHeight: 1 }}>
                {fmt(stats.totalCompanyBank)}
              </div>
            </div>

            {/* ── Article Gross Sales ── */}
            <div style={{
              backgroundColor: '#fafafa', border: '2px solid #aaa', borderRadius: '10px',
              padding: '1.25rem 1.75rem', marginBottom: '0.75rem',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem',
            }}>
              <div>
                <div style={{ fontSize: '0.8rem', color: '#555', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.25rem' }}>Article Gross Sales</div>
                <div style={{ fontSize: '0.85rem', color: '#777' }}>credits redeemed for articles &times; $0.01</div>
              </div>
              <div style={{ fontSize: '2.25rem', fontWeight: '900', color: '#333', lineHeight: 1 }}>
                {fmt(stats.totalArticleGross ?? 0)}
              </div>
            </div>

            {/* ── Platform Fee + Net Payout — 50/50 row ── */}
            <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem' }}>
              {/* PubSilo Article Platform Fee */}
              <div style={{
                flex: '1 1 0', backgroundColor: '#fff8f0', border: '2px solid #e67e22', borderRadius: '10px',
                padding: '1.25rem 1.75rem',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem',
              }}>
                <div>
                  <div style={{ fontSize: '0.8rem', color: '#555', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.25rem' }}>
                    PubSilo Platform Fee
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#777' }}>
                    tiered 9–15%
                  </div>
                </div>
                <div style={{ fontSize: '2.25rem', fontWeight: '900', color: '#c0710a', lineHeight: 1 }}>
                  {fmt(stats.totalArticleSiteFee ?? 0)}
                </div>
              </div>
              {/* Net to Pay Out to Companies */}
              <div style={{
                flex: '1 1 0', backgroundColor: '#f5f0ff', border: '2px solid #7b2ff7', borderRadius: '10px',
                padding: '1.25rem 1.75rem',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem',
              }}>
                <div>
                  <div style={{ fontSize: '0.8rem', color: '#555', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.25rem' }}>Net to Pay Out</div>
                  <div style={{ fontSize: '0.85rem', color: '#777' }}>
                    article gross &minus; platform fee
                  </div>
                </div>
                <div style={{ fontSize: '2.25rem', fontWeight: '900', color: '#7b2ff7', lineHeight: 1 }}>
                  {fmt(stats.totalArticlePayout ?? 0)}
                </div>
              </div>
            </div>

            {/* ── Company Bank Balance ── */}
            <div style={{
              backgroundColor: '#f8fff9', border: '1px solid #a3d9b1', borderRadius: '10px',
              padding: '1.25rem 1.75rem', marginBottom: '1rem',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem',
            }}>
              <div>
                <div style={{ fontSize: '0.8rem', color: '#555', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.25rem' }}>Company Bank Balance</div>
                <div style={{ fontSize: '0.85rem', color: '#777' }}>
                  {fmt(stats.totalCompanyBank)} bank &minus; {fmt(stats.totalArticleGross ?? 0)} article gross sales
                </div>
              </div>
              <div style={{ fontSize: '2.25rem', fontWeight: '900', color: (stats.companyBankBalance ?? 0) >= 0 ? '#1a7a3a' : '#dc3545', lineHeight: 1 }}>
                {fmt(stats.companyBankBalance ?? 0)}
              </div>
            </div>

            {/* ── PubSilo Token Package Fee Revenue ── */}
            <div style={{
              backgroundColor: '#f0f4ff', border: '2px solid #0d3165', borderRadius: '10px',
              padding: '1.25rem 1.75rem', marginBottom: '2rem',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem',
            }}>
              <div>
                <div style={{ fontSize: '0.8rem', color: '#555', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.25rem' }}>
                  PubSilo Article Credit Package Fee Revenue
                </div>
                <div style={{ fontSize: '0.85rem', color: '#777' }}>
                  {fmt(stats.totalGrossRevenue)} gross &minus; {fmt(stats.totalStripeFees)} Stripe &minus; {fmt(stats.totalCompanyBank)} company pool
                  &nbsp;&middot;&nbsp; {stats.tokenPurchaseCount} package sale{stats.tokenPurchaseCount !== 1 ? 's' : ''}
                </div>
              </div>
              <div style={{ fontSize: '2.25rem', fontWeight: '900', color: '#0d3165', lineHeight: 1 }}>
                {fmt(stats.totalPlatformNet)}
              </div>
            </div>

            {/* ── Master Invoice ── */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2rem' }}>
              <div>
                <div style={{ fontSize: '0.8rem', color: '#555', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>Master Invoice</div>
                <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '0.2rem' }}>All companies · publisher payouts + PubSilo revenue by month</div>
              </div>
              <button
                onClick={openMasterInvoice}
                style={{ padding: '0.55rem 1.25rem', background: '#0d3165', color: '#fff', border: 'none', borderRadius: '7px', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer' }}
              >
                📊 Open Master Invoice
              </button>
            </div>

            {/* ── Live Analytics ── */}
            <h5 style={{ color: '#555', marginBottom: '0.75rem', textTransform: 'uppercase', fontSize: '0.78rem', letterSpacing: '0.07em' }}>Live Right Now</h5>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginBottom: '2rem' }}>
              <StatCard
                label="Silo Visitors"
                value={liveStats ? (liveStats.totalVisitors > 0 ? `🟢 ${liveStats.totalVisitors}` : '0') : '—'}
                sub="across all company silos"
              />
              <StatCard
                label="Article Readers"
                value={liveStats ? (liveStats.totalReaders > 0 ? `🟢 ${liveStats.totalReaders}` : '0') : '—'}
                sub="users with an article open"
              />
            </div>

            {/* ── Token Package Reference ── */}
            <h5 style={{ color: '#555', marginBottom: '0.75rem', textTransform: 'uppercase', fontSize: '0.78rem', letterSpacing: '0.07em' }}>Article Credit Package Breakdown</h5>
            <div style={{ backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px', overflow: 'hidden', marginBottom: '2rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #e0e0e0' }}>
                    <th style={th}>Package</th>
                    <th style={th}>Price</th>
                    <th style={th}>Credits</th>
                    <th style={th}>Stripe Fee</th>
                    <th style={th}>Site Fee (net)</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: 'Starter',  price: 6.00,  tokens: 500, stripe: 0.48, site: 0.52 },
                    { label: 'Standard', price: 10.00, tokens: 900, stripe: 0.59, site: 0.41 },
                  ].map((row, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f0f0f0', backgroundColor: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <td style={{ ...td, fontWeight: '600' }}>{row.label}</td>
                      <td style={td}>{fmt(row.price)}</td>
                      <td style={td}>{row.tokens.toLocaleString()} credits</td>
                      <td style={td}>{fmt(row.stripe)}</td>
                      <td style={{ ...td, color: '#28a745', fontWeight: '600' }}>{fmt(row.site)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* ── All Users Table ── */}
            <div id="section-users" style={{ scrollMarginTop: '160px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                <h5 style={{ color: '#555', margin: 0, textTransform: 'uppercase', fontSize: '0.78rem', letterSpacing: '0.07em' }}>All Users</h5>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  {allUsersLoaded && (
                    <>
                      <input
                        value={userFilter}
                        onChange={e => setUserFilter(e.target.value)}
                        placeholder="Filter by username or email…"
                        style={{ padding: '0.35rem 0.65rem', border: '1px solid #ccc', borderRadius: '6px', fontSize: '0.82rem', width: '220px', outline: 'none' }}
                      />
                      <select
                        value={userStatusFilter}
                        onChange={e => setUserStatusFilter(e.target.value)}
                        style={{ padding: '0.35rem 0.55rem', border: '1px solid #ccc', borderRadius: '6px', fontSize: '0.82rem', background: '#fff' }}
                      >
                        <option value="all">All Statuses</option>
                        <option value="active">Active</option>
                        <option value="suspended">CB Suspended</option>
                      </select>
                    </>
                  )}
                  <button
                    onClick={fetchAllUsers}
                    disabled={allUsersLoading}
                    style={{ padding: '0.35rem 0.9rem', background: '#0d3165', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '0.82rem', fontWeight: '700', cursor: allUsersLoading ? 'not-allowed' : 'pointer', opacity: allUsersLoading ? 0.7 : 1, whiteSpace: 'nowrap' }}
                  >
                    {allUsersLoading ? 'Loading…' : allUsersLoaded ? '↻ Refresh' : 'Load Users'}
                  </button>
                </div>
              </div>

              {allUsersLoaded && (() => {
                const filtered = allUsers.filter(u => {
                  if (userStatusFilter === 'active' && u.chargebackSuspended) return false;
                  if (userStatusFilter === 'suspended' && !u.chargebackSuspended) return false;
                  if (userFilter) {
                    const q = userFilter.toLowerCase();
                    if (!u.username?.toLowerCase().includes(q) && !u.email?.toLowerCase().includes(q)) return false;
                  }
                  return true;
                });
                const sorted = [...filtered].sort((a, b) => {
                  let av = a[userSort], bv = b[userSort];
                  if (userSort === 'date_created' || userSort === 'last_visit' || userSort === 'chargebackSuspendedAt') {
                    av = av ? new Date(av).getTime() : 0;
                    bv = bv ? new Date(bv).getTime() : 0;
                  } else if (typeof av === 'string') {
                    av = (av || '').toLowerCase(); bv = (bv || '').toLowerCase();
                  } else {
                    av = av ?? -Infinity; bv = bv ?? -Infinity;
                  }
                  return userSortDir === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
                });

                const colHdr = (col, label) => {
                  const active = userSort === col;
                  return (
                    <th
                      onClick={() => { if (active) setUserSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setUserSort(col); setUserSortDir('desc'); } }}
                      style={{ ...th, cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none', background: active ? '#e8eeff' : '#f8f9fa', fontSize: '0.72rem' }}
                    >{label} {active ? (userSortDir === 'asc' ? '▲' : '▼') : ''}</th>
                  );
                };

                return (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <div style={{ fontSize: '0.78rem', color: '#888', marginBottom: '0.4rem' }}>
                      {filtered.length} user{filtered.length !== 1 ? 's' : ''} {userFilter || userStatusFilter !== 'all' ? `(filtered from ${allUsers.length})` : ''}
                    </div>
                    <div style={{ backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px', overflow: 'hidden', overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem', minWidth: '960px' }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid #e0e0e0', background: '#f8f9fa' }}>
                            <th style={{ ...th, width: '32px', textAlign: 'center' }}>
                              <input
                                type="checkbox"
                                checked={sorted.length > 0 && sorted.every(u => selectedUserIds.has(String(u._id)))}
                                onChange={e => {
                                  if (e.target.checked) {
                                    setSelectedUserIds(new Set(sorted.map(u => String(u._id))));
                                  } else {
                                    setSelectedUserIds(new Set());
                                  }
                                }}
                                title="Select all visible"
                              />
                            </th>
                            {colHdr('username', 'Username')}
                            {colHdr('email', 'Email')}
                            {colHdr('date_created', 'Joined')}
                            {colHdr('last_visit', 'Last Login')}
                            {colHdr('last_login_ip', 'Last IP')}
                            {colHdr('state', 'State')}
                            {colHdr('zipcode', 'ZIP')}
                            {colHdr('tokens', 'Balance')}
                            {colHdr('totalSpent', 'Total Spent')}
                            {colHdr('purchaseCount', '# Pkgs')}
                            {colHdr('articlesPurchased', 'Articles')}
                            <th style={{ ...th, fontSize: '0.72rem', whiteSpace: 'nowrap' }}>Status</th>
                            <th style={{ ...th, fontSize: '0.72rem' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sorted.length === 0 && (
                            <tr><td colSpan={14} style={{ ...td, color: '#aaa', textAlign: 'center', fontStyle: 'italic' }}>No users match this filter.</td></tr>
                          )}
                          {sorted.map((u, i) => (
                            <tr key={u._id} style={{ borderBottom: '1px solid #f0f0f0', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                              <td style={{ ...td, textAlign: 'center', width: '32px' }}>
                                <input
                                  type="checkbox"
                                  checked={selectedUserIds.has(String(u._id))}
                                  onChange={e => {
                                    setSelectedUserIds(prev => {
                                      const next = new Set(prev);
                                      if (e.target.checked) next.add(String(u._id));
                                      else next.delete(String(u._id));
                                      return next;
                                    });
                                  }}
                                />
                              </td>
                              <td style={{ ...td, fontWeight: 700, color: '#0d3165', fontSize: '0.82rem' }}>{u.username}</td>
                              <td style={{ ...td, color: '#666', fontSize: '0.78rem' }}>{u.email}</td>
                              <td style={{ ...td, whiteSpace: 'nowrap', color: '#888', fontSize: '0.78rem' }}>
                                {u.date_created ? new Date(u.date_created).toLocaleDateString() : '—'}
                              </td>
                              <td style={{ ...td, whiteSpace: 'nowrap', color: '#888', fontSize: '0.78rem' }}>
                                {u.last_visit ? new Date(u.last_visit).toLocaleDateString() : '—'}
                              </td>
                              <td style={{ ...td, fontFamily: 'monospace', fontSize: '0.72rem', color: '#888' }}>{u.last_login_ip || '—'}</td>
                              <td style={{ ...td, fontSize: '0.78rem', color: '#555' }}>{u.state || '—'}</td>
                              <td style={{ ...td, fontFamily: 'monospace', fontSize: '0.78rem', color: '#555' }}>{u.zipcode || '—'}</td>
                              <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{(u.tokens || 0).toLocaleString()}</td>
                              <td style={{ ...td, textAlign: 'right', color: '#1a7a3a', fontWeight: 700, fontSize: '0.8rem' }}>{fmt(u.totalSpent || 0)}</td>
                              <td style={{ ...td, textAlign: 'center', color: '#555' }}>{u.purchaseCount || 0}</td>
                              <td style={{ ...td, textAlign: 'center', color: '#555' }}>{u.articlesPurchased || 0}</td>
                              <td style={td}>
                                {u.chargebackSuspended ? (
                                  <span style={{ display: 'inline-block', padding: '0.15rem 0.5rem', background: '#ffe0e0', color: '#cc0000', borderRadius: 99, fontSize: '0.7rem', fontWeight: 700 }}>
                                    🚫 CB Suspended
                                  </span>
                                ) : (
                                  <span style={{ display: 'inline-block', padding: '0.15rem 0.5rem', background: '#e6f9ec', color: '#1a7a3a', borderRadius: 99, fontSize: '0.7rem', fontWeight: 700 }}>
                                    Active
                                  </span>
                                )}
                              </td>
                              <td style={{ ...td, whiteSpace: 'nowrap' }}>
                                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'nowrap' }}>
                                  <button
                                    onClick={() => lookupUserFromTable(u.username)}
                                    style={{ padding: '0.2rem 0.55rem', fontSize: '0.72rem', background: '#0d3165', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 600 }}
                                  >
                                    History
                                  </button>
                                  {u.chargebackSuspended ? (
                                    <button
                                      onClick={() => setUnsuspendConfirm(u)}
                                      style={{ padding: '0.2rem 0.55rem', fontSize: '0.72rem', background: '#28a745', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 600 }}
                                    >
                                      Unsuspend
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => { setChargebackModal(u); setChargebackForm({ amount: '', reason: '', stripeChargeId: '' }); }}
                                      style={{ padding: '0.2rem 0.55rem', fontSize: '0.72rem', background: '#cc0000', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 600 }}
                                    >
                                      CB Suspend
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                  {/* Floating action bar */}
                  {selectedUserIds.size > 0 && (
                    <div style={{
                      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9000,
                      background: '#0d3165', color: '#fff',
                      padding: '0.75rem 2rem', display: 'flex', alignItems: 'center', gap: '1rem',
                      boxShadow: '0 -3px 12px rgba(0,0,0,0.25)',
                    }}>
                      <span style={{ fontWeight: 700, fontSize: '0.92rem' }}>
                        ✓ {selectedUserIds.size} user{selectedUserIds.size !== 1 ? 's' : ''} selected
                      </span>
                      <button
                        onClick={() => setSelectedUserIds(new Set())}
                        style={{ padding: '0.35rem 0.9rem', background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '5px', cursor: 'pointer', fontSize: '0.82rem' }}
                      >
                        Clear
                      </button>
                      <button
                        onClick={() => { setBroadcastResult(null); setBroadcastModal(true); }}
                        style={{ padding: '0.35rem 0.9rem', background: '#F41238', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 700, fontSize: '0.82rem' }}
                      >
                        📧 Send Email
                      </button>
                    </div>
                  )}
                  </div>
                );
              })()}
            </div>

            {/* ── Find User History ── */}
            <h5 style={{ color: '#555', marginBottom: '0.75rem', textTransform: 'uppercase', fontSize: '0.78rem', letterSpacing: '0.07em', scrollMarginTop: '160px' }}>Find User History</h5>
            <form onSubmit={fetchUserHistory} style={{ display: 'flex', gap: '0.6rem', marginBottom: '2rem', maxWidth: 520 }}>
              <input
                value={userSearch}
                onChange={e => setUserSearch(e.target.value)}
                placeholder="Username or email address…"
                style={{ flex: 1, padding: '0.6rem 0.9rem', border: '1px solid #ccc', borderRadius: '7px', fontSize: '0.92rem', outline: 'none' }}
              />
              <button
                type="submit"
                disabled={userHistoryLoading || !userSearch.trim()}
                style={{ padding: '0.6rem 1.2rem', backgroundColor: userHistoryLoading ? '#aaa' : '#0d3165', color: '#fff', border: 'none', borderRadius: '7px', fontWeight: '700', cursor: userHistoryLoading ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}
              >
                {userHistoryLoading ? 'Searching…' : 'Look Up'}
              </button>
            </form>
            {userHistoryError && <p style={{ color: '#dc3545', fontSize: '0.88rem', marginTop: '-1.5rem', marginBottom: '2rem' }}>{userHistoryError}</p>}

            {/* ── Per-Company Breakdown ── */}
            <h5 id="section-companies" style={{ color: '#555', marginBottom: '0.75rem', textTransform: 'uppercase', fontSize: '0.78rem', letterSpacing: '0.07em', scrollMarginTop: '160px' }}>Companies</h5>
            <div style={{ backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px', overflow: 'hidden', marginBottom: '2rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #e0e0e0' }}>
                    <th style={th}>Company</th>
                    <th style={th}>Followers</th>
                    <th style={th}>Articles</th>
                    <th style={th}>Purchases</th>
                    <th style={th}>Co. Revenue</th>
                    <th style={th}>Invoice</th>
                  </tr>
                </thead>
                <tbody>
                  {companies.length === 0 && (
                    <tr><td colSpan={6} style={{ ...td, color: '#aaa', textAlign: 'center' }}>No companies yet.</td></tr>
                  )}
                  {companies.map((co, i) => (
                    <React.Fragment key={co._id}>
                      <tr
                        style={{ borderBottom: '1px solid #f0f0f0', backgroundColor: i % 2 === 0 ? '#fff' : '#fafafa', cursor: co.articles.length > 0 ? 'pointer' : 'default' }}
                        onClick={() => co.articles.length > 0 && setExpandedCompany(expandedCompany === co._id ? null : co._id)}
                      >
                        <td style={{ ...td, fontWeight: '600' }}>
                          {co.articles.length > 0 && (
                            <span style={{ fontSize: '0.7rem', marginRight: '0.4rem', color: '#aaa' }}>
                              {expandedCompany === co._id ? '▲' : '▼'}
                            </span>
                          )}
                          {co.company_name}
                          {co.disabled && (
                            <span style={{ marginLeft: '0.5rem', fontSize: '0.68rem', color: '#fff', backgroundColor: '#cc0000', padding: '1px 5px', borderRadius: '3px', fontWeight: '700' }}>🚫 DISABLED</span>
                          )}
                          {co.deletionFlagged && (
                            <span style={{ marginLeft: '0.5rem', fontSize: '0.68rem', color: '#fff', backgroundColor: '#e67e22', padding: '1px 5px', borderRadius: '3px', fontWeight: '700' }}>⚠️ DEBT ${(co.deletionDebt || 0).toFixed(2)}</span>
                          )}
                          {!co.w9?.submitted && (
                            <span style={{ marginLeft: '0.5rem', fontSize: '0.68rem', color: '#7a4e00', backgroundColor: '#fff3cd', padding: '1px 5px', borderRadius: '3px', fontWeight: '700' }}>⚠️ NO W-9</span>
                          )}
                          {co.w9?.submitted && (
                            <span style={{ marginLeft: '0.5rem', fontSize: '0.68rem', color: '#155724', backgroundColor: '#d4edda', padding: '1px 5px', borderRadius: '3px', fontWeight: '700' }}>W-9 ✓</span>
                          )}
                          {liveStats?.byCompany?.[co._id] > 0 && (
                            <span style={{ marginLeft: '0.5rem', fontSize: '0.72rem', color: '#28a745', fontWeight: '400' }}>
                              🟢 {liveStats.byCompany[co._id]} live
                            </span>
                          )}
                          <div style={{ marginTop: '0.3rem', fontSize: '0.72rem', color: '#888', display: 'flex', flexWrap: 'wrap', gap: '0.6rem', fontWeight: '400' }}>
                            <span title="Last login (company or any author/editor)">🔐 {co.last_login_at ? new Date(co.last_login_at).toLocaleDateString() : '—'}</span>
                            <span title="Last article created">📝 {co.lastArticleDate ? new Date(co.lastArticleDate).toLocaleDateString() : '—'}</span>
                            <span title="New articles in past 30 days">30d: <strong style={{ color: '#555' }}>{co.articlesLast30 ?? 0}</strong></span>
                            <span title="Average new articles per month since account creation">avg: <strong style={{ color: '#555' }}>{co.avgArticlesPerMonth ?? 0}/mo</strong></span>
                          </div>
                        </td>
                        <td style={{ ...td, textAlign: 'center' }}>{co.followerCount || 0}</td>
                        <td style={td}>{co.articleCount}</td>
                        <td style={td}>{co.totalPurchases}</td>
                        <td style={{ ...td, fontWeight: '600' }}>{fmt(co.totalGrossRevenue)}</td>
                        <td style={{ ...td }} onClick={e => e.stopPropagation()}>
                          <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                            <button
                              onClick={() => openInvoice(co)}
                              style={{ fontSize: '0.72rem', padding: '0.25rem 0.6rem', backgroundColor: '#0d3165', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                            >
                              📄 Invoice
                            </button>
                          </div>
                        </td>
                      </tr>
                      {expandedCompany === co._id && (() => {
                        const loc = locEditing[co._id] ?? {
                          zipcode:       co.location?.zipcode       ?? '',
                          city:          co.location?.city          ?? '',
                          state:         co.location?.state         ?? '',
                          isLocalOnly:   co.location?.isLocalOnly   ?? false,
                          serviceRadius: co.location?.serviceRadius ?? 15,
                        };
                        const setLoc = (patch) => setLocEditing(prev => ({ ...prev, [co._id]: { ...loc, ...patch } }));
                        const currentMedia = mediaEditing[co._id] ?? (co.mediaType || 'news');
                        return (
                          <tr key="loc-row" style={{ backgroundColor: '#fffbf0' }}>
                            <td colSpan={6} style={{ ...td, padding: '0.75rem 1.5rem' }}>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', alignItems: 'flex-end', fontSize: '0.8rem', marginBottom: '0.6rem' }}>
                                <span style={{ fontWeight: 600, color: '#555', marginRight: '0.25rem' }}>Media Type:</span>
                                <select
                                  value={currentMedia}
                                  onChange={e => setMediaEditing(prev => ({ ...prev, [co._id]: e.target.value }))}
                                  style={{ fontSize: '0.78rem', padding: '0.2rem 0.35rem', border: '1px solid #ccc', borderRadius: '4px' }}
                                >
                                  {['news', 'magazine', 'blog', 'podcast'].map(mt => (
                                    <option key={mt} value={mt}>{mt.charAt(0).toUpperCase() + mt.slice(1)}</option>
                                  ))}
                                </select>
                                <button onClick={() => handleMediaSave(co._id)}
                                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.7rem', backgroundColor: '#2a7a3b', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', alignSelf: 'flex-end' }}>
                                  Save Media Type
                                </button>
                                {mediaMsg[co._id] && (
                                  <span style={{ fontSize: '0.72rem', color: mediaMsg[co._id] === 'Saved' ? '#28a745' : '#dc3545', alignSelf: 'flex-end' }}>
                                    {mediaMsg[co._id]}
                                  </span>
                                )}
                                <span style={{ fontSize: '0.7rem', color: '#888', alignSelf: 'flex-end' }}>
                                  Current: {co.mediaType || 'news'}
                                </span>
                              </div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', alignItems: 'flex-end', fontSize: '0.8rem' }}>
                                <span style={{ fontWeight: 600, color: '#555', marginRight: '0.25rem' }}>Location:</span>
                                <label style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                  ZIP
                                  <input value={loc.zipcode} onChange={e => setLoc({ zipcode: e.target.value })}
                                    style={{ width: '70px', fontSize: '0.78rem', padding: '0.2rem 0.35rem', border: '1px solid #ccc', borderRadius: '4px' }} />
                                </label>
                                <label style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                  City
                                  <input value={loc.city} onChange={e => setLoc({ city: e.target.value })}
                                    style={{ width: '110px', fontSize: '0.78rem', padding: '0.2rem 0.35rem', border: '1px solid #ccc', borderRadius: '4px' }} />
                                </label>
                                <label style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                  State
                                  <input value={loc.state} onChange={e => setLoc({ state: e.target.value.toUpperCase().slice(0, 2) })}
                                    style={{ width: '48px', fontSize: '0.78rem', padding: '0.2rem 0.35rem', border: '1px solid #ccc', borderRadius: '4px' }} />
                                </label>
                                <label style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                  Radius (mi)
                                  <input type="number" min="1" value={loc.serviceRadius} onChange={e => setLoc({ serviceRadius: e.target.value })}
                                    style={{ width: '70px', fontSize: '0.78rem', padding: '0.2rem 0.35rem', border: '1px solid #ccc', borderRadius: '4px' }} />
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', marginBottom: '2px' }}>
                                  <input type="checkbox" checked={!!loc.isLocalOnly} onChange={e => setLoc({ isLocalOnly: e.target.checked })} />
                                  Local-only
                                </label>
                                <button onClick={() => handleLocSave(co._id)}
                                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.7rem', backgroundColor: '#5a4a8a', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', alignSelf: 'flex-end' }}>
                                  Save Location
                                </button>
                                {locMsg[co._id] && (
                                  <span style={{ fontSize: '0.72rem', color: locMsg[co._id] === 'Saved' ? '#28a745' : '#dc3545', alignSelf: 'flex-end' }}>
                                    {locMsg[co._id]}
                                  </span>
                                )}
                                {co.location?.coordinates && (
                                  <span style={{ fontSize: '0.7rem', color: '#888', alignSelf: 'flex-end' }}>
                                    📍 {co.location.coordinates[1].toFixed(4)}, {co.location.coordinates[0].toFixed(4)}
                                  </span>
                                )}
                              </div>
                              {(co.location?.zipcode || co.location?.city || co.location?.state) && (
                                <div style={{ marginTop: '0.45rem', fontSize: '0.72rem', color: '#666' }}>
                                  <span style={{ fontWeight: 600, color: '#888' }}>Current: </span>
                                  {[
                                    co.location.zipcode && `ZIP ${co.location.zipcode}`,
                                    co.location.city,
                                    co.location.state,
                                    co.location.serviceRadius && `radius ${co.location.serviceRadius} mi`,
                                    `local-only: ${co.location.isLocalOnly ? 'true' : 'false'}`,
                                  ].filter(Boolean).join(' · ')}
                                </div>
                              )}

                              {/* Branding */}
                              <div style={{ marginTop: '0.75rem', paddingTop: '0.65rem', borderTop: '1px solid #e8e0c8', display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'flex-start', fontSize: '0.8rem' }}>
                                <span style={{ fontWeight: 600, color: '#555', alignSelf: 'center' }}>Branding:</span>

                                {/* Logo preview + upload */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                                  {co.hero_image && (
                                    <img src={co.hero_image} alt="logo" style={{ height: '40px', objectFit: 'contain', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: co.navbar_color || '#13273F', padding: '4px' }} />
                                  )}
                                  <label style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem', backgroundColor: '#f0f4ff', color: '#3366cc', border: '1px solid #c0cff8', borderRadius: '4px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                    {brandingLoading[co._id] ? 'Uploading…' : co.hero_image ? 'Replace Logo' : 'Upload Logo'}
                                    <input
                                      type="file"
                                      accept="image/*"
                                      style={{ display: 'none' }}
                                      onChange={e => handleLogoUpload(co._id, e.target.files[0])}
                                    />
                                  </label>
                                </div>

                                {/* Navbar color */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                                  <label style={{ fontSize: '0.72rem', color: '#888' }}>Navbar Color</label>
                                  <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
                                    <input
                                      type="text"
                                      placeholder={co.navbar_color || '#13273F'}
                                      value={brandingColor[co._id] ?? (co.navbar_color || '')}
                                      onChange={e => setBrandingColor(prev => ({ ...prev, [co._id]: e.target.value }))}
                                      style={{ width: '100px', fontSize: '0.78rem', padding: '0.2rem 0.35rem', border: '1px solid #ccc', borderRadius: '4px', fontFamily: 'monospace' }}
                                    />
                                    {(brandingColor[co._id] ?? co.navbar_color) && (
                                      <div style={{ width: '22px', height: '22px', borderRadius: '4px', background: brandingColor[co._id] ?? co.navbar_color, border: '1px solid #ccc', flexShrink: 0 }} />
                                    )}
                                    <button onClick={() => handleColorSave(co._id)}
                                      style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem', backgroundColor: '#2a7a3b', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                      Save Color
                                    </button>
                                    {co.navbar_color && (
                                      <button onClick={() => { setBrandingColor(prev => ({ ...prev, [co._id]: '' })); handleColorSave(co._id); }}
                                        style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem', backgroundColor: '#fff', color: '#888', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                        Reset
                                      </button>
                                    )}
                                  </div>
                                </div>

                                {brandingMsg[co._id] && (
                                  <span style={{ fontSize: '0.72rem', color: brandingMsg[co._id].includes('failed') || brandingMsg[co._id].includes('Error') || brandingMsg[co._id].includes('valid') || brandingMsg[co._id].includes('MB') ? '#dc3545' : '#28a745', alignSelf: 'center' }}>
                                    {brandingMsg[co._id]}
                                  </span>
                                )}
                              </div>

                              {/* Account Controls */}
                              <div style={{ marginTop: '0.75rem', paddingTop: '0.65rem', borderTop: '1px solid #e8e0c8', display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center', fontSize: '0.8rem' }}>
                                <span style={{ fontWeight: 600, color: '#555', marginRight: '0.25rem' }}>Account:</span>
                                {!co.disabled ? (
                                  <button
                                    onClick={() => { setDisableTarget(co); setDisableMode('disable'); setDisableReason(co.disabledReason || ''); setDisableMessage(co.adminMessage || ''); setDisablePassword(''); setDisableError(''); }}
                                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.7rem', backgroundColor: '#cc0000', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                  >🚫 Disable</button>
                                ) : (
                                  <button
                                    onClick={() => { setDisableTarget(co); setDisableMode('enable'); setDisablePassword(''); setDisableError(''); }}
                                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.7rem', backgroundColor: '#28a745', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                  >✅ Re-enable</button>
                                )}
                                <button
                                  onClick={() => { setDisableTarget(co); setDisableMode('message'); setDisableMessage(co.adminMessage || ''); setDisableError(''); }}
                                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.7rem', backgroundColor: '#f0a500', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                >✉️ {co.adminMessage ? 'Edit Message' : 'Send Message'}</button>
                                <button
                                  onClick={() => { setSunsetAllTarget(co); setSunsetAllPassword(''); setSunsetAllMsg(''); }}
                                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.7rem', backgroundColor: '#7c3aed', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                  title="Immediately stop all new article purchases for this publisher"
                                >☀️ Sunset All</button>
                                {co.adminMessage && (
                                  <button
                                    onClick={async () => {
                                      await axios.post(`/api/admin/companies/${co._id}/message`, { message: '' }, { headers: { Authorization: `Bearer ${token}` } });
                                      setCompanies(prev => prev.map(c => c._id === co._id ? { ...c, adminMessage: '' } : c));
                                    }}
                                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.7rem', backgroundColor: '#fff', color: '#888', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer' }}
                                  >✕ Remove Message</button>
                                )}
                                {co.deletionFlagged && (
                                  <button
                                    onClick={() => { setClearDebtTarget(co); setClearDebtPassword(''); setClearDebtError(''); }}
                                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.7rem', backgroundColor: '#6c757d', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                  >💳 Clear Debt (${(co.deletionDebt || 0).toFixed(2)})</button>
                                )}
                                <button
                                  onClick={() => { setResetEmbedTarget(co); setResetEmbedPassword(''); setResetEmbedError(''); setResetEmbedResult(null); }}
                                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.7rem', backgroundColor: '#495057', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                >🔑 Reset Embed Keys</button>
                                <button
                                  onClick={() => { setArchiveTarget(co); setArchivePassword(''); setArchiveError(''); }}
                                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.7rem', backgroundColor: '#6c4a00', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                  title="Archive this company — moves them off the active list, preserving all data"
                                >🗄️ Archive</button>
                                {co.disabled && co.disabledReason && (
                                  <span style={{ fontSize: '0.72rem', color: '#888', fontStyle: 'italic' }}>Reason: {co.disabledReason}</span>
                                )}
                                {co.adminMessage && (
                                  <span style={{ fontSize: '0.72rem', color: '#666' }}>Msg: "{co.adminMessage}"</span>
                                )}
                              </div>

                              {/* Plugin License Key */}
                              <div style={{ marginTop: '0.65rem', paddingTop: '0.65rem', borderTop: '1px solid #e8e0c8', display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center', fontSize: '0.8rem' }}>
                                <span style={{ fontWeight: 600, color: '#555' }}>Plugin License:</span>
                                {co.pluginLicenseKey ? (
                                  <>
                                    <code style={{ fontSize: '0.78rem', background: '#f0f0f0', padding: '0.15rem 0.5rem', borderRadius: 4, letterSpacing: '0.03em', fontFamily: 'Consolas, monospace' }}>{co.pluginLicenseKey}</code>
                                    <button
                                      onClick={() => navigator.clipboard.writeText(co.pluginLicenseKey)}
                                      style={{ fontSize: '0.72rem', padding: '0.2rem 0.5rem', background: '#e9ecef', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}
                                    >Copy</button>
                                    <button
                                      onClick={() => handleRevokeLicense(co._id)}
                                      disabled={licenseState[co._id]?.loading}
                                      style={{ fontSize: '0.72rem', padding: '0.2rem 0.5rem', background: '#dc3545', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', opacity: licenseState[co._id]?.loading ? 0.6 : 1 }}
                                    >Revoke</button>
                                  </>
                                ) : (
                                  <span style={{ color: '#aaa', fontSize: '0.78rem' }}>No key issued</span>
                                )}
                                <button
                                  onClick={() => handleGenerateLicense(co._id)}
                                  disabled={licenseState[co._id]?.loading}
                                  style={{ fontSize: '0.72rem', padding: '0.2rem 0.6rem', background: '#0d3165', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', opacity: licenseState[co._id]?.loading ? 0.6 : 1 }}
                                >{licenseState[co._id]?.loading ? '…' : co.pluginLicenseKey ? 'Regenerate' : 'Generate Key'}</button>
                                {licenseState[co._id]?.msg && (
                                  <span style={{ fontSize: '0.72rem', color: licenseState[co._id].msg === 'Generated' || licenseState[co._id].msg === 'Revoked' ? '#28a745' : '#dc3545' }}>
                                    {licenseState[co._id].msg}
                                  </span>
                                )}
                              </div>

                              {/* Billing Email */}
                              <div style={{ marginTop: '0.65rem', paddingTop: '0.65rem', borderTop: '1px solid #e8e0c8', display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center', fontSize: '0.8rem' }}>
                                <span style={{ fontWeight: 600, color: '#555' }}>Billing Email:</span>
                                <input
                                  value={billingEmailEdits[co._id]?.val ?? co.billingEmail ?? ''}
                                  onChange={e => setBEField(co._id, 'val', e.target.value)}
                                  placeholder={co.email + ' (fallback)'}
                                  style={{ fontSize: '0.78rem', padding: '0.2rem 0.45rem', border: '1px solid #ccc', borderRadius: 4, width: '220px' }}
                                />
                                <button
                                  onClick={async () => {
                                    const val = billingEmailEdits[co._id]?.val ?? '';
                                    try {
                                      await axios.patch(`/api/admin/companies/${co._id}/billing-email`,
                                        { billingEmail: val },
                                        { headers: { Authorization: `Bearer ${token}` } }
                                      );
                                      setCompanies(prev => prev.map(c => c._id === co._id ? { ...c, billingEmail: val || null } : c));
                                      setBEField(co._id, 'msg', 'Saved');
                                      setTimeout(() => setBEField(co._id, 'msg', ''), 2500);
                                    } catch (err) {
                                      setBEField(co._id, 'msg', err.response?.data?.error || 'Error');
                                    }
                                  }}
                                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.7rem', backgroundColor: '#0d3165', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                                >Save</button>
                                {billingEmailEdits[co._id]?.msg && <span style={{ fontSize: '0.72rem', color: billingEmailEdits[co._id].msg === 'Saved' ? '#28a745' : '#dc3545' }}>{billingEmailEdits[co._id].msg}</span>}
                                <span style={{ fontSize: '0.7rem', color: '#aaa' }}>Invoice emails go here; defaults to account email if blank.</span>
                              </div>

                              {/* Platform Fee Rates */}
                              {(() => {
                                const fr = co.feeRates || {};
                                const activeTier1    = fr.baseTier     != null ? (fr.baseTier     * 100).toFixed(2) : '15.00 (default)';
                                const activeTier2    = fr.elevatedTier != null ? (fr.elevatedTier * 100).toFixed(2) : '12.50 (default)';
                                const activeTier3    = fr.tier3Rate    != null ? (fr.tier3Rate    * 100).toFixed(2) : '9.00 (default)';
                                const activeThresh   = fr.threshold    != null ? fr.threshold  : '2000 (default)';
                                const activeThresh2  = fr.threshold2   != null ? fr.threshold2 : '5000 (default)';
                                const hasPending     = !!fr.pendingEffectiveAt;
                                const editing        = feeRateEditing[co._id] || {};
                                const msgObj         = feeRateMsg[co._id];

                                // Detect if proposed changes include an increase
                                const curBase    = fr.baseTier     ?? 0.150;
                                const curThresh  = fr.threshold    ?? 2000;
                                const curElev    = fr.elevatedTier ?? 0.125;
                                const curThresh2 = fr.threshold2   ?? 5000;
                                const curTier3   = fr.tier3Rate    ?? 0.090;
                                const propBase    = editing.baseTier     !== undefined && editing.baseTier     !== '' ? parseFloat(editing.baseTier)     / 100 : null;
                                const propThresh  = editing.threshold    !== undefined && editing.threshold    !== '' ? parseFloat(editing.threshold)              : null;
                                const propElev    = editing.elevatedTier !== undefined && editing.elevatedTier !== '' ? parseFloat(editing.elevatedTier) / 100 : null;
                                const propThresh2 = editing.threshold2   !== undefined && editing.threshold2   !== '' ? parseFloat(editing.threshold2)             : null;
                                const propTier3   = editing.tier3Rate    !== undefined && editing.tier3Rate    !== '' ? parseFloat(editing.tier3Rate)    / 100 : null;
                                const willIncrease = (propBase   != null && propBase   > curBase)
                                                  || (propThresh  != null && propThresh  < curThresh)
                                                  || (propElev    != null && propElev    > curElev)
                                                  || (propThresh2 != null && propThresh2 < curThresh2)
                                                  || (propTier3   != null && propTier3   > curTier3);

                                return (
                                  <div style={{ marginTop: '0.75rem', paddingTop: '0.65rem', borderTop: '1px solid #e8e0c8', fontSize: '0.8rem' }}>
                                    <span style={{ fontWeight: 600, color: '#555' }}>Platform Fee Rates:</span>
                                    <div style={{ marginTop: '0.35rem', fontSize: '0.72rem', color: '#666', marginBottom: '0.5rem' }}>
                                      Active: <strong>{activeTier1}%</strong> up to ${activeThresh} · <strong>{activeTier2}%</strong> up to ${activeThresh2} · <strong>{activeTier3}%</strong> above
                                    </div>

                                    {hasPending && (
                                      <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '5px', padding: '0.5rem 0.75rem', marginBottom: '0.55rem', fontSize: '0.72rem', color: '#856404' }}>
                                        <strong>Pending increase</strong> — effective {new Date(fr.pendingEffectiveAt).toLocaleDateString()}
                                        {fr.pendingBaseTier     != null && <> · Tier 1 → {(fr.pendingBaseTier     * 100).toFixed(2)}%</>}
                                        {fr.pendingThreshold    != null && <> · Threshold 1 → ${fr.pendingThreshold}</>}
                                        {fr.pendingElevatedTier != null && <> · Tier 2 → {(fr.pendingElevatedTier * 100).toFixed(2)}%</>}
                                        {fr.pendingThreshold2   != null && <> · Threshold 2 → ${fr.pendingThreshold2}</>}
                                        {fr.pendingTier3Rate    != null && <> · Tier 3 → {(fr.pendingTier3Rate    * 100).toFixed(2)}%</>}
                                        <br />
                                        {fr.pendingNotifiedAt
                                          ? <span style={{ color: '#155724' }}>Company notified {new Date(fr.pendingNotifiedAt).toLocaleDateString()}</span>
                                          : <span style={{ color: '#cc0000', fontWeight: 600 }}>Company NOT yet notified — action required</span>
                                        }
                                        <div style={{ marginTop: '0.4rem', display: 'flex', gap: '0.5rem' }}>
                                          {!fr.pendingNotifiedAt && (
                                            <button onClick={() => handleFeeRateNotify(co._id)}
                                              style={{ fontSize: '0.72rem', padding: '0.2rem 0.55rem', backgroundColor: '#28a745', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                                              Mark Company Notified
                                            </button>
                                          )}
                                          <button onClick={() => handleFeeRateCancelPending(co._id)}
                                            style={{ fontSize: '0.72rem', padding: '0.2rem 0.55rem', backgroundColor: '#fff', color: '#888', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer' }}>
                                            Cancel Pending
                                          </button>
                                        </div>
                                      </div>
                                    )}

                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', alignItems: 'flex-end' }}>
                                      <label style={{ display: 'flex', flexDirection: 'column', gap: '2px', fontSize: '0.72rem', color: '#555' }}>
                                        Tier 1 Rate (%)
                                        <input type="number" step="0.01" min="0" max="50"
                                          placeholder={fr.baseTier != null ? (fr.baseTier * 100).toFixed(2) : '15.00'}
                                          value={editing.baseTier ?? ''}
                                          onChange={e => setFeeRateEditing(prev => ({ ...prev, [co._id]: { ...editing, baseTier: e.target.value } }))}
                                          style={{ width: '72px', fontSize: '0.78rem', padding: '0.2rem 0.35rem', border: '1px solid #ccc', borderRadius: '4px' }}
                                        />
                                      </label>
                                      <label style={{ display: 'flex', flexDirection: 'column', gap: '2px', fontSize: '0.72rem', color: '#555' }}>
                                        Threshold 1 ($)
                                        <input type="number" step="1" min="0" max="100000"
                                          placeholder={fr.threshold != null ? fr.threshold : '2000'}
                                          value={editing.threshold ?? ''}
                                          onChange={e => setFeeRateEditing(prev => ({ ...prev, [co._id]: { ...editing, threshold: e.target.value } }))}
                                          style={{ width: '72px', fontSize: '0.78rem', padding: '0.2rem 0.35rem', border: '1px solid #ccc', borderRadius: '4px' }}
                                        />
                                      </label>
                                      <label style={{ display: 'flex', flexDirection: 'column', gap: '2px', fontSize: '0.72rem', color: '#555' }}>
                                        Tier 2 Rate (%)
                                        <input type="number" step="0.01" min="0" max="50"
                                          placeholder={fr.elevatedTier != null ? (fr.elevatedTier * 100).toFixed(2) : '12.50'}
                                          value={editing.elevatedTier ?? ''}
                                          onChange={e => setFeeRateEditing(prev => ({ ...prev, [co._id]: { ...editing, elevatedTier: e.target.value } }))}
                                          style={{ width: '72px', fontSize: '0.78rem', padding: '0.2rem 0.35rem', border: '1px solid #ccc', borderRadius: '4px' }}
                                        />
                                      </label>
                                      <label style={{ display: 'flex', flexDirection: 'column', gap: '2px', fontSize: '0.72rem', color: '#555' }}>
                                        Threshold 2 ($)
                                        <input type="number" step="1" min="0" max="100000"
                                          placeholder={fr.threshold2 != null ? fr.threshold2 : '5000'}
                                          value={editing.threshold2 ?? ''}
                                          onChange={e => setFeeRateEditing(prev => ({ ...prev, [co._id]: { ...editing, threshold2: e.target.value } }))}
                                          style={{ width: '72px', fontSize: '0.78rem', padding: '0.2rem 0.35rem', border: '1px solid #ccc', borderRadius: '4px' }}
                                        />
                                      </label>
                                      <label style={{ display: 'flex', flexDirection: 'column', gap: '2px', fontSize: '0.72rem', color: '#555' }}>
                                        Tier 3 Rate (%)
                                        <input type="number" step="0.01" min="0" max="50"
                                          placeholder={fr.tier3Rate != null ? (fr.tier3Rate * 100).toFixed(2) : '9.00'}
                                          value={editing.tier3Rate ?? ''}
                                          onChange={e => setFeeRateEditing(prev => ({ ...prev, [co._id]: { ...editing, tier3Rate: e.target.value } }))}
                                          style={{ width: '72px', fontSize: '0.78rem', padding: '0.2rem 0.35rem', border: '1px solid #ccc', borderRadius: '4px' }}
                                        />
                                      </label>
                                      <label style={{ display: 'flex', flexDirection: 'column', gap: '2px', fontSize: '0.72rem', color: '#555' }}>
                                        Admin Password
                                        <input type="password"
                                          value={feeRatePassword[co._id] ?? ''}
                                          onChange={e => setFeeRatePassword(prev => ({ ...prev, [co._id]: e.target.value }))}
                                          style={{ width: '110px', fontSize: '0.78rem', padding: '0.2rem 0.35rem', border: '1px solid #ccc', borderRadius: '4px' }}
                                        />
                                      </label>
                                      <button
                                        onClick={() => handleFeeRateSave(co._id, co)}
                                        style={{ fontSize: '0.75rem', padding: '0.25rem 0.7rem', backgroundColor: willIncrease ? '#c0710a' : '#2a7a3b', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', alignSelf: 'flex-end' }}
                                      >
                                        {willIncrease ? 'Schedule (60-day notice)' : 'Save Rates'}
                                      </button>
                                    </div>
                                    {willIncrease && (
                                      <div style={{ marginTop: '0.35rem', fontSize: '0.7rem', color: '#c0710a', fontWeight: 600 }}>
                                        One or more rates is an increase — will be scheduled 60 days out. You must notify the company.
                                      </div>
                                    )}
                                    {msgObj && (
                                      <div style={{ marginTop: '0.35rem', fontSize: '0.72rem', color: msgObj.type === 'ok' ? '#28a745' : msgObj.type === 'warn' ? '#856404' : '#dc3545', fontWeight: 600 }}>
                                        {msgObj.text}
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                            </td>
                          </tr>
                        );
                      })()}
                      {expandedCompany === co._id && co.articles.map((art) => {
                        // Build logMap: purchases per price point (all-time)
                        const logMap = {};
                        (art.purchaseLog || []).forEach(e => {
                          logMap[e.token_cost] = (logMap[e.token_cost] || 0) + e.count;
                        });
                        return (
                        <React.Fragment key={art._id}>
                          {/* Article summary row */}
                          <tr style={{ backgroundColor: '#f0f4ff', borderBottom: 'none' }}>
                            <td style={{ ...td, paddingLeft: '2.5rem', color: '#333', fontSize: '0.82rem', fontWeight: '600' }}>
                              ↳ {art.title}
                              <button
                                onClick={() => setGraphArticle({ _id: art._id, title: art.title })}
                                style={{
                                  marginLeft: '0.6rem', padding: '0.1rem 0.45rem', fontSize: '0.72rem',
                                  backgroundColor: '#f0f4ff', color: '#3366cc', border: '1px solid #c0cff8',
                                  borderRadius: '4px', cursor: 'pointer',
                                }}
                              >
                                📈
                              </button>
                              <button
                                onClick={() => openPurchaseLog(art)}
                                style={{
                                  marginLeft: '0.35rem', padding: '0.1rem 0.45rem', fontSize: '0.72rem',
                                  backgroundColor: '#f0f4ff', color: '#555', border: '1px solid #c8c8d8',
                                  borderRadius: '4px', cursor: 'pointer',
                                }}
                                title="View full purchase audit log"
                              >
                                🧾
                              </button>
                              <button
                                onClick={() => { setForceDeleteTarget({ _id: art._id, title: art.title }); setForceDeletePassword(''); setForceDeleteError(''); }}
                                style={{ marginLeft: '0.35rem', padding: '0.1rem 0.45rem', fontSize: '0.72rem', backgroundColor: '#fff0f0', color: '#cc0000', border: '1px solid #f5c6cb', borderRadius: '4px', cursor: 'pointer' }}
                                title="Force-delete this article"
                              >🗑️</button>
                              {liveStats?.byArticle?.[art._id] > 0 && (
                                <span style={{ marginLeft: '0.5rem', fontSize: '0.72rem', color: '#28a745' }}>
                                  🟢 {liveStats.byArticle[art._id]} reading
                                </span>
                              )}
                            </td>
                            <td style={{ ...td, fontSize: '0.82rem', color: '#888' }}>{art.token_cost} cr</td>
                            <td style={{ ...td, fontSize: '0.82rem' }}>{art.purchases}</td>
                            <td style={{ ...td, fontSize: '0.82rem', fontWeight: '600' }}>{fmt(art.grossRevenue)}</td>
                            <td style={td} />
                          </tr>
                          {/* Price-point breakdown sub-table */}
                          <tr style={{ backgroundColor: '#f7f9ff' }}>
                            <td colSpan={6} style={{ padding: '0 2.5rem 0.75rem 4rem' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.76rem', border: '1px solid #e0e4f0', borderRadius: 6, overflow: 'hidden' }}>
                                <thead>
                                  <tr style={{ backgroundColor: '#e8eeff' }}>
                                    <th style={{ ...th, fontSize: '0.72rem', padding: '0.4rem 0.75rem' }}>Price (cr)</th>
                                    <th style={{ ...th, fontSize: '0.72rem', padding: '0.4rem 0.75rem' }}>Active From</th>
                                    <th style={{ ...th, fontSize: '0.72rem', padding: '0.4rem 0.75rem' }}>To</th>
                                    <th style={{ ...th, fontSize: '0.72rem', padding: '0.4rem 0.75rem', textAlign: 'center' }}>Purchases</th>
                                    <th style={{ ...th, fontSize: '0.72rem', padding: '0.4rem 0.75rem', textAlign: 'right' }}>Revenue</th>
                                    <th style={{ ...th, fontSize: '0.72rem', padding: '0.4rem 0.75rem' }}>By</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(art.priceHistory || [{ token_cost: art.token_cost, startedAt: null, endedAt: null, changedBy: 'manual' }]).map((ph, pi) => {
                                    const count = logMap[ph.token_cost] || 0;
                                    const rev   = count * ph.token_cost * 0.01;
                                    return (
                                      <tr key={pi} style={{ borderTop: '1px solid #e8e8f0', backgroundColor: pi % 2 === 0 ? '#fff' : '#fafbff' }}>
                                        <td style={{ ...td, fontSize: '0.76rem', padding: '0.35rem 0.75rem', fontWeight: 600 }}>{ph.token_cost}</td>
                                        <td style={{ ...td, fontSize: '0.76rem', padding: '0.35rem 0.75rem', color: '#666' }}>
                                          {ph.startedAt ? new Date(ph.startedAt).toLocaleDateString() : '—'}
                                        </td>
                                        <td style={{ ...td, fontSize: '0.76rem', padding: '0.35rem 0.75rem', color: '#666' }}>
                                          {ph.endedAt ? new Date(ph.endedAt).toLocaleDateString() : <span style={{ color: '#28a745' }}>Current</span>}
                                        </td>
                                        <td style={{ ...td, fontSize: '0.76rem', padding: '0.35rem 0.75rem', textAlign: 'center' }}>{count > 0 ? count : <span style={{ color: '#bbb' }}>—</span>}</td>
                                        <td style={{ ...td, fontSize: '0.76rem', padding: '0.35rem 0.75rem', textAlign: 'right', color: count > 0 ? '#1a7a3a' : '#bbb' }}>{count > 0 ? fmt(rev) : '—'}</td>
                                        <td style={{ ...td, fontSize: '0.76rem', padding: '0.35rem 0.75rem', color: '#999', fontStyle: 'italic' }}>{ph.changedBy || 'manual'}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                                <tfoot>
                                  <tr style={{ borderTop: '1px solid #c8d0e8', backgroundColor: '#eef0fb' }}>
                                    <td colSpan={3} style={{ ...td, fontSize: '0.76rem', padding: '0.35rem 0.75rem', fontWeight: 700, color: '#555' }}>Totals</td>
                                    <td style={{ ...td, fontSize: '0.76rem', padding: '0.35rem 0.75rem', textAlign: 'center', fontWeight: 700 }}>{art.purchases}</td>
                                    <td style={{ ...td, fontSize: '0.76rem', padding: '0.35rem 0.75rem', textAlign: 'right', fontWeight: 700, color: '#1a7a3a' }}>{fmt(art.grossRevenue)}</td>
                                    <td style={td} />
                                  </tr>
                                </tfoot>
                              </table>
                            </td>
                          </tr>
                        </React.Fragment>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* ── Archived Companies ── */}
      {archivedCompanies.length > 0 && (
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 2.5rem 1.5rem' }}>
          <button
            onClick={() => setArchivedOpen(o => !o)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', fontSize: '0.82rem', fontWeight: '600', padding: '0.4rem 0', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
          >
            {archivedOpen ? '▲' : '▼'} 🗄️ Archived Companies ({archivedCompanies.length})
          </button>
          {archivedOpen && (
            <div style={{ backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px', overflow: 'hidden', marginTop: '0.5rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f5f0e8', borderBottom: '1px solid #e0e0e0' }}>
                    <th style={{ padding: '0.55rem 0.75rem', textAlign: 'left', fontWeight: 700, color: '#666' }}>Company</th>
                    <th style={{ padding: '0.55rem 0.75rem', textAlign: 'left', fontWeight: 700, color: '#666' }}>Archived</th>
                    <th style={{ padding: '0.55rem 0.75rem', textAlign: 'right', fontWeight: 700, color: '#666' }}>Articles</th>
                    <th style={{ padding: '0.55rem 0.75rem', fontWeight: 700, color: '#666' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {archivedCompanies.map((co, i) => (
                    <tr key={co._id} style={{ borderBottom: '1px solid #f0f0f0', backgroundColor: i % 2 === 0 ? '#fff' : '#fafaf7' }}>
                      <td style={{ padding: '0.55rem 0.75rem', fontWeight: 600, color: '#555' }}>
                        {co.company_name}
                        {co.disabled && <span style={{ marginLeft: '0.4rem', fontSize: '0.68rem', color: '#fff', backgroundColor: '#cc0000', padding: '1px 5px', borderRadius: '3px' }}>🚫 DISABLED</span>}
                        <div style={{ fontSize: '0.72rem', color: '#aaa', marginTop: '0.2rem' }}>{co.email}</div>
                      </td>
                      <td style={{ padding: '0.55rem 0.75rem', fontSize: '0.8rem', color: '#888' }}>
                        {co.archivedAt ? new Date(co.archivedAt).toLocaleDateString() : '—'}
                      </td>
                      <td style={{ padding: '0.55rem 0.75rem', textAlign: 'right', color: '#555' }}>{co.articleCount}</td>
                      <td style={{ padding: '0.55rem 0.75rem' }}>
                        <button
                          onClick={() => { setUnarchiveTarget(co); setUnarchivePassword(''); setUnarchiveError(''); }}
                          style={{ fontSize: '0.75rem', padding: '0.25rem 0.7rem', backgroundColor: '#2a7a3b', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                        >↩ Restore</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Promo Codes ── */}
      <PromoCodesSection token={token} promoStats={stats} />

      {/* ── Returns ── */}
      <div id="section-returns" style={{ maxWidth: 1100, margin: '0 auto', padding: '0 2.5rem 2rem', scrollMarginTop: '160px' }}>
        <hr style={{ border: 'none', borderTop: '2px solid #f0d0d0', marginBottom: '1.25rem' }} />
        <h5 style={{ color: '#555', marginBottom: '1rem', textTransform: 'uppercase', fontSize: '0.78rem', letterSpacing: '0.07em' }}>↩ Article Returns</h5>

        {/* Stats row */}
        {returnsStats ? (
          <div style={{ display: 'flex', gap: '2.5rem', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '1.5rem' }}>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '0.2rem' }}>All-time returns</div>
              <div style={{ fontSize: '2.25rem', fontWeight: '900', color: '#c0392b', lineHeight: 1 }}>
                {returnsStats.totalReturns.toLocaleString()}
              </div>
            </div>
            <div style={{ borderLeft: '1px solid #f0c0c0', paddingLeft: '2.5rem' }}>
              <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '0.2rem' }}>This month</div>
              <div style={{ fontSize: '2.25rem', fontWeight: '900', color: '#e74c3c', lineHeight: 1 }}>
                {returnsStats.thisMonth}
              </div>
            </div>
            <div style={{ borderLeft: '1px solid #f0c0c0', paddingLeft: '2.5rem' }}>
              <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '0.2rem' }}>Last month</div>
              <div style={{ fontSize: '2.25rem', fontWeight: '900', color: '#c0392b', lineHeight: 1 }}>
                {returnsStats.lastMonth}
              </div>
            </div>
            <div style={{ borderLeft: '1px solid #f0c0c0', paddingLeft: '2.5rem' }}>
              <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '0.2rem' }}>Avg per month</div>
              <div style={{ fontSize: '2.25rem', fontWeight: '900', color: '#999', lineHeight: 1 }}>
                {returnsStats.avgPerMonth}
              </div>
            </div>
            <div style={{ borderLeft: '1px solid #f0c0c0', paddingLeft: '2.5rem' }}>
              <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '0.2rem' }}>Status</div>
              <div style={{ fontSize: '1.1rem', fontWeight: '800', color: returnsStats.returnsEnabled ? '#27ae60' : '#c0392b', lineHeight: 1 }}>
                {returnsStats.returnsEnabled ? '✓ ENABLED' : '✗ DISABLED'}
              </div>
            </div>
          </div>
        ) : (
          <div style={{ fontSize: '0.82rem', color: '#aaa', marginBottom: '1.5rem' }}>Loading…</div>
        )}

        {/* Kill-switch toggle */}
        <div style={{ background: '#fff8f8', border: '1px solid #f0c0c0', borderRadius: '8px', padding: '1rem 1.25rem', maxWidth: '480px' }}>
          <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#555', marginBottom: '0.5rem' }}>
            {returnsStats?.returnsEnabled !== false ? 'Disable returns site-wide' : 'Re-enable returns site-wide'}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '0.75rem' }}>
            {returnsStats?.returnsEnabled !== false
              ? 'Hides the Return button from all article cards and blocks the API endpoint. Readers with in-progress returns are unaffected.'
              : 'Returns are currently disabled. Re-enabling restores the Return button for all readers immediately.'}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <input
              type="password"
              placeholder="Admin password"
              value={returnsTogglePw}
              onChange={e => { setReturnsTogglePw(e.target.value); setReturnsToggleMsg(''); }}
              style={{ flex: 1, padding: '0.35rem 0.6rem', border: '1px solid #ccc', borderRadius: '5px', fontSize: '0.82rem' }}
            />
            <button
              onClick={handleReturnsToggle}
              disabled={returnsToggling || !returnsTogglePw}
              style={{ padding: '0.35rem 0.9rem', background: returnsStats?.returnsEnabled !== false ? '#c0392b' : '#27ae60', color: '#fff', border: 'none', borderRadius: '5px', fontSize: '0.8rem', fontWeight: 700, cursor: returnsToggling || !returnsTogglePw ? 'not-allowed' : 'pointer', opacity: returnsToggling || !returnsTogglePw ? 0.6 : 1 }}
            >
              {returnsToggling ? 'Saving…' : returnsStats?.returnsEnabled !== false ? 'Disable Returns' : 'Enable Returns'}
            </button>
          </div>
          {returnsToggleMsg && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.78rem', color: returnsToggleMsg.includes('ENABLED') ? '#27ae60' : returnsToggleMsg.includes('DISABLED') ? '#c0392b' : '#c0710a', fontWeight: 600 }}>
              {returnsToggleMsg}
            </div>
          )}
          <div style={{ marginTop: '0.6rem', fontSize: '0.7rem', color: '#bbb' }}>
            Limit: 2 returns per reader per calendar month. Same-day restriction removed.
          </div>
        </div>
      </div>

      {/* ── Broken-link flagged articles ── */}
      <div id="section-broken" style={{ scrollMarginTop: '160px' }} />
      {needsReview.length > 0 && (
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 1.5rem 2rem' }}>
          <h5 style={{ color: '#cc0000', marginBottom: '0.75rem', textTransform: 'uppercase', fontSize: '0.78rem', letterSpacing: '0.07em' }}>🔗 External Articles Flagged for Broken Link ({needsReview.length})</h5>
          <div style={{ backgroundColor: '#fff', border: '1px solid #f5c6cb', borderRadius: '10px', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ backgroundColor: '#fff5f5', borderBottom: '1px solid #f5c6cb' }}>
                  <th style={th}>Article</th>
                  <th style={th}>Company</th>
                  <th style={th}>Reports</th>
                  <th style={th}>URL</th>
                  <th style={th}>Action</th>
                </tr>
              </thead>
              <tbody>
                {needsReview.map(art => (
                  <tr key={art._id} style={{ borderBottom: '1px solid #fce8e8' }}>
                    <td style={{ ...td, fontWeight: '600', color: '#333' }}>{art.title}</td>
                    <td style={{ ...td, color: '#666' }}>{art.company_name}</td>
                    <td style={{ ...td, color: '#cc0000', fontWeight: '700' }}>{art.brokenLinkReports}</td>
                    <td style={{ ...td }}>
                      <a href={art.external_url} target="_blank" rel="noopener noreferrer" style={{ color: '#3366cc', fontSize: '0.78rem', wordBreak: 'break-all' }}>
                        {art.external_url?.slice(0, 60)}{art.external_url?.length > 60 ? '…' : ''}
                      </a>
                    </td>
                    <td style={{ ...td }}>
                      <button
                        onClick={() => { setForceDeleteTarget({ _id: art._id, title: art.title }); setForceDeletePassword(''); setForceDeleteError(''); }}
                        style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem', backgroundColor: '#fff0f0', color: '#cc0000', border: '1px solid #f5c6cb', borderRadius: '4px', cursor: 'pointer' }}
                      >🗑️ Force Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {graphArticle && (
        <PurchaseGraph
          articleId={graphArticle._id}
          articleTitle={graphArticle.title}
          onClose={() => setGraphArticle(null)}
        />
      )}

      {/* ── Disable / Enable / Message Company Modal ── */}
      {disableTarget && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 2100, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '2rem', maxWidth: '460px', width: '90%', boxShadow: '0 8px 40px rgba(0,0,0,0.25)', fontFamily: 'Arial, sans-serif' }}>
            <div style={{ fontWeight: '700', fontSize: '1.05rem', color: disableMode === 'disable' ? '#cc0000' : disableMode === 'enable' ? '#28a745' : '#f0a500', marginBottom: '0.6rem' }}>
              {disableMode === 'disable' ? '🚫 Disable Company' : disableMode === 'enable' ? '✅ Re-enable Company' : '✉️ Update Admin Message'}
            </div>
            <div style={{ fontWeight: '600', color: '#333', marginBottom: '1rem', fontSize: '0.9rem' }}>{disableTarget.company_name}</div>

            {disableMode === 'disable' && (
              <div style={{ marginBottom: '0.85rem' }}>
                <label style={{ display: 'block', fontWeight: '600', fontSize: '0.82rem', marginBottom: '0.3rem', color: '#333' }}>Reason (admin-only note):</label>
                <textarea
                  value={disableReason}
                  onChange={e => setDisableReason(e.target.value)}
                  rows={2}
                  placeholder="Required"
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '6px', fontSize: '0.85rem', boxSizing: 'border-box', resize: 'vertical' }}
                />
              </div>
            )}
            {(disableMode === 'disable' || disableMode === 'message') && (
              <div style={{ marginBottom: '0.85rem' }}>
                <label style={{ display: 'block', fontWeight: '600', fontSize: '0.82rem', marginBottom: '0.3rem', color: '#333' }}>Message to company (shown on their dashboard):</label>
                <textarea
                  value={disableMessage}
                  onChange={e => setDisableMessage(e.target.value)}
                  rows={3}
                  placeholder="Optional"
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '6px', fontSize: '0.85rem', boxSizing: 'border-box', resize: 'vertical' }}
                />
              </div>
            )}
            {disableMode !== 'message' && (
              <div style={{ marginBottom: '0.85rem' }}>
                <label style={{ display: 'block', fontWeight: '600', fontSize: '0.82rem', marginBottom: '0.3rem', color: '#333' }}>Your admin password:</label>
                <input
                  type="password"
                  value={disablePassword}
                  onChange={e => setDisablePassword(e.target.value)}
                  placeholder="Required"
                  style={{ width: '100%', padding: '0.5rem 0.7rem', border: '1px solid #ccc', borderRadius: '6px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                />
              </div>
            )}
            {disableError && <div style={{ color: '#cc0000', fontSize: '0.82rem', marginBottom: '0.75rem' }}>{disableError}</div>}
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={handleDisableSubmit}
                disabled={disableLoading || (disableMode === 'disable' && !disableReason) || (disableMode !== 'message' && !disablePassword)}
                style={{ flex: 1, padding: '0.6rem', backgroundColor: disableMode === 'enable' ? '#28a745' : disableMode === 'message' ? '#f0a500' : '#cc0000', color: '#fff', border: 'none', borderRadius: '7px', fontWeight: '700', fontSize: '0.88rem', cursor: 'pointer', opacity: (disableLoading || (disableMode === 'disable' && !disableReason) || (disableMode !== 'message' && !disablePassword)) ? 0.6 : 1 }}
              >
                {disableLoading ? 'Saving…' : 'Confirm'}
              </button>
              <button onClick={() => setDisableTarget(null)} style={{ flex: 1, padding: '0.6rem', backgroundColor: '#f4f4f4', color: '#333', border: '1px solid #ddd', borderRadius: '7px', cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Sunset All Articles Modal ── */}
      {sunsetAllTarget && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 2100, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '2rem', maxWidth: '460px', width: '90%', boxShadow: '0 8px 40px rgba(0,0,0,0.25)', fontFamily: 'Arial, sans-serif' }}>
            <div style={{ fontWeight: '700', fontSize: '1.05rem', color: '#7c3aed', marginBottom: '0.6rem' }}>☀️ Sunset All Articles</div>
            <div style={{ fontWeight: '600', color: '#333', marginBottom: '0.5rem', fontSize: '0.9rem' }}>{sunsetAllTarget.company_name}</div>
            <div style={{ fontSize: '0.82rem', color: '#555', marginBottom: '1rem', lineHeight: '1.5', background: '#fdf4ff', border: '1px solid #e9d5ff', borderRadius: '6px', padding: '0.75rem' }}>
              This will immediately sunset <strong>every active article</strong> for this publisher — no new purchases will be accepted on any article from this moment forward. Existing readers retain access through their 30-day window. This action cannot be undone in bulk.
            </div>
            <label style={{ display: 'block', fontWeight: '600', fontSize: '0.82rem', marginBottom: '0.3rem', color: '#333' }}>Admin password to confirm:</label>
            <input
              type="password"
              value={sunsetAllPassword}
              onChange={e => { setSunsetAllPassword(e.target.value); setSunsetAllMsg(''); }}
              placeholder="Admin password"
              style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #ccc', borderRadius: '6px', fontSize: '0.88rem', marginBottom: '0.75rem', boxSizing: 'border-box' }}
            />
            {sunsetAllMsg && (
              <div style={{ fontSize: '0.82rem', marginBottom: '0.75rem', color: sunsetAllMsg.startsWith('✓') ? '#166534' : '#cc0000', background: sunsetAllMsg.startsWith('✓') ? '#f0fdf4' : '#fff5f5', border: `1px solid ${sunsetAllMsg.startsWith('✓') ? '#86efac' : '#fca5a5'}`, borderRadius: '5px', padding: '0.5rem 0.75rem' }}>
                {sunsetAllMsg}
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={handleSunsetAll}
                disabled={sunsetAllLoading || !sunsetAllPassword || sunsetAllMsg.startsWith('✓')}
                style={{ flex: 1, padding: '0.6rem', backgroundColor: '#7c3aed', color: '#fff', border: 'none', borderRadius: '7px', fontWeight: '700', fontSize: '0.88rem', cursor: 'pointer', opacity: (sunsetAllLoading || !sunsetAllPassword || sunsetAllMsg.startsWith('✓')) ? 0.6 : 1 }}
              >
                {sunsetAllLoading ? 'Sunsetting…' : '☀️ Sunset All Articles'}
              </button>
              <button onClick={() => setSunsetAllTarget(null)} style={{ flex: 1, padding: '0.6rem', backgroundColor: '#f4f4f4', color: '#333', border: '1px solid #ddd', borderRadius: '7px', cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Clear Deletion Debt Modal ── */}
      {clearDebtTarget && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 2100, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '2rem', maxWidth: '420px', width: '90%', boxShadow: '0 8px 40px rgba(0,0,0,0.25)', fontFamily: 'Arial, sans-serif' }}>
            <div style={{ fontWeight: '700', fontSize: '1.05rem', color: '#6c757d', marginBottom: '0.6rem' }}>💳 Clear Deletion Debt</div>
            <div style={{ fontWeight: '600', color: '#333', marginBottom: '0.4rem', fontSize: '0.9rem' }}>{clearDebtTarget.company_name}</div>
            <div style={{ fontSize: '0.85rem', color: '#555', marginBottom: '1rem' }}>
              Clearing the deletion debt of <strong>${(clearDebtTarget.deletionDebt || 0).toFixed(2)}</strong> will resume payouts for this company. Only do this after the company has paid PubSilo the owed amount.
            </div>
            <div style={{ marginBottom: '0.85rem' }}>
              <label style={{ display: 'block', fontWeight: '600', fontSize: '0.82rem', marginBottom: '0.3rem', color: '#333' }}>Your admin password:</label>
              <input
                type="password"
                value={clearDebtPassword}
                onChange={e => setClearDebtPassword(e.target.value)}
                placeholder="Required"
                style={{ width: '100%', padding: '0.5rem 0.7rem', border: '1px solid #ccc', borderRadius: '6px', fontSize: '0.85rem', boxSizing: 'border-box' }}
              />
            </div>
            {clearDebtError && <div style={{ color: '#cc0000', fontSize: '0.82rem', marginBottom: '0.75rem' }}>{clearDebtError}</div>}
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={handleClearDebt}
                disabled={clearDebtLoading || !clearDebtPassword}
                style={{ flex: 1, padding: '0.6rem', backgroundColor: '#6c757d', color: '#fff', border: 'none', borderRadius: '7px', fontWeight: '700', fontSize: '0.88rem', cursor: 'pointer', opacity: clearDebtLoading || !clearDebtPassword ? 0.6 : 1 }}
              >
                {clearDebtLoading ? 'Clearing…' : 'Clear Debt'}
              </button>
              <button onClick={() => setClearDebtTarget(null)} style={{ flex: 1, padding: '0.6rem', backgroundColor: '#f4f4f4', color: '#333', border: '1px solid #ddd', borderRadius: '7px', cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Force Delete Article Modal ── */}
      {forceDeleteTarget && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 2100, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '2rem', maxWidth: '420px', width: '90%', boxShadow: '0 8px 40px rgba(0,0,0,0.25)', fontFamily: 'Arial, sans-serif' }}>
            <div style={{ fontWeight: '700', fontSize: '1.05rem', color: '#cc0000', marginBottom: '0.6rem' }}>🗑️ Force Delete Article</div>
            <div style={{ fontWeight: '600', color: '#222', marginBottom: '0.75rem', fontSize: '0.9rem' }}>"{forceDeleteTarget.title}"</div>
            <div style={{ background: '#fff5f0', border: '1px solid #f5c6cb', borderRadius: '8px', padding: '0.75rem', marginBottom: '1rem', fontSize: '0.82rem', color: '#721c24' }}>
              All active purchasers will receive Promotional Credit refunds. The company will be flagged with a deletion debt equal to the total refund value. This action cannot be undone.
            </div>
            <div style={{ marginBottom: '0.85rem' }}>
              <label style={{ display: 'block', fontWeight: '600', fontSize: '0.82rem', marginBottom: '0.3rem', color: '#333' }}>Your admin password:</label>
              <input
                type="password"
                value={forceDeletePassword}
                onChange={e => setForceDeletePassword(e.target.value)}
                placeholder="Required"
                style={{ width: '100%', padding: '0.5rem 0.7rem', border: '1px solid #ccc', borderRadius: '6px', fontSize: '0.85rem', boxSizing: 'border-box' }}
              />
            </div>
            {forceDeleteError && <div style={{ color: '#cc0000', fontSize: '0.82rem', marginBottom: '0.75rem' }}>{forceDeleteError}</div>}
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={handleForceDelete}
                disabled={forceDeleteLoading || !forceDeletePassword}
                style={{ flex: 1, padding: '0.6rem', backgroundColor: '#cc0000', color: '#fff', border: 'none', borderRadius: '7px', fontWeight: '700', fontSize: '0.88rem', cursor: 'pointer', opacity: forceDeleteLoading || !forceDeletePassword ? 0.6 : 1 }}
              >
                {forceDeleteLoading ? 'Deleting…' : 'Force Delete'}
              </button>
              <button onClick={() => setForceDeleteTarget(null)} style={{ flex: 1, padding: '0.6rem', backgroundColor: '#f4f4f4', color: '#333', border: '1px solid #ddd', borderRadius: '7px', cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Reset Embed Keys Modal ── */}
      {resetEmbedTarget && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 2100, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '2rem', maxWidth: '480px', width: '90%', boxShadow: '0 8px 40px rgba(0,0,0,0.25)', fontFamily: 'Arial, sans-serif' }}>
            <div style={{ fontWeight: '700', fontSize: '1.05rem', color: '#495057', marginBottom: '0.4rem' }}>🔑 Reset Embed Keys</div>
            <div style={{ fontWeight: '600', color: '#333', marginBottom: '0.75rem', fontSize: '0.9rem' }}>{resetEmbedTarget.company_name}</div>

            {!resetEmbedResult ? (
              <>
                <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px', padding: '0.75rem', marginBottom: '1rem', fontSize: '0.82rem', color: '#856404' }}>
                  <strong>Warning:</strong> This generates a new embedKey and embedSecret. All existing tokens issued with the old secret become invalid immediately. The company will need to update their server environment with the new secret.
                </div>
                <div style={{ marginBottom: '0.85rem' }}>
                  <label style={{ display: 'block', fontWeight: '600', fontSize: '0.82rem', marginBottom: '0.3rem', color: '#333' }}>Your admin password:</label>
                  <input
                    type="password"
                    value={resetEmbedPassword}
                    onChange={e => setResetEmbedPassword(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && resetEmbedPassword && !resetEmbedLoading && handleResetEmbedKeys()}
                    placeholder="Required"
                    style={{ width: '100%', padding: '0.5rem 0.7rem', border: '1px solid #ccc', borderRadius: '6px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                  />
                </div>
                {resetEmbedError && <div style={{ color: '#cc0000', fontSize: '0.82rem', marginBottom: '0.75rem' }}>{resetEmbedError}</div>}
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button
                    onClick={handleResetEmbedKeys}
                    disabled={resetEmbedLoading || !resetEmbedPassword}
                    style={{ flex: 1, padding: '0.6rem', backgroundColor: '#495057', color: '#fff', border: 'none', borderRadius: '7px', fontWeight: '700', fontSize: '0.88rem', cursor: 'pointer', opacity: resetEmbedLoading || !resetEmbedPassword ? 0.6 : 1 }}
                  >
                    {resetEmbedLoading ? 'Resetting…' : 'Reset Keys'}
                  </button>
                  <button onClick={() => setResetEmbedTarget(null)} style={{ flex: 1, padding: '0.6rem', backgroundColor: '#f4f4f4', color: '#333', border: '1px solid #ddd', borderRadius: '7px', cursor: 'pointer' }}>
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{ background: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '8px', padding: '0.75rem', marginBottom: '1rem', fontSize: '0.82rem', color: '#155724' }}>
                  <strong>Keys reset successfully.</strong> Share these values with the company. The secret is shown <strong>only once</strong> — it cannot be retrieved again.
                </div>
                <div style={{ marginBottom: '0.75rem' }}>
                  <div style={{ fontSize: '0.78rem', fontWeight: '700', color: '#555', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>Embed Key (public)</div>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <code style={{ flex: 1, background: '#f4f4f4', padding: '0.4rem 0.6rem', borderRadius: '5px', fontSize: '0.82rem', overflowX: 'auto', wordBreak: 'break-all' }}>{resetEmbedResult.embedKey}</code>
                    <button onClick={() => navigator.clipboard.writeText(resetEmbedResult.embedKey)} style={{ flexShrink: 0, padding: '0.3rem 0.65rem', fontSize: '0.75rem', backgroundColor: '#0d3165', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>Copy</button>
                  </div>
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ fontSize: '0.78rem', fontWeight: '700', color: '#cc0000', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>Embed Secret — SHOWN ONCE, COPY NOW</div>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <code style={{ flex: 1, background: '#fff5f0', padding: '0.4rem 0.6rem', borderRadius: '5px', fontSize: '0.78rem', overflowX: 'auto', wordBreak: 'break-all', border: '1px solid #f5c6cb' }}>{resetEmbedResult.embedSecret}</code>
                    <button onClick={() => navigator.clipboard.writeText(resetEmbedResult.embedSecret)} style={{ flexShrink: 0, padding: '0.3rem 0.65rem', fontSize: '0.75rem', backgroundColor: '#cc0000', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>Copy</button>
                  </div>
                </div>
                <button
                  onClick={() => { setResetEmbedTarget(null); setResetEmbedResult(null); }}
                  style={{ width: '100%', padding: '0.6rem', backgroundColor: '#f4f4f4', color: '#333', border: '1px solid #ddd', borderRadius: '7px', cursor: 'pointer', fontWeight: '600' }}
                >
                  Done
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Archive Company Modal ── */}
      {archiveTarget && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 2100, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '2rem', maxWidth: '460px', width: '90%', boxShadow: '0 8px 40px rgba(0,0,0,0.25)', fontFamily: 'Arial, sans-serif' }}>
            <div style={{ fontWeight: '700', fontSize: '1.05rem', color: '#6c4a00', marginBottom: '0.6rem' }}>🗄️ Archive Company</div>
            <div style={{ fontWeight: '600', color: '#333', marginBottom: '0.75rem', fontSize: '0.9rem' }}>{archiveTarget.company_name}</div>
            <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: '8px', padding: '0.75rem', marginBottom: '1rem', fontSize: '0.82rem', color: '#5d4037' }}>
              Archiving moves this company off the active list. All data and articles are preserved. You can restore them at any time from the Archived Companies section.
            </div>
            <div style={{ marginBottom: '0.85rem' }}>
              <label style={{ display: 'block', fontWeight: '600', fontSize: '0.82rem', marginBottom: '0.3rem', color: '#333' }}>Your admin password:</label>
              <input
                type="password"
                value={archivePassword}
                onChange={e => setArchivePassword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && archivePassword && !archiveLoading && handleArchiveSubmit()}
                placeholder="Required"
                style={{ width: '100%', padding: '0.5rem 0.7rem', border: '1px solid #ccc', borderRadius: '6px', fontSize: '0.85rem', boxSizing: 'border-box' }}
              />
            </div>
            {archiveError && <div style={{ color: '#cc0000', fontSize: '0.82rem', marginBottom: '0.75rem' }}>{archiveError}</div>}
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={handleArchiveSubmit}
                disabled={archiveLoading || !archivePassword}
                style={{ flex: 1, padding: '0.6rem', backgroundColor: '#6c4a00', color: '#fff', border: 'none', borderRadius: '7px', fontWeight: '700', fontSize: '0.88rem', cursor: 'pointer', opacity: archiveLoading || !archivePassword ? 0.6 : 1 }}
              >
                {archiveLoading ? 'Archiving…' : 'Archive Company'}
              </button>
              <button onClick={() => setArchiveTarget(null)} style={{ flex: 1, padding: '0.6rem', backgroundColor: '#f4f4f4', color: '#333', border: '1px solid #ddd', borderRadius: '7px', cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Unarchive Company Modal ── */}
      {unarchiveTarget && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 2100, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '2rem', maxWidth: '420px', width: '90%', boxShadow: '0 8px 40px rgba(0,0,0,0.25)', fontFamily: 'Arial, sans-serif' }}>
            <div style={{ fontWeight: '700', fontSize: '1.05rem', color: '#2a7a3b', marginBottom: '0.6rem' }}>↩ Restore Company</div>
            <div style={{ fontWeight: '600', color: '#333', marginBottom: '0.75rem', fontSize: '0.9rem' }}>{unarchiveTarget.company_name}</div>
            <div style={{ marginBottom: '0.85rem' }}>
              <label style={{ display: 'block', fontWeight: '600', fontSize: '0.82rem', marginBottom: '0.3rem', color: '#333' }}>Your admin password:</label>
              <input
                type="password"
                value={unarchivePassword}
                onChange={e => setUnarchivePassword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && unarchivePassword && !unarchiveLoading && handleUnarchiveSubmit()}
                placeholder="Required"
                style={{ width: '100%', padding: '0.5rem 0.7rem', border: '1px solid #ccc', borderRadius: '6px', fontSize: '0.85rem', boxSizing: 'border-box' }}
              />
            </div>
            {unarchiveError && <div style={{ color: '#cc0000', fontSize: '0.82rem', marginBottom: '0.75rem' }}>{unarchiveError}</div>}
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={handleUnarchiveSubmit}
                disabled={unarchiveLoading || !unarchivePassword}
                style={{ flex: 1, padding: '0.6rem', backgroundColor: '#2a7a3b', color: '#fff', border: 'none', borderRadius: '7px', fontWeight: '700', fontSize: '0.88rem', cursor: 'pointer', opacity: unarchiveLoading || !unarchivePassword ? 0.6 : 1 }}
              >
                {unarchiveLoading ? 'Restoring…' : 'Restore Company'}
              </button>
              <button onClick={() => setUnarchiveTarget(null)} style={{ flex: 1, padding: '0.6rem', backgroundColor: '#f4f4f4', color: '#333', border: '1px solid #ddd', borderRadius: '7px', cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Purchase Audit Log Modal ── */}
      {purchaseLog && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          zIndex: 1060, overflowY: 'auto', padding: '2rem 1rem',
        }} onClick={() => setPurchaseLog(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 950, boxShadow: '0 12px 48px rgba(0,0,0,0.22)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.1rem 1.5rem', borderBottom: '1px solid #e0e0e0' }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: '1rem', color: '#0d3165' }}>Purchase Audit Log</div>
                <div style={{ fontSize: '0.82rem', color: '#888', marginTop: '0.1rem' }}>{purchaseLog.title}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                {!purchaseLogLoading && purchaseLog.purchases.length > 0 && (
                  <span style={{ fontSize: '0.78rem', color: '#888' }}>
                    {purchaseLog.purchases.length} record{purchaseLog.purchases.length !== 1 ? 's' : ''}
                  </span>
                )}
                {!purchaseLogLoading && purchaseLog.purchases.length === 0 && purchaseLog.aggregate?.purchased > 0 && (
                  <span style={{ fontSize: '0.78rem', color: '#e67e22' }}>aggregate only</span>
                )}
                <button onClick={() => setPurchaseLog(null)} aria-label="Close" style={{ background: 'none', border: 'none', fontSize: '1.3rem', cursor: 'pointer', color: '#888', lineHeight: 1 }}>×</button>
              </div>
            </div>

            <div style={{ padding: '1rem 1.25rem', overflowX: 'auto' }}>
              {purchaseLogLoading && <p style={{ color: '#888', textAlign: 'center' }}>Loading…</p>}
              {purchaseLogError  && <p style={{ color: 'red',  textAlign: 'center' }}>{purchaseLogError}</p>}
              {!purchaseLogLoading && purchaseLog.purchases.length === 0 && !purchaseLogError && (() => {
                const agg = purchaseLog.aggregate;
                if (!agg || agg.purchased === 0) return (
                  <p style={{ color: '#aaa', textAlign: 'center', fontStyle: 'italic' }}>No purchase records found.</p>
                );
                // No individual user records but article has aggregate data (e.g. seeded articles)
                return (
                  <div>
                    <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.82rem', color: '#7a5c00' }}>
                      No individual purchase records exist for this article (data was seeded or pre-dates per-user tracking). Showing aggregate statistics only.
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                      <thead>
                        <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>
                          <th style={{ ...th, fontSize: '0.75rem' }}>Price (cr)</th>
                          <th style={{ ...th, fontSize: '0.75rem' }}>Active From</th>
                          <th style={{ ...th, fontSize: '0.75rem' }}>To</th>
                          <th style={{ ...th, fontSize: '0.75rem', textAlign: 'center' }}>Purchases</th>
                          <th style={{ ...th, fontSize: '0.75rem', textAlign: 'right' }}>Revenue</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(agg.priceHistory || []).map((ph, i) => {
                          const count = (agg.purchaseLog || []).find(e => e.token_cost === ph.token_cost)?.count || 0;
                          const rev   = count * ph.token_cost * 0.01;
                          return (
                            <tr key={i} style={{ borderBottom: '1px solid #f0f0f0', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                              <td style={{ ...td, fontWeight: 600 }}>{ph.token_cost}{ph.endedAt === null && <span style={{ marginLeft: '0.4rem', fontSize: '0.7rem', color: '#28a745' }}>current</span>}</td>
                              <td style={{ ...td, color: '#666', fontSize: '0.78rem' }}>{ph.startedAt ? new Date(ph.startedAt).toLocaleDateString() : '—'}</td>
                              <td style={{ ...td, color: '#666', fontSize: '0.78rem' }}>{ph.endedAt ? new Date(ph.endedAt).toLocaleDateString() : '—'}</td>
                              <td style={{ ...td, textAlign: 'center' }}>{count || '—'}</td>
                              <td style={{ ...td, textAlign: 'right', color: count > 0 ? '#1a7a3a' : '#bbb', fontWeight: count > 0 ? 600 : 'normal' }}>{count > 0 ? fmt(rev) : '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr style={{ borderTop: '2px solid #e0e0e0', background: '#f8f9fa', fontWeight: 700 }}>
                          <td colSpan={3} style={{ ...td, fontSize: '0.78rem' }}>Totals</td>
                          <td style={{ ...td, textAlign: 'center' }}>{agg.purchased}</td>
                          <td style={{ ...td, textAlign: 'right', color: '#1a7a3a' }}>
                            {fmt((agg.purchaseLog || []).reduce((s, e) => s + e.count * e.token_cost * 0.01, 0))}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                );
              })()}
              {!purchaseLogLoading && purchaseLog.purchases.length > 0 && (() => {
                const rows = sortedPurchases();
                const colBtn = (col, label) => (
                  <th
                    onClick={() => toggleSort(col)}
                    style={{ ...th, fontSize: '0.72rem', cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none', background: purchaseLogSort.col === col ? '#e8eeff' : '#f8f9fa' }}
                  >{label}{sortIcon(col)}</th>
                );
                return (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                        {colBtn('purchasedAt',    'Date')}
                        {colBtn('username',       'User')}
                        {colBtn('listPrice',      'List Price')}
                        {colBtn('tokensPaid',     'Paid (tkn)')}
                        {colBtn('promoTokensUsed','Promo Used')}
                        {colBtn('revenue',        'Gross Revenue')}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((p, i) => (
                        <tr key={String(p._id)} style={{
                          borderBottom: '1px solid #f0f0f0',
                          backgroundColor: i % 2 === 0 ? '#fff' : '#fafafa',
                        }}>
                          <td style={{ ...td, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                            {new Date(p.purchasedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                          </td>
                          <td style={{ ...td, fontSize: '0.78rem' }}>
                            <div style={{ fontWeight: 600 }}>{p.username}</div>
                            <div style={{ color: '#888', fontSize: '0.72rem' }}>{p.email}</div>
                          </td>
                          <td style={{ ...td, fontSize: '0.78rem', textAlign: 'center' }}>{p.listPrice}</td>
                          <td style={{ ...td, fontSize: '0.78rem', textAlign: 'center', fontWeight: 600 }}>{p.tokensPaid}</td>
                          <td style={{ ...td, fontSize: '0.78rem', textAlign: 'center', color: p.promoTokensUsed > 0 ? '#8e44ad' : '#aaa' }}>
                            {p.promoTokensUsed > 0 ? p.promoTokensUsed : '—'}
                          </td>
                          <td style={{ ...td, fontSize: '0.78rem', textAlign: 'right', color: p.revenue > 0 ? '#1a7a3a' : '#aaa', fontWeight: p.revenue > 0 ? 600 : 'normal' }}>
                            {p.revenue > 0 ? fmt(p.revenue) : '$0.00'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ borderTop: '2px solid #e0e0e0', backgroundColor: '#f8f9fa' }}>
                        <td colSpan={3} style={{ ...td, fontWeight: 700, fontSize: '0.8rem' }}>Totals</td>
                        <td style={{ ...td, fontSize: '0.8rem', textAlign: 'center', fontWeight: 700 }}>
                          {rows.reduce((s, p) => s + p.tokensPaid, 0)} cr
                        </td>
                        <td style={td} />
                        <td style={{ ...td, fontSize: '0.8rem', textAlign: 'right', fontWeight: 700, color: '#1a7a3a' }}>
                          {fmt(rows.reduce((s, p) => s + p.revenue, 0))}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* ── Token Package Purchase Log Modal ── */}
      {tokenPkgLog !== null && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          zIndex: 1070, overflowY: 'auto', padding: '2rem 1rem',
        }} onClick={() => setTokenPkgLog(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 1050, boxShadow: '0 12px 48px rgba(0,0,0,0.22)' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.1rem 1.5rem', borderBottom: '1px solid #e0e0e0' }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: '1rem', color: '#0d3165' }}>Token Package Purchases — All Time</div>
                {!tokenPkgLoading && tokenPkgLog.length > 0 && (
                  <div style={{ fontSize: '0.82rem', color: '#888', marginTop: '0.1rem' }}>
                    {tokenPkgLog.length} purchase{tokenPkgLog.length !== 1 ? 's' : ''} &nbsp;·&nbsp; {fmt(tokenPkgLog.reduce((s, r) => s + (r.amountUSD ?? 0), 0))} gross
                  </div>
                )}
              </div>
              <button onClick={() => setTokenPkgLog(null)} aria-label="Close" style={{ background: 'none', border: 'none', fontSize: '1.3rem', cursor: 'pointer', color: '#888', lineHeight: 1 }}>×</button>
            </div>

            <div style={{ padding: '1rem 1.25rem', overflowX: 'auto' }}>
              {tokenPkgLoading && <p style={{ color: '#888', textAlign: 'center' }}>Loading…</p>}
              {tokenPkgError  && <p style={{ color: 'red',  textAlign: 'center' }}>{tokenPkgError}</p>}
              {!tokenPkgLoading && tokenPkgLog.length === 0 && !tokenPkgError && (
                <p style={{ color: '#aaa', textAlign: 'center', fontStyle: 'italic' }}>No token purchases yet.</p>
              )}
              {!tokenPkgLoading && tokenPkgLog.length > 0 && (() => {
                const rows = sortedTokenPkgRows();
                const PACKAGE_LABEL = { starter: 'Starter', standard: 'Standard', premium: 'Premium' };
                const PACKAGE_COLOR = { starter: '#5a7fcb', standard: '#2a7a3b', premium: '#8e44ad' };

                const colBtn = (col, label) => (
                  <th
                    onClick={() => toggleTokenPkgSort(col)}
                    style={{ ...th, fontSize: '0.72rem', cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none', background: tokenPkgSort.col === col ? '#e8eeff' : '#f8f9fa' }}
                  >{label}{tokenPkgSortIcon(col)}</th>
                );

                // Totals
                const totalGross    = round2(rows.reduce((s, r) => s + (r.amountUSD   ?? 0), 0));
                const totalStripe   = round2(rows.reduce((s, r) => s + round2((r.amountUSD ?? 0) * 0.029 + 0.30), 0));
                const totalTokens   = rows.reduce((s, r) => s + (r.tokensAdded ?? 0), 0);
                const totalCoBank   = round2(totalTokens * 0.01);
                const totalNet      = round2(totalGross - totalStripe - totalCoBank);

                return (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                        {colBtn('purchasedAt', 'Date')}
                        {colBtn('username',    'Username')}
                        {colBtn('email',       'Email')}
                        {colBtn('package',     'Package')}
                        {colBtn('tokensAdded', 'Tokens')}
                        {colBtn('amountUSD',   'Paid')}
                        <th style={{ ...th, fontSize: '0.72rem', background: '#f8f9fa', whiteSpace: 'nowrap' }}>Stripe Fee</th>
                        <th style={{ ...th, fontSize: '0.72rem', background: '#f8f9fa', whiteSpace: 'nowrap' }}>Co. Bank</th>
                        <th style={{ ...th, fontSize: '0.72rem', background: '#f8f9fa', whiteSpace: 'nowrap' }}>Net</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => {
                        const gross    = r.amountUSD ?? 0;
                        const stripe   = round2(gross * 0.029 + 0.30);
                        const coBank   = round2((r.tokensAdded ?? 0) * 0.01);
                        const net      = round2(gross - stripe - coBank);
                        return (
                          <tr key={i} style={{ borderBottom: '1px solid #f0f0f0', backgroundColor: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                            <td style={{ ...td, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                              {r.purchasedAt
                                ? new Date(r.purchasedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
                                : '—'}
                            </td>
                            <td style={{ ...td, fontSize: '0.78rem', fontWeight: 600 }}>{r.username}</td>
                            <td style={{ ...td, fontSize: '0.75rem', color: '#888' }}>{r.email}</td>
                            <td style={{ ...td, fontSize: '0.78rem' }}>
                              <span style={{ padding: '0.15rem 0.55rem', borderRadius: 99, fontSize: '0.72rem', fontWeight: 700, color: '#fff', backgroundColor: PACKAGE_COLOR[r.package] || '#aaa' }}>
                                {PACKAGE_LABEL[r.package] || r.package}
                              </span>
                            </td>
                            <td style={{ ...td, fontSize: '0.78rem', textAlign: 'right', fontWeight: 600 }}>{(r.tokensAdded ?? 0).toLocaleString()}</td>
                            <td style={{ ...td, fontSize: '0.78rem', textAlign: 'right', fontWeight: 700, color: '#1a7a3a' }}>{fmt(gross)}</td>
                            <td style={{ ...td, fontSize: '0.78rem', textAlign: 'right', color: '#dc3545' }}>{fmt(stripe)}</td>
                            <td style={{ ...td, fontSize: '0.78rem', textAlign: 'right', color: '#777' }}>{fmt(coBank)}</td>
                            <td style={{ ...td, fontSize: '0.78rem', textAlign: 'right', fontWeight: 600, color: '#555' }}>{fmt(net)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{ borderTop: '2px solid #e0e0e0', backgroundColor: '#f8f9fa', fontWeight: 700 }}>
                        <td colSpan={4} style={{ ...td, fontSize: '0.8rem' }}>Totals ({rows.length} purchases)</td>
                        <td style={{ ...td, fontSize: '0.8rem', textAlign: 'right' }}>{totalTokens.toLocaleString()}</td>
                        <td style={{ ...td, fontSize: '0.8rem', textAlign: 'right', color: '#1a7a3a' }}>{fmt(totalGross)}</td>
                        <td style={{ ...td, fontSize: '0.8rem', textAlign: 'right', color: '#dc3545' }}>{fmt(totalStripe)}</td>
                        <td style={{ ...td, fontSize: '0.8rem', textAlign: 'right', color: '#777' }}>{fmt(totalCoBank)}</td>
                        <td style={{ ...td, fontSize: '0.8rem', textAlign: 'right', color: '#555' }}>{fmt(totalNet)}</td>
                      </tr>
                    </tfoot>
                  </table>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* ── User History Modal ── */}
      {userHistory && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          zIndex: 1090, overflowY: 'auto', padding: '2rem 1rem',
        }} onClick={() => setUserHistory(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 820, boxShadow: '0 12px 48px rgba(0,0,0,0.22)' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '1.25rem 1.5rem', borderBottom: '1px solid #e0e0e0', background: '#f8f9ff', borderRadius: '12px 12px 0 0' }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: '1.1rem', color: '#0d3165' }}>{userHistory.user.username}</div>
                <div style={{ fontSize: '0.83rem', color: '#888', marginTop: '0.1rem' }}>{userHistory.user.email}</div>
                <div style={{ fontSize: '0.78rem', color: '#aaa', marginTop: '0.2rem' }}>
                  Joined {new Date(userHistory.user.date_created).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  {userHistory.user.last_visit && (
                    <> &nbsp;·&nbsp; Last visit {new Date(userHistory.user.last_visit).toLocaleDateString()}</>
                  )}
                  {userHistory.user.location?.state && (
                    <> &nbsp;·&nbsp; {[userHistory.user.location.city, userHistory.user.location.state].filter(Boolean).join(', ')}</>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1.5rem' }}>
                <div style={{ textAlign: 'right', fontSize: '0.8rem' }}>
                  <div style={{ color: '#888', marginBottom: '0.15rem' }}>Token Balance</div>
                  <div style={{ fontWeight: 800, fontSize: '1.3rem', color: '#0d3165', lineHeight: 1 }}>{userHistory.user.tokens.toLocaleString()}</div>
                  <div style={{ color: '#aaa', fontSize: '0.72rem' }}>
                    {userHistory.user.purchasedTokens.toLocaleString()} purchased &nbsp;·&nbsp; {userHistory.user.promoTokens.toLocaleString()} promo
                  </div>
                </div>
                <button onClick={() => setUserHistory(null)} aria-label="Close" style={{ background: 'none', border: 'none', fontSize: '1.3rem', cursor: 'pointer', color: '#888', lineHeight: 1, marginTop: '0.1rem' }}>×</button>
              </div>
            </div>

            <div style={{ padding: '1.25rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '1.75rem' }}>

              {/* Promotional Credit History */}
              <div>
                <div style={sectionLabel}>Promotional Credit History</div>
                {!userHistory.signupBonus && !userHistory.firstPurchaseBonus && userHistory.promoRedemptions.length === 0 ? (
                  <p style={{ color: '#aaa', fontSize: '0.84rem', fontStyle: 'italic' }}>No promotional credits received.</p>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                    <thead><tr style={{ background: '#f8f9fa', borderBottom: '1px solid #e0e0e0' }}>
                      <th style={th}>Source</th>
                      <th style={{ ...th, textAlign: 'right' }}>Credits</th>
                      <th style={th}>Date</th>
                    </tr></thead>
                    <tbody>
                      {userHistory.signupBonus && (
                        <tr style={{ borderBottom: '1px solid #f0f0f0', background: '#fffbf0' }}>
                          <td style={{ ...td, fontWeight: 600 }}>Signup Bonus</td>
                          <td style={{ ...td, textAlign: 'right', color: '#6a00cc', fontWeight: 700 }}>+{userHistory.signupBonus.tokensAwarded}</td>
                          <td style={{ ...td, color: '#888' }}>{new Date(userHistory.signupBonus.awardedAt).toLocaleDateString()}</td>
                        </tr>
                      )}
                      {userHistory.firstPurchaseBonus && (
                        <tr style={{ borderBottom: '1px solid #f0f0f0', background: '#fffbf0' }}>
                          <td style={{ ...td, fontWeight: 600 }}>First Purchase Bonus</td>
                          <td style={{ ...td, textAlign: 'right', color: '#6a00cc', fontWeight: 700 }}>+{userHistory.firstPurchaseBonus.tokensAwarded}</td>
                          <td style={{ ...td, color: '#888' }}>{userHistory.firstPurchaseBonus.awardedAt ? new Date(userHistory.firstPurchaseBonus.awardedAt).toLocaleDateString() : '—'}</td>
                        </tr>
                      )}
                      {userHistory.promoRedemptions.map((p, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #f0f0f0', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                          <td style={{ ...td, fontWeight: 600 }}>Code: <span style={{ color: '#6a00cc', letterSpacing: '0.05em' }}>{p.code}</span></td>
                          <td style={{ ...td, textAlign: 'right', color: '#6a00cc', fontWeight: 700 }}>+{p.tokensAwarded}</td>
                          <td style={{ ...td, color: '#888' }}>{p.usedAt ? new Date(p.usedAt).toLocaleDateString() : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ borderTop: '2px solid #e0e0e0', background: '#f8f9fa', fontWeight: 700 }}>
                        <td style={td}>Total Promo Received</td>
                        <td style={{ ...td, textAlign: 'right', color: '#6a00cc' }}>
                          +{(userHistory.signupBonus?.tokensAwarded || 0) + (userHistory.firstPurchaseBonus?.tokensAwarded || 0) + userHistory.promoRedemptions.reduce((s, p) => s + p.tokensAwarded, 0)}
                        </td>
                        <td style={td} />
                      </tr>
                    </tfoot>
                  </table>
                )}
              </div>

              {/* Article Credit Purchases */}
              <div>
                <div style={sectionLabel}>Article Credit Purchases</div>
                {userHistory.tokenPurchases.length === 0 ? (
                  <p style={{ color: '#aaa', fontSize: '0.84rem', fontStyle: 'italic' }}>No Article Credit packages purchased.</p>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                    <thead><tr style={{ background: '#f8f9fa', borderBottom: '1px solid #e0e0e0' }}>
                      <th style={th}>Date</th>
                      <th style={th}>Package</th>
                      <th style={{ ...th, textAlign: 'right' }}>Credits</th>
                      <th style={{ ...th, textAlign: 'right' }}>Paid</th>
                      <th style={{ ...th, textAlign: 'right' }}>Stripe Fee</th>
                    </tr></thead>
                    <tbody>
                      {userHistory.tokenPurchases.map((p, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #f0f0f0', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                          <td style={{ ...td, whiteSpace: 'nowrap', color: '#888' }}>{new Date(p.purchasedAt).toLocaleDateString()}</td>
                          <td style={{ ...td, fontWeight: 600, textTransform: 'capitalize' }}>{p.package}</td>
                          <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{(p.tokensAdded || 0).toLocaleString()}</td>
                          <td style={{ ...td, textAlign: 'right', color: '#1a7a3a', fontWeight: 700 }}>{fmt(p.amountUSD || 0)}</td>
                          <td style={{ ...td, textAlign: 'right', color: '#dc3545' }}>{fmt(p.stripeFee || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ borderTop: '2px solid #e0e0e0', background: '#f8f9fa', fontWeight: 700 }}>
                        <td colSpan={2} style={td}>Total</td>
                        <td style={{ ...td, textAlign: 'right' }}>{userHistory.tokenPurchases.reduce((s, p) => s + (p.tokensAdded || 0), 0).toLocaleString()}</td>
                        <td style={{ ...td, textAlign: 'right', color: '#1a7a3a' }}>{fmt(userHistory.tokenPurchases.reduce((s, p) => s + (p.amountUSD || 0), 0))}</td>
                        <td style={{ ...td, textAlign: 'right', color: '#dc3545' }}>{fmt(userHistory.tokenPurchases.reduce((s, p) => s + (p.stripeFee || 0), 0))}</td>
                      </tr>
                    </tfoot>
                  </table>
                )}
              </div>

              {/* Article Purchases */}
              <div>
                <div style={sectionLabel}>Article Purchases ({userHistory.articlePurchases.length})</div>
                {userHistory.articlePurchases.length === 0 ? (
                  <p style={{ color: '#aaa', fontSize: '0.84rem', fontStyle: 'italic' }}>No articles purchased.</p>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                    <thead><tr style={{ background: '#f8f9fa', borderBottom: '1px solid #e0e0e0' }}>
                      <th style={th}>Date</th>
                      <th style={th}>Article</th>
                      <th style={{ ...th, textAlign: 'right' }}>Paid (cr)</th>
                      <th style={{ ...th, textAlign: 'right' }}>Promo Used</th>
                    </tr></thead>
                    <tbody>
                      {userHistory.articlePurchases.map((p, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #f0f0f0', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                          <td style={{ ...td, whiteSpace: 'nowrap', color: '#888' }}>{new Date(p.purchasedAt).toLocaleDateString()}</td>
                          <td style={td}>
                            <div style={{ fontWeight: 600, fontSize: '0.82rem' }}>{p.title}</div>
                            {p.company_name && <div style={{ color: '#aaa', fontSize: '0.72rem' }}>{p.company_name}</div>}
                          </td>
                          <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{p.tokensPaid}</td>
                          <td style={{ ...td, textAlign: 'right', color: p.promoTokensUsed > 0 ? '#8e44ad' : '#ccc' }}>
                            {p.promoTokensUsed > 0 ? p.promoTokensUsed : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Refunds */}
              <div>
                <div style={sectionLabel}>Refunds / Returns ({userHistory.refunds.length})</div>
                {userHistory.refunds.length === 0 ? (
                  <p style={{ color: '#aaa', fontSize: '0.84rem', fontStyle: 'italic' }}>No refunds.</p>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                    <thead><tr style={{ background: '#f8f9fa', borderBottom: '1px solid #e0e0e0' }}>
                      <th style={th}>Purchased</th>
                      <th style={th}>Returned</th>
                      <th style={th}>Article</th>
                      <th style={{ ...th, textAlign: 'right' }}>Tokens Refunded</th>
                    </tr></thead>
                    <tbody>
                      {userHistory.refunds.map((p, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #f0f0f0', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                          <td style={{ ...td, whiteSpace: 'nowrap', color: '#888' }}>{new Date(p.purchasedAt).toLocaleDateString()}</td>
                          <td style={{ ...td, whiteSpace: 'nowrap', color: '#888' }}>{p.returnedAt ? new Date(p.returnedAt).toLocaleDateString() : '—'}</td>
                          <td style={td}>
                            <div style={{ fontWeight: 600, fontSize: '0.82rem' }}>{p.title}</div>
                            {p.company_name && <div style={{ color: '#aaa', fontSize: '0.72rem' }}>{p.company_name}</div>}
                          </td>
                          <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: '#28a745' }}>+{p.tokensPaid}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Receipts */}
              <div>
                <div style={sectionLabel}>Purchase Receipts ({(userHistory.receipts || []).length})</div>
                {(userHistory.receipts || []).length === 0 ? (
                  <p style={{ color: '#aaa', fontSize: '0.84rem', fontStyle: 'italic' }}>No receipts on file.</p>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                    <thead><tr style={{ background: '#f8f9fa', borderBottom: '1px solid #e0e0e0' }}>
                      <th style={th}>Date</th>
                      <th style={th}>Receipt #</th>
                      <th style={th}>Package</th>
                      <th style={{ ...th, textAlign: 'right' }}>Amount</th>
                      <th style={{ ...th, textAlign: 'right' }}>Credits</th>
                      <th style={th}>IP</th>
                      <th style={th}>Acknowledged</th>
                      <th style={th}>Email</th>
                    </tr></thead>
                    <tbody>
                      {userHistory.receipts.map((r, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #f0f0f0', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                          <td style={{ ...td, whiteSpace: 'nowrap', color: '#888' }}>{new Date(r.createdAt).toLocaleDateString()}</td>
                          <td style={{ ...td, fontFamily: 'monospace', fontSize: '0.78rem', color: '#0d3165', letterSpacing: '0.03em' }}>{r.receiptNumber}</td>
                          <td style={{ ...td, textTransform: 'capitalize', fontWeight: 600 }}>{r.package}</td>
                          <td style={{ ...td, textAlign: 'right', color: '#1a7a3a', fontWeight: 700 }}>${(r.amountUSD || 0).toFixed(2)}</td>
                          <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{(r.tokensAdded || 0).toLocaleString()}{r.bonusTokens > 0 ? ` +${r.bonusTokens}` : ''}</td>
                          <td style={{ ...td, fontFamily: 'monospace', fontSize: '0.75rem', color: '#888' }}>{r.ipAddress || '—'}</td>
                          <td style={{ ...td, fontSize: '0.75rem', color: r.acknowledgedAt ? '#1a7a3a' : '#dc3545' }}>
                            {r.acknowledgedAt ? `✓ ${new Date(r.acknowledgedAt).toLocaleTimeString()}` : '—'}
                          </td>
                          <td style={td}>
                            {r.emailSentAt ? (
                              <button
                                onClick={() => fetchReceiptEmail(r._id, r.receiptNumber)}
                                disabled={receiptEmailLoading}
                                style={{ fontSize: '0.72rem', padding: '0.2rem 0.5rem', background: '#0d3165', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                              >
                                📧 View
                              </button>
                            ) : <span style={{ color: '#ccc', fontSize: '0.75rem' }}>—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Activity Log */}
              <div>
                <div style={sectionLabel}>Activity Log ({(userHistory.activityLog || []).length} events) — Chargeback Evidence</div>
                {(userHistory.activityLog || []).length === 0 ? (
                  <p style={{ color: '#aaa', fontSize: '0.84rem', fontStyle: 'italic' }}>No activity logged yet.</p>
                ) : (
                  <div style={{ maxHeight: '320px', overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: '6px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                      <thead style={{ position: 'sticky', top: 0 }}><tr style={{ background: '#f8f9fa', borderBottom: '1px solid #e0e0e0' }}>
                        <th style={th}>Date / Time</th>
                        <th style={th}>Event</th>
                        <th style={th}>IP</th>
                        <th style={th}>Details</th>
                      </tr></thead>
                      <tbody>
                        {userHistory.activityLog.map((a, i) => {
                          const typeColors = { login: '#0d6efd', token_purchase: '#1a7a3a', article_purchase: '#8e44ad', article_read: '#6c757d' };
                          const typeLabels = { login: 'Login', token_purchase: 'Token Purchase', article_purchase: 'Article Purchase', article_read: 'Article Read' };
                          const meta = a.metadata || {};
                          const detail = a.type === 'login' ? '' :
                            a.type === 'token_purchase' ? `${meta.package} · $${(meta.amountUSD || 0).toFixed(2)} · ${meta.receiptNumber || ''}` :
                            a.type === 'article_purchase' ? `${meta.articleTitle || ''} · ${meta.tokensPaid || 0} cr` :
                            a.type === 'article_read' ? (meta.articleTitle || '') : '';
                          return (
                            <tr key={i} style={{ borderBottom: '1px solid #f0f0f0', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                              <td style={{ ...td, whiteSpace: 'nowrap', color: '#888', fontSize: '0.75rem' }}>
                                {new Date(a.createdAt).toLocaleDateString()} {new Date(a.createdAt).toLocaleTimeString()}
                              </td>
                              <td style={{ ...td, fontWeight: 600, color: typeColors[a.type] || '#333', whiteSpace: 'nowrap' }}>
                                {typeLabels[a.type] || a.type}
                              </td>
                              <td style={{ ...td, fontFamily: 'monospace', fontSize: '0.72rem', color: '#888' }}>{a.ipAddress || '—'}</td>
                              <td style={{ ...td, color: '#555', fontSize: '0.77rem', maxWidth: '240px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{detail}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      )}

      {/* ── Receipt Email Preview Modal ── */}
      {receiptEmailModal && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1200, padding: '1.5rem',
        }} onClick={() => setReceiptEmailModal(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, width: '100%', maxWidth: 620, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 16px 56px rgba(0,0,0,0.3)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.9rem 1.25rem', borderBottom: '1px solid #e0e0e0', background: '#f8f9ff', borderRadius: '10px 10px 0 0', flexShrink: 0 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#0d3165' }}>Receipt Email</div>
                <div style={{ fontSize: '0.75rem', color: '#aaa', fontFamily: 'monospace', letterSpacing: '0.04em' }}>{receiptEmailModal.receiptNumber}</div>
              </div>
              <button onClick={() => setReceiptEmailModal(null)} style={{ background: 'none', border: 'none', fontSize: '1.3rem', cursor: 'pointer', color: '#888', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '1rem' }}>
              {receiptEmailModal.emailHtml ? (
                <iframe
                  title="Receipt Email Preview"
                  srcDoc={receiptEmailModal.emailHtml}
                  sandbox="allow-same-origin"
                  style={{ width: '100%', height: '500px', border: 'none', borderRadius: 6 }}
                />
              ) : (
                <p style={{ color: '#aaa', fontStyle: 'italic', textAlign: 'center', marginTop: '2rem' }}>No email content stored for this receipt.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Chargeback Suspension Modal ── */}
      {chargebackModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 2200, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '2rem', maxWidth: '480px', width: '90%', boxShadow: '0 8px 40px rgba(0,0,0,0.3)', fontFamily: 'Arial, sans-serif' }}>
            <div style={{ fontWeight: '800', fontSize: '1.05rem', color: '#cc0000', marginBottom: '0.25rem' }}>🚫 Suspend for Chargeback</div>
            <div style={{ fontWeight: '600', color: '#333', marginBottom: '0.15rem', fontSize: '0.92rem' }}>{chargebackModal.username}</div>
            <div style={{ fontSize: '0.8rem', color: '#888', marginBottom: '1rem' }}>{chargebackModal.email}</div>

            <div style={{ background: '#fff5f0', border: '1px solid #f5c6cb', borderRadius: '8px', padding: '0.75rem', marginBottom: '1rem', fontSize: '0.82rem', color: '#721c24' }}>
              This will remove credits proportional to the disputed amount, flag the account as chargeback-suspended, and send a suspension email. This action is logged.
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', fontWeight: '600', fontSize: '0.82rem', marginBottom: '0.25rem', color: '#333' }}>Dispute amount (USD):</label>
              <input
                type="number" min="0" step="0.01"
                value={chargebackForm.amount}
                onChange={e => setChargebackForm(f => ({ ...f, amount: e.target.value }))}
                placeholder="e.g. 6.00 or 10.00"
                style={{ width: '100%', padding: '0.5rem 0.7rem', border: '1px solid #ccc', borderRadius: '6px', fontSize: '0.88rem', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', fontWeight: '600', fontSize: '0.82rem', marginBottom: '0.25rem', color: '#333' }}>Reason:</label>
              <input
                value={chargebackForm.reason}
                onChange={e => setChargebackForm(f => ({ ...f, reason: e.target.value }))}
                placeholder="e.g. fraudulent, credit_not_processed"
                style={{ width: '100%', padding: '0.5rem 0.7rem', border: '1px solid #ccc', borderRadius: '6px', fontSize: '0.88rem', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontWeight: '600', fontSize: '0.82rem', marginBottom: '0.25rem', color: '#333' }}>Stripe Charge ID (optional):</label>
              <input
                value={chargebackForm.stripeChargeId}
                onChange={e => setChargebackForm(f => ({ ...f, stripeChargeId: e.target.value }))}
                placeholder="ch_… (from Stripe Dashboard)"
                style={{ width: '100%', padding: '0.5rem 0.7rem', border: '1px solid #ccc', borderRadius: '6px', fontSize: '0.88rem', boxSizing: 'border-box', fontFamily: 'monospace' }}
              />
            </div>

            <div style={{ fontSize: '0.78rem', color: '#888', marginBottom: '1rem' }}>
              Current balance: <strong>{(chargebackModal.tokens || 0).toLocaleString()}</strong> credits
              &nbsp;·&nbsp; Purchased: <strong>{(chargebackModal.purchasedTokens || 0).toLocaleString()}</strong>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={handleChargebackSuspend}
                disabled={!chargebackForm.amount}
                style={{ flex: 1, padding: '0.6rem', background: '#cc0000', color: '#fff', border: 'none', borderRadius: '7px', fontWeight: '700', fontSize: '0.88rem', cursor: !chargebackForm.amount ? 'not-allowed' : 'pointer', opacity: !chargebackForm.amount ? 0.6 : 1 }}
              >
                Confirm Suspension
              </button>
              <button
                onClick={() => setChargebackModal(null)}
                style={{ flex: 1, padding: '0.6rem', background: '#f4f4f4', color: '#333', border: '1px solid #ddd', borderRadius: '7px', cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Unsuspend Confirmation Modal ── */}
      {unsuspendConfirm && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 2200, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '2rem', maxWidth: '420px', width: '90%', boxShadow: '0 8px 40px rgba(0,0,0,0.3)', fontFamily: 'Arial, sans-serif' }}>
            <div style={{ fontWeight: '800', fontSize: '1.05rem', color: '#28a745', marginBottom: '0.25rem' }}>✅ Unsuspend Account</div>
            <div style={{ fontWeight: '600', color: '#333', marginBottom: '0.15rem', fontSize: '0.92rem' }}>{unsuspendConfirm.username}</div>
            <div style={{ fontSize: '0.8rem', color: '#888', marginBottom: '1rem' }}>{unsuspendConfirm.email}</div>

            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '0.75rem', marginBottom: '0.75rem', fontSize: '0.82rem', color: '#166534' }}>
              This clears the chargeback suspension flag and sends a reinstatement email. Credits are <strong>not</strong> restored — do that manually if warranted.
            </div>

            {unsuspendConfirm.chargebackSuspendedAt && (
              <div style={{ fontSize: '0.78rem', color: '#888', marginBottom: '1rem' }}>
                Suspended: {new Date(unsuspendConfirm.chargebackSuspendedAt).toLocaleDateString()}
                {unsuspendConfirm.chargebackAmount && <> &nbsp;·&nbsp; Amount: ${Number(unsuspendConfirm.chargebackAmount).toFixed(2)}</>}
                {unsuspendConfirm.chargebackReason && <> &nbsp;·&nbsp; Reason: {unsuspendConfirm.chargebackReason}</>}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={handleChargebackUnsuspend}
                style={{ flex: 1, padding: '0.6rem', background: '#28a745', color: '#fff', border: 'none', borderRadius: '7px', fontWeight: '700', fontSize: '0.88rem', cursor: 'pointer' }}
              >
                Confirm Unsuspend
              </button>
              <button
                onClick={() => setUnsuspendConfirm(null)}
                style={{ flex: 1, padding: '0.6rem', background: '#f4f4f4', color: '#333', border: '1px solid #ddd', borderRadius: '7px', cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Master Invoice Modal ── */}
      {masterInvOpen && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          zIndex: 1200, overflowY: 'auto', padding: '2rem 1rem',
        }} onClick={() => setMasterInvOpen(false)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: '#fff', borderRadius: 12, width: '100%', maxWidth: 820,
            boxShadow: '0 12px 48px rgba(0,0,0,0.22)', marginBottom: '2rem',
          }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.1rem 1.5rem', borderBottom: '1px solid #e0e0e0', background: '#0d3165', borderRadius: '12px 12px 0 0' }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: '1rem', color: '#fff' }}>📊 Master Invoice</div>
                <div style={{ fontSize: '0.8rem', color: '#8fa8cc', marginTop: '0.1rem' }}>All publishers · PubSilo revenue by month</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <select
                  value={masterInvMonth}
                  onChange={e => { setMasterInvMonth(e.target.value); fetchMasterInvoice(e.target.value); }}
                  style={{ padding: '0.4rem 0.7rem', borderRadius: 6, border: '1px solid #4a6fa5', background: '#13273F', color: '#fff', fontSize: '0.85rem', cursor: 'pointer' }}
                >
                  {masterInvMonths.map(m => <option key={m.val} value={m.val}>{m.label}</option>)}
                </select>
                <button onClick={() => setMasterInvOpen(false)} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#8fa8cc', lineHeight: 1 }}>×</button>
              </div>
            </div>

            <div style={{ padding: '1.5rem' }}>
              {masterInvLoading && <p style={{ color: '#888', textAlign: 'center' }}>Loading…</p>}
              {masterInvError  && <p style={{ color: '#dc3545', textAlign: 'center' }}>{masterInvError}</p>}

              {masterInvData && (() => {
                const d = masterInvData;
                const fmt = v => `$${Number(v).toFixed(2)}`;
                const monthLabel = new Date(`${d.month}-01`).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

                return (
                  <>
                    {/* ── Section 1: Publisher Payouts ── */}
                    <div style={{ marginBottom: '1.5rem' }}>
                      <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#0d3165', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.6rem' }}>
                        Publisher Payouts — {monthLabel}
                      </div>

                      {d.companies.length === 0 ? (
                        <p style={{ color: '#aaa', fontStyle: 'italic', fontSize: '0.88rem' }}>No article licensing activity this month.</p>
                      ) : (
                        <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, overflow: 'hidden' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                            <thead>
                              <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>
                                <th style={{ padding: '0.55rem 0.75rem', textAlign: 'left', fontWeight: 700, color: '#333' }}>Publisher</th>
                                <th style={{ padding: '0.55rem 0.75rem', textAlign: 'right', fontWeight: 700, color: '#333' }}>Gross</th>
                                <th style={{ padding: '0.55rem 0.75rem', textAlign: 'right', fontWeight: 700, color: '#333' }}>PubSilo Fee</th>
                                <th style={{ padding: '0.55rem 0.75rem', textAlign: 'right', fontWeight: 700, color: '#333' }}>Net Payout</th>
                                <th style={{ padding: '0.55rem 0.75rem', textAlign: 'center', fontWeight: 700, color: '#333' }}>W-9</th>
                                <th style={{ padding: '0.55rem 0.75rem', textAlign: 'center', fontWeight: 700, color: '#333' }}>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {d.companies.map((co, i) => {
                                const paid = masterPayoutMap[co._id.toString()];
                                return (
                                  <tr key={co._id} style={{ borderBottom: '1px solid #f0f0f0', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                                    <td style={{ padding: '0.5rem 0.75rem' }}>
                                      <div style={{ fontWeight: 700, color: '#0d3165' }}>{co.company_name}</div>
                                      <div style={{ fontSize: '0.73rem', color: '#888' }}>{co.email}</div>
                                      {co.w9Name && <div style={{ fontSize: '0.73rem', color: '#555', marginTop: '0.1rem' }}>{co.w9Name}</div>}
                                    </td>
                                    <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', fontWeight: 600 }}>{fmt(co.gross)}</td>
                                    <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', color: '#c0710a' }}>
                                      {fmt(co.fee)}
                                      <div style={{ fontSize: '0.7rem', color: '#aaa' }}>{(co.effectiveRate * 100).toFixed(1)}%</div>
                                    </td>
                                    <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', fontWeight: 700, color: '#7b2ff7', fontSize: '0.92rem' }}>{fmt(co.net)}</td>
                                    <td style={{ padding: '0.5rem 0.75rem', textAlign: 'center' }}>
                                      {co.hasW9
                                        ? <span style={{ background: '#e6f9ec', color: '#1a7a3a', borderRadius: 99, padding: '0.15rem 0.5rem', fontSize: '0.7rem', fontWeight: 700 }}>✓ On file</span>
                                        : <span style={{ background: '#fff3cd', color: '#856404', borderRadius: 99, padding: '0.15rem 0.5rem', fontSize: '0.7rem', fontWeight: 700 }}>⚠️ Missing</span>
                                      }
                                    </td>
                                    <td style={{ padding: '0.5rem 0.75rem', textAlign: 'center' }}>
                                      {paid
                                        ? <span style={{ background: '#e6f9ec', color: '#1a7a3a', borderRadius: 99, padding: '0.15rem 0.5rem', fontSize: '0.7rem', fontWeight: 700 }}>✓ Paid</span>
                                        : <span style={{ background: '#fff8e1', color: '#856404', borderRadius: 99, padding: '0.15rem 0.5rem', fontSize: '0.7rem', fontWeight: 700 }}>⬜ Unpaid</span>
                                      }
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                            {/* Totals + paid summary */}
                            <tfoot>
                              <tr style={{ borderTop: '2px solid #e0e0e0', background: '#f0f4ff', fontWeight: 700 }}>
                                <td style={{ padding: '0.6rem 0.75rem', color: '#0d3165' }}>
                                  Total — {d.companies.length} publisher{d.companies.length !== 1 ? 's' : ''}
                                </td>
                                <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right' }}>{fmt(d.totals.gross)}</td>
                                <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right', color: '#c0710a' }}>{fmt(d.totals.fee)}</td>
                                <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right', color: '#7b2ff7', fontSize: '0.96rem' }}>{fmt(d.totals.net)}</td>
                                <td />
                                <td style={{ padding: '0.6rem 0.75rem', textAlign: 'center', fontSize: '0.75rem', color: '#555' }}>
                                  {(() => {
                                    const paidCount = d.companies.filter(co => masterPayoutMap[co._id.toString()]).length;
                                    const paidAmt   = d.companies.filter(co => masterPayoutMap[co._id.toString()]).reduce((s, co) => s + (co.net || 0), 0);
                                    const outAmt    = d.totals.net - paidAmt;
                                    return paidCount > 0
                                      ? <><span style={{ color: '#1a7a3a', fontWeight: 700 }}>{paidCount}/{d.companies.length} paid</span><br/><span style={{ color: '#888' }}>{fmt(outAmt)} outstanding</span></>
                                      : <span style={{ color: '#856404' }}>0/{d.companies.length} paid</span>;
                                  })()}
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      )}
                    </div>

                    {/* ── Section 2: Token Package Revenue ── */}
                    <div style={{ marginBottom: '1.5rem' }}>
                      <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#0d3165', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.6rem' }}>
                        Article Credit Package Sales — {monthLabel}
                      </div>
                      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                        {[
                          { label: 'Packages Sold',  value: d.tokenPackages.count.toLocaleString(), color: '#0d3165', sub: 'this month' },
                          { label: 'Gross Revenue',  value: fmt(d.tokenPackages.gross),              color: '#1a7a3a', sub: 'collected from buyers' },
                          { label: 'Stripe Fees',    value: `−${fmt(d.tokenPackages.stripeFees)}`,  color: '#dc3545', sub: 'processing costs' },
                          { label: 'Package Net',    value: fmt(d.tokenPackages.platformNet),        color: '#0d3165', sub: 'after Stripe (excl. company pool)' },
                        ].map(c => (
                          <div key={c.label} style={{ flex: '1 1 140px', background: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: 8, padding: '0.85rem 1rem' }}>
                            <div style={{ fontSize: '0.72rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.25rem' }}>{c.label}</div>
                            <div style={{ fontSize: '1.35rem', fontWeight: 800, color: c.color, lineHeight: 1 }}>{c.value}</div>
                            <div style={{ fontSize: '0.7rem', color: '#aaa', marginTop: '0.2rem' }}>{c.sub}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* ── Section 3: PubSilo Monthly P&L ── */}
                    <div style={{ background: '#0d3165', borderRadius: 10, padding: '1.25rem 1.5rem', color: '#fff' }}>
                      <div style={{ fontWeight: 800, fontSize: '0.88rem', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '1rem', color: '#8fa8cc' }}>
                        PubSilo Revenue — {monthLabel}
                      </div>
                      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                        <div>
                          <div style={{ fontSize: '0.72rem', color: '#8fa8cc', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Article Platform Fees</div>
                          <div style={{ fontSize: '1.5rem', fontWeight: 800 }}>{fmt(d.pubsiloRevenue.articleFees)}</div>
                          <div style={{ fontSize: '0.7rem', color: '#8fa8cc' }}>tiered fee on {fmt(d.totals.gross)} gross</div>
                        </div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 300, color: '#4a6fa5', alignSelf: 'center' }}>+</div>
                        <div>
                          <div style={{ fontSize: '0.72rem', color: '#8fa8cc', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Package Net (margin only)</div>
                          <div style={{ fontSize: '1.5rem', fontWeight: 800 }}>{fmt(d.pubsiloRevenue.tokenPackageNet)}</div>
                          <div style={{ fontSize: '0.7rem', color: '#8fa8cc' }}>{d.tokenPackages.count} pkg{d.tokenPackages.count !== 1 ? 's' : ''} · after Stripe</div>
                        </div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 300, color: '#4a6fa5', alignSelf: 'center' }}>=</div>
                        <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: 8, padding: '0.5rem 1rem' }}>
                          <div style={{ fontSize: '0.72rem', color: '#8fa8cc', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Total PubSilo Revenue</div>
                          <div style={{ fontSize: '2rem', fontWeight: 900, color: '#fff' }}>{fmt(d.pubsiloRevenue.total)}</div>
                          <div style={{ fontSize: '0.7rem', color: '#8fa8cc' }}>before operating expenses</div>
                        </div>
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#4a6fa5', borderTop: '1px solid #1e4a80', paddingTop: '0.75rem' }}>
                        Note: Package net above reflects PubSilo's margin on credit sales only (gross − Stripe fees). The company pool portion ($0.01 × tokens) is not included — it flows out as article licensing payouts shown above.
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* ── Broadcast Email Modal ── */}
      {broadcastModal && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9100, padding: '2rem 1rem',
        }} onClick={() => setBroadcastModal(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 540, boxShadow: '0 12px 48px rgba(0,0,0,0.22)', padding: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <div style={{ fontWeight: 800, fontSize: '1rem', color: '#0d3165' }}>📧 Send Promotional Email</div>
              <button onClick={() => setBroadcastModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.3rem', cursor: 'pointer', color: '#888' }}>×</button>
            </div>
            <div style={{ fontSize: '0.82rem', color: '#555', marginBottom: '1rem', padding: '0.5rem 0.75rem', background: '#f0f4ff', borderRadius: 6 }}>
              Sending to <strong>{selectedUserIds.size} user{selectedUserIds.size !== 1 ? 's' : ''}</strong>
            </div>

            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#333', marginBottom: '0.3rem' }}>Subject</label>
            <input
              value={broadcastForm.subject}
              onChange={e => setBroadcastForm(p => ({ ...p, subject: e.target.value }))}
              placeholder="Email subject line…"
              style={{ width: '100%', padding: '0.55rem 0.8rem', border: '1px solid #ccc', borderRadius: 6, fontSize: '0.88rem', marginBottom: '0.85rem', boxSizing: 'border-box' }}
            />

            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#333', marginBottom: '0.3rem' }}>
              Message Body <span style={{ fontWeight: 400, color: '#888' }}>(plain text — blank lines become paragraphs)</span>
            </label>
            <textarea
              value={broadcastForm.html}
              onChange={e => setBroadcastForm(p => ({ ...p, html: e.target.value }))}
              placeholder="Write your message here. Blank lines between paragraphs are preserved automatically."
              rows={7}
              style={{ width: '100%', padding: '0.55rem 0.8rem', border: '1px solid #ccc', borderRadius: 6, fontSize: '0.88rem', marginBottom: '0.85rem', resize: 'vertical', boxSizing: 'border-box' }}
            />

            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#333', marginBottom: '0.3rem' }}>Admin Password (to confirm send)</label>
            <input
              type="password"
              value={broadcastPassword}
              onChange={e => setBroadcastPassword(e.target.value)}
              placeholder="Admin password…"
              style={{ width: '100%', padding: '0.55rem 0.8rem', border: '1px solid #ccc', borderRadius: 6, fontSize: '0.88rem', marginBottom: '1rem', boxSizing: 'border-box' }}
            />

            {broadcastResult && (
              <div style={{
                marginBottom: '1rem', padding: '0.6rem 0.9rem', borderRadius: 6, fontSize: '0.84rem',
                background: broadcastResult.error ? '#fff0f0' : '#f0fff4',
                color: broadcastResult.error ? '#cc0000' : '#1a7a3a',
                border: `1px solid ${broadcastResult.error ? '#ffb3b3' : '#b3f0c7'}`,
              }}>
                {broadcastResult.error
                  ? `Error: ${broadcastResult.error}`
                  : `Sent: ${broadcastResult.sent} · Failed: ${broadcastResult.failed} · Total: ${broadcastResult.total}`
                }
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.6rem' }}>
              <button
                onClick={handleBroadcastSend}
                disabled={broadcastLoading || !broadcastForm.subject.trim() || !broadcastForm.html.trim() || !broadcastPassword}
                style={{
                  flex: 1, padding: '0.65rem', background: broadcastLoading ? '#aaa' : '#0d3165', color: '#fff',
                  border: 'none', borderRadius: 7, fontWeight: 700, fontSize: '0.9rem',
                  cursor: broadcastLoading ? 'not-allowed' : 'pointer',
                }}
              >
                {broadcastLoading ? 'Sending…' : `Send to ${selectedUserIds.size} user${selectedUserIds.size !== 1 ? 's' : ''}`}
              </button>
              <button
                onClick={() => setBroadcastModal(false)}
                style={{ padding: '0.65rem 1.2rem', background: '#f4f4f4', color: '#333', border: '1px solid #ddd', borderRadius: 7, cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Invoice Modal ── */}
      {invoiceTarget && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          zIndex: 1050, overflowY: 'auto', padding: '2rem 1rem',
        }} onClick={() => setInvoiceTarget(null)}>
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 680, boxShadow: '0 12px 48px rgba(0,0,0,0.22)' }}
          >
            {/* Modal header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.25rem 1.5rem', borderBottom: '1px solid #e0e0e0' }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: '1.05rem', color: '#0d3165' }}>Content Licensing Statement</div>
                <div style={{ fontSize: '0.82rem', color: '#888', marginTop: '0.15rem' }}>{invoiceTarget.company_name}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <select
                  value={invoiceMonth}
                  onChange={e => handleInvoiceMonthChange(e.target.value)}
                  style={{ fontSize: '0.88rem', padding: '0.35rem 0.6rem', border: '1px solid #ccc', borderRadius: 6, background: '#fff' }}
                >
                  {getInvoiceMonths(invoiceTarget).map(m => (
                    <option key={m} value={m}>{formatMonthLabel(m)}</option>
                  ))}
                  {getInvoiceMonths(invoiceTarget).length === 0 && (
                    <option value="">No closed months yet</option>
                  )}
                </select>
                <button onClick={() => setInvoiceTarget(null)} aria-label="Close" style={{ background: 'none', border: 'none', fontSize: '1.3rem', cursor: 'pointer', color: '#888', lineHeight: 1 }}>×</button>
              </div>
            </div>

            {/* Payout hold warning */}
            {(invoiceTarget?.disabled || invoiceTarget?.deletionFlagged) && (
              <div style={{ background: '#fff3cd', borderLeft: '4px solid #e67e22', padding: '0.75rem 1.25rem', margin: '0 1.5rem', marginTop: '1rem', borderRadius: '4px', fontSize: '0.84rem', color: '#7a4e00' }}>
                {invoiceTarget.disabled && <div><strong>🚫 Account Disabled</strong> — Payouts are on hold. Do not pay until account is re-enabled.</div>}
                {invoiceTarget.deletionFlagged && <div><strong>⚠️ Deletion Debt: ${(invoiceTarget.deletionDebt || 0).toFixed(2)}</strong> — Payouts are held until debt is cleared.</div>}
              </div>
            )}

            {/* W-9 missing warning */}
            {!invoiceTarget?.w9?.submitted && (
              <div style={{ background: '#fef3cd', borderLeft: '4px solid #c0710a', padding: '0.75rem 1.25rem', margin: '0 1.5rem', marginTop: '0.75rem', borderRadius: '4px', fontSize: '0.84rem', color: '#7a4e00' }}>
                <strong>📋 W-9 Not on File</strong> — This publisher has not submitted tax information. Do not process payouts exceeding $600 until a W-9 is on file (required for IRS 1099-NEC reporting). Contact the publisher at <em>{invoiceTarget?.email || '—'}</em> to request submission.
              </div>
            )}
            {invoiceTarget?.w9?.submitted && (
              <div style={{ background: '#d4edda', borderLeft: '4px solid #28a745', padding: '0.5rem 1.25rem', margin: '0 1.5rem', marginTop: '0.75rem', borderRadius: '4px', fontSize: '0.8rem', color: '#155724' }}>
                ✓ <strong>W-9 on file</strong> — {invoiceTarget.w9.legalName}{invoiceTarget.w9.tinType ? ` · ${invoiceTarget.w9.tinType.toUpperCase()} ending ${invoiceTarget.w9.tinLast4 || '????'}` : ''}
              </div>
            )}

            {/* ── Invoice Actions: Email + Mark Paid ── */}
            {invoiceData && invoiceTarget && (() => {
              const payKey    = `${invoiceTarget._id}-${invoiceMonth}`;
              const paidRec   = payoutRecords[payKey];
              const billingTo = invoiceTarget.billingEmail || invoiceTarget.email;

              return (
                <div style={{ borderTop: '1px solid #e0e0e0', padding: '1rem 1.5rem', background: '#fafafa', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {/* Row 1: Email Invoice */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => handleEmailInvoice(invoiceTarget._id, invoiceMonth)}
                      disabled={invoiceSending}
                      style={{ padding: '0.45rem 1rem', background: invoiceSending ? '#aaa' : '#0d3165', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: '0.82rem', cursor: invoiceSending ? 'not-allowed' : 'pointer' }}
                    >
                      {invoiceSending ? 'Sending…' : '📧 Email Invoice'}
                    </button>
                    <span style={{ fontSize: '0.78rem', color: '#888' }}>
                      → {billingTo}
                      {invoiceTarget.billingEmail && invoiceTarget.billingEmail !== invoiceTarget.email && (
                        <span style={{ marginLeft: '0.4rem', fontSize: '0.72rem', color: '#aaa' }}>(billing email)</span>
                      )}
                    </span>
                    {invoiceSentMsg && (
                      <span style={{ fontSize: '0.78rem', color: invoiceSentMsg.startsWith('Error') ? '#dc3545' : '#1a7a3a', fontWeight: 600 }}>
                        {invoiceSentMsg}
                      </span>
                    )}
                  </div>

                  {/* Row 2: Payout status */}
                  {paidRec ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                      <span style={{ background: '#e6f9ec', color: '#1a7a3a', borderRadius: 99, padding: '0.3rem 0.8rem', fontSize: '0.8rem', fontWeight: 700 }}>
                        ✓ Paid ${(paidRec.net || 0).toFixed(2)} · {new Date(paidRec.paidAt).toLocaleDateString()} · {paidRec.method?.toUpperCase()}
                        {paidRec.notes && ` — ${paidRec.notes}`}
                      </span>
                      <button
                        onClick={() => handleUndoPayout(paidRec._id, invoiceTarget._id, invoiceMonth)}
                        style={{ fontSize: '0.72rem', padding: '0.2rem 0.6rem', background: 'none', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', color: '#888' }}
                      >
                        Undo
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <select
                        value={payoutForm.method}
                        onChange={e => setPayoutForm(p => ({ ...p, method: e.target.value }))}
                        style={{ padding: '0.35rem 0.6rem', border: '1px solid #ccc', borderRadius: 5, fontSize: '0.82rem' }}
                      >
                        <option value="ach">ACH</option>
                        <option value="check">Check</option>
                        <option value="paypal">PayPal</option>
                        <option value="wire">Wire</option>
                        <option value="other">Other</option>
                      </select>
                      <input
                        value={payoutForm.notes}
                        onChange={e => setPayoutForm(p => ({ ...p, notes: e.target.value }))}
                        placeholder="Notes (optional)…"
                        style={{ padding: '0.35rem 0.7rem', border: '1px solid #ccc', borderRadius: 5, fontSize: '0.82rem', width: '180px' }}
                      />
                      <button
                        onClick={() => handleMarkPaid(
                          invoiceTarget._id, invoiceMonth,
                          invoiceData.totalArticleGross,
                          invoiceData.totalArticleSiteFee,
                          invoiceData.totalArticleRevenue
                        )}
                        disabled={markingPaid}
                        style={{ padding: '0.35rem 0.9rem', background: markingPaid ? '#aaa' : '#28a745', color: '#fff', border: 'none', borderRadius: 5, fontWeight: 700, fontSize: '0.82rem', cursor: markingPaid ? 'not-allowed' : 'pointer' }}
                      >
                        {markingPaid ? 'Saving…' : '✓ Mark as Paid'}
                      </button>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Invoice body */}
            <div style={{ padding: '1.5rem' }} id="pasnat-invoice-print">
              {invoiceLoading && <p style={{ color: '#888', textAlign: 'center' }}>Loading…</p>}
              {invoiceError  && <p style={{ color: 'red', textAlign: 'center' }}>{invoiceError}</p>}

              {invoiceData && (() => {
                const d = invoiceData;
                const invoiceDate = new Date(`${d.month}-01`);
                const dueDate = new Date(invoiceDate.getFullYear(), invoiceDate.getMonth() + 1, 1)
                  .toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
                const periodLabel = formatMonthLabel(d.month);
                return (
                  <>
                    {/* Invoice header block */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '2px solid #0d3165' }}>
                      <div>
                        <div style={{ fontWeight: 900, fontSize: '1.4rem', color: '#0d3165', letterSpacing: '-0.02em' }}>PUBSILO</div>
                        <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '0.15rem' }}>pubsilo.com</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontWeight: 700, fontSize: '1rem', color: '#333' }}>{d.company.company_name}</div>
                        <div style={{ fontSize: '0.82rem', color: '#777' }}>{d.company.email}</div>
                        <div style={{ fontSize: '0.82rem', color: '#888', marginTop: '0.25rem' }}>Period: <strong>{periodLabel}</strong></div>
                        <div style={{ fontSize: '0.82rem', color: '#888' }}>Settlement date: <strong>{dueDate}</strong></div>
                      </div>
                    </div>

                    {/* Revenue rows */}
                    <div style={{ marginBottom: '1.5rem' }}>
                      <div style={{ fontWeight: 700, fontSize: '0.78rem', color: '#555', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.5rem' }}>
                        Content Access Activity
                      </div>
                      <div style={{ background: '#f0f8ff', border: '1px solid #ddd', borderRadius: 8, overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                          <thead>
                            <tr style={{ background: '#d8eeff' }}>
                              <th style={{ ...th, fontSize: '0.78rem' }}>Article / Price Point</th>
                              <th style={{ ...th, fontSize: '0.78rem', textAlign: 'center' }}>Sales</th>
                              <th style={{ ...th, fontSize: '0.78rem', textAlign: 'center', color: '#8e44ad' }}>Promo cr</th>
                              <th style={{ ...th, fontSize: '0.78rem', textAlign: 'right' }}>Gross</th>
                              <th style={{ ...th, fontSize: '0.78rem', textAlign: 'right', color: '#c0710a' }}>Site Fee (est.)</th>
                              <th style={{ ...th, fontSize: '0.78rem', textAlign: 'right' }}>Net to Co.</th>
                            </tr>
                          </thead>
                          <tbody>
                            {d.articles.length === 0 ? (
                              <tr><td colSpan={6} style={{ ...td, color: '#aaa', textAlign: 'center', fontStyle: 'italic' }}>No article purchases this period.</td></tr>
                            ) : (
                              d.articles.map((a) => (
                                <React.Fragment key={a._id}>
                                  <tr style={{ borderTop: '2px solid #c8dcf0', background: '#eaf4ff' }}>
                                    <td style={{ ...td, fontWeight: 700, fontSize: '0.88rem' }} colSpan={3}>
                                      <div>{a.title}</div>
                                      <div style={{ fontSize: '0.71rem', fontWeight: 400, color: '#888', marginTop: '0.15rem' }}>
                                        {(a.lifetimePurchasesAtStart || 0).toLocaleString()} entering period &nbsp;+&nbsp;
                                        {(a.thisMonthPurchases || 0).toLocaleString()} this period &nbsp;=&nbsp;
                                        <strong>{(a.lifetimePurchases || 0).toLocaleString()} lifetime</strong>
                                      </div>
                                    </td>
                                    <td style={{ ...td, textAlign: 'right', fontWeight: 600, color: '#555', fontSize: '0.88rem' }}>{fmt(a.grossRevenue)}</td>
                                    <td style={{ ...td, textAlign: 'right', fontWeight: 600, color: '#c0710a', fontSize: '0.88rem' }}>−{fmt(a.siteFee)}</td>
                                    <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: '#1a7a3a', fontSize: '0.88rem' }}>{fmt(a.netRevenue)}</td>
                                  </tr>
                                  {a.priorMonth && (
                                    <tr style={{ background: '#fffbee', borderTop: '1px solid #eee0aa' }}>
                                      <td style={{ ...td, paddingLeft: '2rem', fontSize: '0.78rem', color: '#aaa', fontStyle: 'italic' }} colSpan={3}>
                                        Prior invoice ({formatMonthLabel(a.priorMonth.month)}): {(a.priorMonth.purchases || 0).toLocaleString()} sales — publisher paid {fmt(a.priorMonth.netRevenue)}
                                      </td>
                                      <td style={{ ...td, textAlign: 'right', fontSize: '0.78rem', color: '#bbb' }}>{fmt(a.priorMonth.gross)}</td>
                                      <td style={{ ...td, textAlign: 'right', fontSize: '0.78rem', color: '#bbb' }}>−{fmt(a.priorMonth.siteFee)}</td>
                                      <td style={{ ...td, textAlign: 'right', fontSize: '0.78rem', color: '#bbb' }}>{fmt(a.priorMonth.netRevenue)}</td>
                                    </tr>
                                  )}
                                  {(a.pricePoints || []).map((pp, pi) => (
                                    <tr key={pi} style={{ borderTop: '1px solid #e8f0f8', background: pi % 2 === 0 ? '#fff' : '#f7fbff' }}>
                                      <td style={{ ...td, paddingLeft: '2rem', fontSize: '0.82rem', color: '#555' }}>
                                        ↳ {pp.token_cost} cr/sale
                                        <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: '#aaa' }}>
                                          ({pp.count} × {pp.token_cost} − {pp.promoTokensUsed} promo = {Math.max(0, pp.count * pp.token_cost - pp.promoTokensUsed)} cr)
                                        </span>
                                      </td>
                                      <td style={{ ...td, textAlign: 'center', fontSize: '0.82rem' }}>{pp.count}</td>
                                      <td style={{ ...td, textAlign: 'center', fontSize: '0.82rem', color: pp.promoTokensUsed > 0 ? '#8e44ad' : '#bbb' }}>
                                        {pp.promoTokensUsed > 0 ? pp.promoTokensUsed : '—'}
                                      </td>
                                      <td style={{ ...td, textAlign: 'right', color: '#555', fontSize: '0.82rem' }}>{fmt(pp.gross)}</td>
                                      <td style={{ ...td, textAlign: 'right', color: '#c0710a', fontSize: '0.82rem' }}>−{fmt(pp.siteFee)}</td>
                                      <td style={{ ...td, textAlign: 'right', color: '#28a745', fontSize: '0.82rem' }}>{fmt(pp.revenue)}</td>
                                    </tr>
                                  ))}
                                </React.Fragment>
                              ))
                            )}
                          </tbody>
                          <tfoot>
                            <tr style={{ borderTop: '2px solid #b8d8f0', background: '#eaf4ff' }}>
                              <td colSpan={3} style={{ ...td, fontWeight: 700, fontSize: '0.9rem' }}>
                                Total Content Access Events
                                <span style={{ marginLeft: '0.75rem', fontSize: '0.75rem', fontWeight: 400, color: '#888' }}>
                                  ({d.articles.reduce((s,a) => s + (a.pricePoints||[]).reduce((t,p) => t + p.count, 0), 0)} accesses)
                                </span>
                              </td>
                              <td colSpan={3} style={{ ...td, fontWeight: 700, fontSize: '0.9rem', color: '#555', textAlign: 'right' }}>{fmt(d.totalArticleGross ?? 0)}</td>
                            </tr>
                          </tfoot>
                        </table>
                        <div style={{ fontSize: '0.71rem', color: '#aaa', padding: '0.35rem 0.75rem', fontStyle: 'italic' }}>
                          * Site Fee (est.) column uses the flat 12.5% rate per article for reference. Actual billing uses the tiered monthly rate shown in the summary below — per-article estimates will not sum to the billed total.
                        </div>
                      </div>
                    </div>

                    {/* Fee breakdown + Summary */}
                    {(() => {
                      const fb = d.feeBreakdown || {};
                      const aboveTier1 = (fb.tier2Gross || 0) > 0;
                      const aboveTier2 = (fb.tier3Gross || 0) > 0;
                      return (
                        <div style={{ background: '#f4f4f4', borderRadius: 8, padding: '1rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.75rem' }}>
                          <div style={{ fontSize: '0.82rem', color: '#555', lineHeight: 2 }}>
                            <div><strong>Gross content access value:</strong> <span style={{ color: '#555' }}>{fmt(d.totalArticleGross ?? 0)}</span></div>
                            <div style={{ borderTop: '1px solid #ddd', paddingTop: '4px', marginTop: '2px' }}>
                              <span style={{ color: '#888', fontSize: '0.77rem', fontStyle: 'italic' }}>PubSilo distribution &amp; platform fee — tiered monthly rate</span>
                            </div>
                            <div style={{ paddingLeft: '1rem' }}>
                              <div>First {fmt(2000)} at 15%: <span style={{ color: '#c0710a' }}>−{fmt(fb.tier1Fee ?? 0)}</span></div>
                              {aboveTier1
                                ? <div>$2,000–$5,000 at 12.5%: <span style={{ color: '#c0710a' }}>−{fmt(fb.tier2Fee ?? 0)}</span></div>
                                : <div style={{ color: '#aaa' }}>$2,000–$5,000 at 12.5%: <span>n/a — under threshold</span></div>
                              }
                              {aboveTier2
                                ? <div>Above $5,000 at 9.0%: <span style={{ color: '#c0710a' }}>−{fmt(fb.tier3Fee ?? 0)}</span></div>
                                : <div style={{ color: '#aaa' }}>Above $5,000 at 9.0%: <span>n/a — under threshold</span></div>
                              }
                            </div>
                            <div style={{ borderTop: '1px solid #ddd', paddingTop: '4px', marginTop: '2px' }}>
                              <strong>Total distribution fee</strong> <span style={{ color: '#888', fontSize: '0.77rem' }}>({aboveTier1 ? `blended ${((fb.effectiveRate||0)*100).toFixed(1)}%` : '15%'})</span>: <span style={{ color: '#c0710a', fontWeight: 700 }}>−{fmt(d.totalArticleSiteFee ?? 0)}</span>
                            </div>
                            <div><strong>Content licensing payment:</strong> <span style={{ color: '#1a7a3a', fontWeight: 800, fontSize: '0.9rem' }}>{fmt(d.totalArticleRevenue)}</span></div>
                          </div>
                          <button
                            onClick={() => window.print()}
                            style={{ fontSize: '0.82rem', padding: '0.45rem 1rem', background: '#0d3165', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', alignSelf: 'flex-end' }}
                          >
                            🖨 Print / Save PDF
                          </button>
                        </div>
                      );
                    })()}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* ── Ad Management Modal ── */}
      {adsOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 2200, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ background: '#f8f8f8', width: '100%', maxWidth: '1100px', borderRadius: '12px', overflow: 'hidden', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 12px 60px rgba(0,0,0,0.35)', fontFamily: 'Arial, sans-serif' }}>

            {/* Header */}
            <div style={{ background: '#13273F', color: '#fff', padding: '1rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <div style={{ fontWeight: '800', fontSize: '1.05rem' }}>
                Advertisement Management
                {!adForm && adsList.length > 0 && <span style={{ fontWeight: '400', fontSize: '0.82rem', marginLeft: '10px', color: '#7a9cbf' }}>{adsList.filter(a => a.active).length} live · {adsList.length} total</span>}
              </div>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                {!adForm && (
                  <button onClick={() => { setAdError(''); setAdForm({ ...AD_BLANK }); }} style={{ background: '#dd3333', color: '#fff', border: 'none', borderRadius: '6px', padding: '7px 16px', fontWeight: '700', fontSize: '0.85rem', cursor: 'pointer' }}>
                    + New Ad
                  </button>
                )}
                <button onClick={() => { setAdsOpen(false); setAdForm(null); setAdError(''); }} style={{ background: 'transparent', color: '#ccc', border: '1px solid #3a3a3a', borderRadius: '6px', padding: '7px 14px', cursor: 'pointer', fontSize: '0.85rem' }}>
                  ✕ Close
                </button>
              </div>
            </div>

            {/* Content */}
            <div style={{ overflow: 'auto', flex: 1, padding: '1.5rem' }}>

              {/* ── Create / Edit Form ── */}
              {adForm && (() => {
                const setF = (field, val) => setAdForm(f => ({ ...f, [field]: val }));
                const toggleArr = (field, val) => setAdForm(f => ({ ...f, [field]: f[field].includes(val) ? f[field].filter(v => v !== val) : [...f[field], val] }));
                const inp = { width: '100%', boxSizing: 'border-box', padding: '0.55rem 0.8rem', border: '1px solid #ccc', borderRadius: '5px', fontSize: '0.9rem', fontFamily: 'Arial, sans-serif' };
                const lbl = { display: 'block', fontSize: '0.78rem', fontWeight: '700', color: '#555', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.04em' };
                const row = { marginBottom: '0.9rem' };
                return (
                  <div style={{ maxWidth: '700px' }}>
                    <h4 style={{ margin: '0 0 1.25rem', color: '#111' }}>{adForm._id ? 'Edit Ad' : 'New Ad'}</h4>

                    {/* Advertiser */}
                    <div style={{ background: '#fff', borderRadius: '8px', padding: '1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: '1rem' }}>
                      <div style={{ fontSize: '11px', fontWeight: '900', color: '#dd3333', borderBottom: '2px solid #dd3333', paddingBottom: '5px', marginBottom: '1rem', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Advertiser</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1rem' }}>
                        <div style={row}><span style={lbl}>Advertiser Name *</span><input style={inp} value={adForm.advertiserName} onChange={e => setF('advertiserName', e.target.value)} placeholder="Bob's Hardware" /></div>
                        <div style={row}><span style={lbl}>Advertiser Email</span><input style={inp} value={adForm.advertiserEmail} onChange={e => setF('advertiserEmail', e.target.value)} placeholder="bob@hardware.com" /></div>
                      </div>
                    </div>

                    {/* Creative */}
                    <div style={{ background: '#fff', borderRadius: '8px', padding: '1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: '1rem' }}>
                      <div style={{ fontSize: '11px', fontWeight: '900', color: '#13273F', borderBottom: '2px solid #13273F', paddingBottom: '5px', marginBottom: '1rem', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Creative</div>
                      {adForm.imageUrl && <img src={adForm.imageUrl} alt="preview" style={{ width: '100%', height: '120px', objectFit: 'cover', borderRadius: '5px', marginBottom: '10px' }} />}
                      <div style={row}>
                        <span style={lbl}>Upload Image {adImgUploading && <span style={{ fontWeight: '400', color: '#888', textTransform: 'none' }}>uploading…</span>}</span>
                        <input type="file" accept="image/*" onChange={e => uploadAdImage(e.target.files[0])} style={{ fontSize: '0.85rem' }} />
                      </div>
                      <div style={row}><span style={lbl}>Image URL * (or paste after upload)</span><input style={inp} value={adForm.imageUrl} onChange={e => setF('imageUrl', e.target.value)} placeholder="https://res.cloudinary.com/…" /></div>
                      <div style={row}><span style={lbl}>Click-through URL *</span><input style={inp} value={adForm.clickUrl} onChange={e => setF('clickUrl', e.target.value)} placeholder="https://advertiser.com/landing" /></div>
                      <div style={row}><span style={lbl}>Alt Text (accessibility)</span><input style={inp} value={adForm.altText} onChange={e => setF('altText', e.target.value)} placeholder="Bob's Hardware — Summer Sale" /></div>
                    </div>

                    {/* Targeting */}
                    <div style={{ background: '#fff', borderRadius: '8px', padding: '1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: '1rem' }}>
                      <div style={{ fontSize: '11px', fontWeight: '900', color: '#555', borderBottom: '2px solid #ddd', paddingBottom: '5px', marginBottom: '1rem', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Targeting <span style={{ fontWeight: '400', textTransform: 'none', color: '#aaa' }}>(leave empty to show everywhere)</span></div>
                      <div style={row}>
                        <span style={lbl}>Genres</span>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 18px', marginTop: '5px' }}>
                          {['Local News', 'State News', 'National News', 'Sports', 'Technology'].map(g => (
                            <label key={g} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.88rem', cursor: 'pointer' }}>
                              <input type="checkbox" checked={adForm.genres.includes(g)} onChange={() => toggleArr('genres', g)} style={{ accentColor: '#dd3333' }} />{g}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div style={row}>
                        <span style={lbl}>Audience Level</span>
                        <div style={{ display: 'flex', gap: '6px 18px', flexWrap: 'wrap', marginTop: '5px' }}>
                          {['local', 'regional', 'state', 'national'].map(a => (
                            <label key={a} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.88rem', cursor: 'pointer', textTransform: 'capitalize' }}>
                              <input type="checkbox" checked={adForm.audienceLevels.includes(a)} onChange={() => toggleArr('audienceLevels', a)} style={{ accentColor: '#dd3333' }} />{a}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 1rem' }}>
                        <div style={row}><span style={lbl}>Target State</span><input style={inp} value={adForm.targetState} onChange={e => setF('targetState', e.target.value.toUpperCase())} placeholder="OK" maxLength={2} /></div>
                        <div style={row}><span style={lbl}>Target City</span><input style={inp} value={adForm.targetCity} onChange={e => setF('targetCity', e.target.value)} placeholder="Norman" /></div>
                        <div style={row}><span style={lbl}>Target ZIP</span><input style={inp} value={adForm.targetZip} onChange={e => setF('targetZip', e.target.value)} placeholder="73069" maxLength={10} /></div>
                      </div>
                    </div>

                    {/* Tier + Schedule */}
                    <div style={{ background: '#fff', borderRadius: '8px', padding: '1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: '1rem' }}>
                      <div style={{ fontSize: '11px', fontWeight: '900', color: '#555', borderBottom: '2px solid #ddd', paddingBottom: '5px', marginBottom: '1rem', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Tier & Schedule</div>
                      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '1rem' }}>
                        {[
                          { val: 'standard',  label: 'Standard',  desc: 'Rotates with up to 3 others' },
                          { val: 'premium',   label: 'Premium',   desc: 'Rotates with 1 other' },
                          { val: 'exclusive', label: 'Exclusive', desc: 'Only ad in this slot' },
                        ].map(({ val, label, desc }) => (
                          <label key={val} onClick={() => setF('tier', val)} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '10px 14px', border: `2px solid ${adForm.tier === val ? '#13273F' : '#e0e0e0'}`, borderRadius: '7px', cursor: 'pointer', flex: '1', background: adForm.tier === val ? '#f0f4fa' : '#fff' }}>
                            <input type="radio" checked={adForm.tier === val} onChange={() => setF('tier', val)} style={{ marginTop: '2px', accentColor: '#13273F' }} />
                            <div><div style={{ fontWeight: '700', fontSize: '0.88rem' }}>{label}</div><div style={{ fontSize: '0.76rem', color: '#888' }}>{desc}</div></div>
                          </label>
                        ))}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1rem' }}>
                        <div style={row}><span style={lbl}>Start Date</span><input type="date" style={inp} value={adForm.startDate} onChange={e => setF('startDate', e.target.value)} /></div>
                        <div style={row}><span style={lbl}>End Date</span><input type="date" style={inp} value={adForm.endDate} onChange={e => setF('endDate', e.target.value)} /></div>
                      </div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '9px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '700' }}>
                        <input type="checkbox" checked={adForm.active} onChange={e => setF('active', e.target.checked)} style={{ width: '16px', height: '16px', accentColor: '#28a745' }} />
                        Active — go live on site immediately
                      </label>
                    </div>

                    {adError && <div style={{ background: '#fdecea', border: '1px solid #f5c6cb', borderRadius: '5px', padding: '0.6rem 1rem', color: '#721c24', fontSize: '0.88rem', marginBottom: '1rem' }}>{adError}</div>}
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button onClick={saveAd} disabled={adSaving} style={{ background: adSaving ? '#999' : '#13273F', color: '#fff', border: 'none', borderRadius: '7px', padding: '10px 24px', fontWeight: '700', fontSize: '0.92rem', cursor: adSaving ? 'not-allowed' : 'pointer' }}>
                        {adSaving ? 'Saving…' : (adForm._id ? 'Save Changes' : 'Create Ad')}
                      </button>
                      <button onClick={() => { setAdForm(null); setAdError(''); }} style={{ background: '#f5f5f5', color: '#555', border: '1px solid #ccc', borderRadius: '7px', padding: '10px 18px', fontWeight: '600', fontSize: '0.92rem', cursor: 'pointer' }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                );
              })()}

              {/* ── Ads List ── */}
              {!adForm && (
                adsLoading ? (
                  <div style={{ textAlign: 'center', color: '#888', padding: '3rem', fontFamily: 'Arial' }}>Loading…</div>
                ) : adsList.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#aaa', padding: '3rem', fontFamily: 'Arial' }}>No ads yet. Click "+ New Ad" to create your first campaign.</div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Arial, sans-serif', fontSize: '0.88rem' }}>
                      <thead>
                        <tr style={{ background: '#f0f0f0' }}>
                          {['Advertiser', 'Genres', 'Audience', 'Tier', 'State', 'Impr.', 'Clicks', 'CTR', 'Status', ''].map(h => (
                            <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '700', color: '#555', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap', borderBottom: '2px solid #e0e0e0' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {adsList.map(ad => {
                          const ctr = ad.impressions > 0 ? `${((ad.clicks / ad.impressions) * 100).toFixed(1)}%` : '—';
                          return (
                            <tr key={ad._id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                              <td style={{ padding: '10px 12px' }}>
                                <div style={{ fontWeight: '700', color: '#111' }}>{ad.advertiserName}</div>
                                {ad.advertiserEmail && <div style={{ fontSize: '0.75rem', color: '#aaa' }}>{ad.advertiserEmail}</div>}
                              </td>
                              <td style={{ padding: '10px 12px', color: '#555', fontSize: '0.82rem' }}>{ad.genres.length ? ad.genres.join(', ') : <span style={{ color: '#bbb' }}>All</span>}</td>
                              <td style={{ padding: '10px 12px', color: '#555', fontSize: '0.82rem' }}>{ad.audienceLevels.length ? ad.audienceLevels.join(', ') : <span style={{ color: '#bbb' }}>All</span>}</td>
                              <td style={{ padding: '10px 12px' }}>
                                <span style={{ fontSize: '0.72rem', fontWeight: '800', textTransform: 'uppercase', padding: '2px 8px', borderRadius: '4px', background: ad.tier === 'exclusive' ? '#1a0533' : ad.tier === 'premium' ? '#0c2a3f' : '#2d4a2d', color: '#fff' }}>{ad.tier}</span>
                              </td>
                              <td style={{ padding: '10px 12px', color: '#555', fontSize: '0.82rem' }}>{ad.targetState || <span style={{ color: '#bbb' }}>—</span>}</td>
                              <td style={{ padding: '10px 12px', color: '#555', textAlign: 'right' }}>{ad.impressions.toLocaleString()}</td>
                              <td style={{ padding: '10px 12px', color: '#555', textAlign: 'right' }}>{ad.clicks.toLocaleString()}</td>
                              <td style={{ padding: '10px 12px', color: '#555', textAlign: 'right' }}>{ctr}</td>
                              <td style={{ padding: '10px 12px' }}>
                                <button onClick={() => toggleAdActive(ad)} style={{ background: ad.active ? '#28a745' : '#e0e0e0', color: ad.active ? '#fff' : '#555', border: 'none', borderRadius: '4px', padding: '4px 10px', fontSize: '0.78rem', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                  {ad.active ? '✅ Live' : '⏸ Off'}
                                </button>
                              </td>
                              <td style={{ padding: '10px 12px' }}>
                                <div style={{ display: 'flex', gap: '6px' }}>
                                  <button onClick={() => { setAdError(''); setAdForm({ ...ad, startDate: ad.startDate ? ad.startDate.slice(0, 10) : '', endDate: ad.endDate ? ad.endDate.slice(0, 10) : '' }); }} style={{ background: '#13273F', color: '#fff', border: 'none', borderRadius: '4px', padding: '4px 10px', fontSize: '0.78rem', fontWeight: '700', cursor: 'pointer' }}>Edit</button>
                                  <button onClick={() => deleteAd(ad._id)} style={{ background: '#fdecea', color: '#c00', border: 'none', borderRadius: '4px', padding: '4px 10px', fontSize: '0.78rem', fontWeight: '700', cursor: 'pointer' }}>Del</button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Analytics & Price Optimization ── */}
      <div id="section-analytics" style={{ scrollMarginTop: '160px' }} />
      <AdminAnalytics token={token} />
    </div>
  );
}

const th = { padding: '0.65rem 1rem', textAlign: 'left', fontWeight: '600', color: '#555' };
const td = { padding: '0.6rem 1rem', color: '#333' };

const formLabel    = { display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.3rem', color: '#444' };
const formInput    = { width: '100%', padding: '0.6rem 0.85rem', border: '1px solid #ccc', borderRadius: 7, fontSize: '0.92rem', boxSizing: 'border-box', outline: 'none' };
const sectionLabel = { fontSize: '0.75rem', fontWeight: 700, color: '#0d3165', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.5rem', borderBottom: '2px solid #e8eeff', paddingBottom: '0.35rem' };
